import React, { useState, useCallback, useEffect, useMemo,useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine, ResponsiveContainer } from 'recharts';
import {
  SIPCalc, RetirementCalc, RRSPCalc,
  TFSACalc, DividendCalc, CurrencyConverter
} from '../components/FinanceToolsExtension';
import html2canvas from 'html2canvas';

import SEO from '../components/SEO';

/* ─── CURRENCIES ─────────────────────────────────────────────────────── */
const CURRENCIES = [
  { code:'USD', symbol:'$',  name:'US Dollar'          },
  { code:'CAD', symbol:'CA$',name:'Canadian Dollar'    },
  { code:'GBP', symbol:'£',  name:'British Pound'      },
  { code:'EUR', symbol:'€',  name:'Euro'               },
  { code:'AUD', symbol:'A$', name:'Australian Dollar'  },
  { code:'INR', symbol:'₹',  name:'Indian Rupee'       },
  { code:'JPY', symbol:'¥',  name:'Japanese Yen'       },
  { code:'SGD', symbol:'S$', name:'Singapore Dollar'   },
  { code:'CHF', symbol:'Fr', name:'Swiss Franc'        },
  { code:'NZD', symbol:'NZ$',name:'New Zealand Dollar' },
];

/* ─── TOKENS ──────────────────────────────────────────────────────────── */
const T = {
  bg:     '#07090f',
  bg2:    '#0c0f18',
  bg3:    '#111520',
  surf:   '#161c2a',
  surf2:  '#1c243a',
  border: 'rgba(255,255,255,.07)',
  borderH:'rgba(255,255,255,.13)',
  white:  '#dce6fa',
  muted:  'rgba(255,255,255,0.75)',
  dim:    'rgba(255,255,255,0.75)',
  blue:   '#4d8fff',
  cyan:   '#00d4ff',
  green:  '#2ecc71',
  red:    '#e74c3c',
  yellow: '#f1c40f',
  purple: '#9b59b6',
  orange: '#e67e22',
  teal:   '#1abc9c',
  sans:   '"Syne","DM Sans",system-ui,sans-serif',
  mono:   '"JetBrains Mono","Fira Code",monospace',
};

/* ─── SHARED ATOMS ────────────────────────────────────────────────────── */
const fmt = (n, sym, decimals = 0) => {
  if (isNaN(n) || !isFinite(n)) return '—';
  return `${sym}${Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
};
const fmtPct = (n, d = 1) => isNaN(n) || !isFinite(n) ? '—' : `${n.toFixed(d)}%`;
const fmtYrs = (n) => isNaN(n) || !isFinite(n) || n < 0 ||n==null ? '—' : `${n.toFixed(1)} yrs`;
const shortNum = (n, sym) => {
  if (n >= 1_000_000) return `${sym}${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${sym}${(n / 1_000).toFixed(0)}K`;
  return `${sym}${n}`;
};
function CurrencyPicker({ value, onChange }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:18 }}>
      <span style={{ fontSize:12, color:T.muted }}>Currency</span>
      <select value={value} onChange={e=>onChange(e.target.value)} style={{
        padding:'5px 10px', borderRadius:7,
        background:T.surf2, border:`1px solid ${T.borderH}`,
        color:T.white, fontSize:13, fontFamily:T.mono,
        cursor:'pointer',
      }}>
        {CURRENCIES.map(c=>(
          <option key={c.code} value={c.code}>{c.code} — {c.symbol} {c.name}</option>
        ))}
      </select>
    </div>
  );
}
function PrintButton({ title, targetRef }) {
  const [loading, setLoading] = useState(false);

const handlePrint = async () => {
  const el = targetRef?.current;
  if (!el) return;
  setLoading(true);

  // ── Temporarily style all inputs for capture ─────────────────
  const inputs  = el.querySelectorAll('input, select, textarea');
  const saved   = [];

  inputs.forEach(inp => {
    saved.push({
      el,
      height:     inp.style.height,
      lineHeight: inp.style.lineHeight,
      paddingTop: inp.style.paddingTop,
      overflow:   inp.style.overflow,
      color:      inp.style.color,
      background: inp.style.background,
      border:     inp.style.border,
      fontSize:   inp.style.fontSize,
    });

    if (inp.type === 'range') return; // sliders are fine

    inp.style.height     = 'auto';
    inp.style.minHeight  = '32px';
    inp.style.lineHeight = '32px';
    inp.style.paddingTop = '6px';
    inp.style.paddingBottom = '6px';
    inp.style.overflow   = 'visible';
    inp.style.color      = '#dce6fa';
    inp.style.background = 'rgba(255,255,255,.08)';
    inp.style.border     = '1px solid rgba(255,255,255,.2)';
    inp.style.fontSize   = '13px';
    inp.style.boxSizing  = 'border-box';
  });

  await new Promise(r => setTimeout(r, 80));

  try {
    const canvas = await html2canvas(el, {
      backgroundColor: '#07080c',
      scale:           2,
      useCORS:         true,
      logging:         false,
      windowWidth:     el.scrollWidth,
      windowHeight:    el.scrollHeight,
    });

    const imgData = canvas.toDataURL('image/png');
    const imgW    = canvas.width  / 2;
    const imgH    = canvas.height / 2;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Tylented — ${title}</title>
        <style>
          * { margin:0; padding:0; box-sizing:border-box; }
          body { background:#07080c; }
          .header {
            display:flex; justify-content:space-between; align-items:center;
            padding:12px 20px; background:#0c0f18;
            border-bottom:1px solid rgba(255,255,255,.08);
            font-family:Arial,sans-serif;
          }
          .logo   { font-size:16px; font-weight:800; color:#00c8f0; }
          .htitle { font-size:13px; font-weight:700; color:#dce6fa; }
          .hdate  { font-size:11px; color:rgba(220,230,250,.4); }
          img     { display:block; width:100%; height:auto; }
          .footer {
            padding:10px 20px; font-family:Arial,sans-serif;
            font-size:10px; color:rgba(220,230,250,.3);
            background:#0c0f18; border-top:1px solid rgba(255,255,255,.06);
            text-align:center;
          }
          @media print {
            @page { margin:0; size:auto; }
            body  { margin:0; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">Tylented</div>
          <div class="htitle">${title}</div>
          <div class="hdate">${new Date().toLocaleDateString('en-CA', {
            year:'numeric', month:'long', day:'numeric'
          })}</div>
        </div>
        <img src="${imgData}" width="${imgW}" height="${imgH}" alt="${title}"/>
        <div class="footer">Tylented — Free Canadian Financial Calculators</div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.onload = () => setTimeout(() => { printWindow.focus(); printWindow.print(); }, 300);

  } catch (err) {
    console.error('Print failed:', err);
    alert('Print failed. Try again.');
  } finally {
    // ── Restore every input exactly as it was ───────────────────
    inputs.forEach((inp, i) => {
      if (inp.type === 'range') return;
      const s = saved[i];
      inp.style.height        = s.height;
      inp.style.lineHeight    = s.lineHeight;
      inp.style.paddingTop    = s.paddingTop;
      inp.style.paddingBottom = '';
      inp.style.overflow      = s.overflow;
      inp.style.color         = s.color;
      inp.style.background    = s.background;
      inp.style.border        = s.border;
      inp.style.fontSize      = s.fontSize;
      inp.style.minHeight     = '';
      inp.style.boxSizing     = '';
    });
    setLoading(false);
  }
};
  return (
    <button
      onClick={handlePrint}
      disabled={loading}
      title={`Print ${title}`}
      style={{
        display:     'flex',
        alignItems:  'center',
        gap:         5,
        padding:     '5px 12px',
        borderRadius:7,
        border:      `1px solid ${T.border}`,
        background:  'transparent',
        color:       loading ? T.dim : T.muted,
        fontSize:    12,
        cursor:      loading ? 'default' : 'pointer',
        fontFamily:  'inherit',
        transition:  'all .15s',
        marginBottom:12,
        marginLeft:  'auto',
      }}
      onMouseEnter={e => {
        if (loading) return;
        e.currentTarget.style.borderColor = T.borderH;
        e.currentTarget.style.color       = T.white;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = T.border;
        e.currentTarget.style.color       = loading ? T.dim : T.muted;
      }}
    >
      {loading
        ? <><span style={{ width:10, height:10, borderRadius:'50%', border:`1.5px solid ${T.dim}`, borderTopColor:T.cyan, animation:'spin .65s linear infinite', display:'inline-block' }}/> Capturing…</>
        : '🖨 Print'
      }
    </button>
  );
}
function Field({ label, help, children }) {
  return (
    <div style={{ marginBottom:14 }}>
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:5 }}>
        <label style={{ fontSize:12, fontWeight:600, color:T.muted }}>{label}</label>
        {help && <span style={{ fontSize:11, color:T.dim }}>{help}</span>}
      </div>
      {children}
    </div>
  );
}

const inputStyle = {
  width:'100%', padding:'8px 12px',
  background:T.bg2, border:`1px solid ${T.border}`,
  borderRadius:7, color:T.white,
  fontSize:13, fontFamily:T.mono,
  outline:'none', transition:'border-color .15s',
};

function Input({ value, onChange, prefix, suffix, type='number', ...rest }) {
  return (
    <div style={{ position:'relative' }}>
      {prefix && <span style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', fontSize:12, color:T.muted, pointerEvents:'none' }}>{prefix}</span>}
      <input
        type={type} value={value}
        onChange={e=>onChange(e.target.value)}
        style={{ ...inputStyle, paddingLeft: prefix ? 26 : 12, paddingRight: suffix ? 36 : 12 }}
        {...rest}
      />
      {suffix && <span style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', fontSize:12, color:T.muted, pointerEvents:'none' }}>{suffix}</span>}
    </div>
  );
}

function Slider({ value, onChange, min, max, step=1 }) {
  return (
    <input type="range" min={min} max={max} step={step} value={value}
      onChange={e=>onChange(Number(e.target.value))}
      style={{ width:'100%', accentColor:T.cyan, cursor:'pointer' }}
    />
  );
}

function ResultCard({ label, value, color=T.cyan, large, sub }) {
  return (
    <div style={{
      padding:'12px 14px', borderRadius:9,
      background:`${color}10`, border:`1px solid ${color}30`,
      textAlign:'center',
    }}>
      <div style={{ fontSize:11, color:`${color}90`, marginBottom:4, textTransform:'uppercase', letterSpacing:'.08em' }}>{label}</div>
      <div style={{ fontSize: large ? 22 : 18, fontWeight:800, color, fontFamily:T.mono }}>{value}</div>
      {sub && <div style={{ fontSize:11, color:T.dim, marginTop:3 }}>{sub}</div>}
    </div>
  );
}

function ResultGrid({ children }) {
  return (
    <div style={{
      display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))',
      gap:10, marginTop:20, paddingTop:16,
      borderTop:`1px solid ${T.border}`,
    }}>{children}</div>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{
      fontSize:11, fontWeight:700, letterSpacing:'.1em',
      textTransform:'uppercase', color:T.dim,
      marginBottom:14, paddingBottom:8,
      borderBottom:`1px solid ${T.border}`,
    }}>{children}</div>
  );
}

function useCurrency(code) {
  return CURRENCIES.find(c=>c.code===code) || CURRENCIES[0];
}

/* ─── TOOL COMPONENTS ────────────────────────────────────────────────── */

/* 1. FIRE CALCULATOR */
function FIRECalc({ sym }) {
  const [annualExp,   setAnnualExp]   = useState(50000);
  const [currentSave, setCurrentSave] = useState(100000);
  const [annualSave,  setAnnualSave]  = useState(30000);
  const [returnRate,  setReturnRate]  = useState(7);
  const [withdrawRate,setWithdrawRate]= useState(4);
  const [inflRate,    setInflRate]    = useState(2.5);
  const printRef = useRef(null);   // ← add

  
  const fireNumber     = (annualExp / (withdrawRate / 100));
  const realReturn     = (returnRate - inflRate) / 100;

const yearsToFire = (() => {
  if (currentSave >= fireNumber) return 0;

  // Iterative: simulate year by year
  // More robust than the closed-form log formula when annualSave/r > currentSave
  if (realReturn !== 0) {
    // Try closed-form first — only valid when den > 0
    const terminalVal = annualSave / realReturn;
    const num = fireNumber  - terminalVal;
    const den = currentSave - terminalVal;

    if (den > 0 && num > 0 && num / den > 0) {
      return Math.log(num / den) / Math.log(1 + realReturn);
    }

    // Fallback: iterate year by year (handles den <= 0 cases)
    let balance = currentSave;
    for (let y = 1; y <= 200; y++) {
      balance = balance * (1 + realReturn) + annualSave;
      if (balance >= fireNumber) return y;
    }
    return null; // genuinely unreachable in 200 years
  }

  // Zero real return — simple linear
  return annualSave > 0 ? (fireNumber - currentSave) / annualSave : Infinity;
})();
  const fireYear = yearsToFire !== null ? new Date().getFullYear() + Math.ceil(yearsToFire) : null;

const chartData = useMemo(() => {
  const maxYears = yearsToFire !== null ? Math.ceil(yearsToFire) + 10 : 50;
  return Array.from({ length: maxYears + 1 }, (_, y) => {
    const portfolio = realReturn !== 0
      ? currentSave * Math.pow(1 + realReturn, y) + annualSave * (Math.pow(1 + realReturn, y) - 1) / realReturn
      : currentSave + annualSave * y;
    return {
      year:      new Date().getFullYear() + y,
      portfolio: Math.round(Math.max(0, portfolio)),
      fireTarget:Math.round(fireNumber),
    };
  });
}, [currentSave, annualSave, realReturn, fireNumber, yearsToFire]);

// ADD this instead:
const savingsRate = (annualSave + annualExp) > 0 ? (annualSave / (annualSave + annualExp)) * 100 : 0;
  return (
    <div ref={printRef}>
          <PrintButton title="FIRE Calculator" targetRef={printRef}/>

      <div className="ft-grid" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <Field label="Annual Expenses">
          <Input value={annualExp} onChange={v=>setAnnualExp(+v)} prefix={sym}/>
        </Field>
        <Field label="Current Savings">
          <Input value={currentSave} onChange={v=>setCurrentSave(+v)} prefix={sym}/>
        </Field>
        <Field label="Annual Savings">
          <Input value={annualSave} onChange={v=>setAnnualSave(+v)} prefix={sym}/>
        </Field>
        <Field label={`Withdrawal Rate: ${withdrawRate}%`}>
          <Slider value={withdrawRate} onChange={v=>setWithdrawRate(v)} min={2} max={8} step={0.5}/>
        </Field>
        <Field label={`Investment Return: ${returnRate}%`}>
          <Slider value={returnRate} onChange={v=>setReturnRate(v)} min={1} max={15} step={0.5}/>
        </Field>
        <Field label={`Inflation Rate: ${inflRate}%`}>
          <Slider value={inflRate} onChange={v=>setInflRate(v)} min={0} max={8} step={0.5}/>
        </Field>
      </div>
      <ResultGrid>
        <ResultCard label="FIRE Number" value={fmt(fireNumber,sym)} color={T.cyan} large/>
        <ResultCard label="Years to FIRE" value={fmtYrs(yearsToFire)} color={T.green}/>
        <ResultCard label="Fire Year" value={fireYear} color={T.purple}/>
        <ResultCard label="Current Savings" value={fmt(currentSave,sym)} color={T.blue}/>
        <ResultCard label="Savings Rate" value={fmtPct(savingsRate)} color={T.yellow}/>
      </ResultGrid>
      {/* Portfolio Growth Chart */}
<div style={{ marginTop:24 }}>
  <div style={{
    fontSize:12, fontWeight:700, color:T.muted,
    textTransform:'uppercase', letterSpacing:'.08em', marginBottom:12,
  }}>
    Portfolio Growth Over Time
  </div>

  {yearsToFire === null && (
    <div style={{
      padding:'10px 14px', borderRadius:8, marginBottom:12,
      background:`${T.red}12`, border:`1px solid ${T.red}30`,
      fontSize:12.5, color:T.red,
    }}>
      ⚠ At your current savings rate, your portfolio may not reach your FIRE number.
      Try increasing savings or return rate.
    </div>
  )}

  <ResponsiveContainer width="100%" height={260}>
    <LineChart data={chartData} margin={{ top:4, right:16, left:8, bottom:4 }}>
      <CartesianGrid
        strokeDasharray="3 3"
        stroke="rgba(255,255,255,.06)"
        vertical={false}
      />
      <XAxis
        dataKey="year"
        tick={{ fill:T.muted, fontSize:11 }}
        tickLine={false}
        axisLine={false}
        interval="preserveStartEnd"
      />
      <YAxis
        tick={{ fill:T.muted, fontSize:11 }}
        tickLine={false}
        axisLine={false}
        width={56}
        tickFormatter={n => shortNum(n, sym)}
      />
      <Tooltip
        contentStyle={{
          background:T.bg2, border:`1px solid ${T.border}`,
          borderRadius:8, fontSize:12,
        }}
        labelStyle={{ color:T.muted, marginBottom:4 }}
        itemStyle={{ color:T.white }}
        formatter={(value, name) => [
          shortNum(value, sym),
          name === 'portfolio' ? 'Portfolio' : 'FIRE Target',
        ]}
        labelFormatter={year => `Year ${year}`}
      />
      <Legend
        wrapperStyle={{ fontSize:12, color:T.muted, paddingTop:8 }}
        formatter={name => name === 'portfolio' ? 'Portfolio value' : 'FIRE target'}
      />

      {/* FIRE crossover line */}
      {fireYear && (
        <ReferenceLine
          x={fireYear}
          stroke={T.green}
          strokeDasharray="4 3"
          strokeWidth={1.5}
          label={{
            value:'FIRE',
            position:'top',
            fill:T.green,
            fontSize:11,
            fontWeight:700,
          }}
        />
      )}

      <Line
        type="monotone"
        dataKey="portfolio"
        stroke={T.cyan}
        strokeWidth={2}
        dot={false}
        activeDot={{ r:4, fill:T.cyan }}
      />
      <Line
        type="monotone"
        dataKey="fireTarget"
        stroke={T.green}
        strokeWidth={1.5}
        strokeDasharray="6 4"
        dot={false}
        activeDot={false}
      />
    </LineChart>
  </ResponsiveContainer>
</div>
    </div>
  );
}

/* 2. COAST FIRE CALCULATOR */
function CoastFIRECalc({ sym }) {
  const [targetFire,   setTargetFire]   = useState(1250000);
  const [currentSave,  setCurrentSave]  = useState(80000);
  const [currentAge,   setCurrentAge]   = useState(30);
  const [retireAge,    setRetireAge]    = useState(60);
  const [returnRate,   setReturnRate]   = useState(7);

  const yearsToGrow   = retireAge - currentAge;
  const coastNumber   = yearsToGrow > 0 ? targetFire / Math.pow(1 + returnRate/100, yearsToGrow) : targetFire;
  const coasted       = currentSave >= coastNumber;
  const pctToCoast    = Math.min(100, (currentSave / coastNumber) * 100);
  const gap           = Math.max(0, coastNumber - currentSave);

const chartData = useMemo(() => {
  const maxYears = Math.ceil(yearsToGrow) + 10;
  return Array.from({ length: maxYears + 1 }, (_, y) => {
    // Coast FIRE = no more contributions, just compound what you have
    const portfolio = currentSave * Math.pow(1 + returnRate / 100, y);
    return {
      year:      new Date().getFullYear() + y,
      portfolio: Math.round(Math.max(0, portfolio)),
      fireTarget:Math.round(targetFire),   // final FIRE goal, not coast number
      coastTarget:Math.round(coastNumber), // coast number as a second reference
    };
  });
}, [currentSave, returnRate, targetFire, coastNumber, yearsToGrow]);
  const fireYear = yearsToGrow !== null ? new Date().getFullYear() + Math.ceil(yearsToGrow) : null;

  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <Field label="Target FIRE Number">
          <Input value={targetFire} onChange={v=>setTargetFire(+v)} prefix={sym}/>
        </Field>
        <Field label="Current Savings">
          <Input value={currentSave} onChange={v=>setCurrentSave(+v)} prefix={sym}/>
        </Field>
        <Field label="Current Age">
          <Input value={currentAge} onChange={v=>setCurrentAge(+v)} suffix="yrs"/>
        </Field>
        <Field label="Retirement Age">
          <Input value={retireAge} onChange={v=>setRetireAge(+v)} suffix="yrs"/>
        </Field>
        <Field label={`Investment Return: ${returnRate}%`}>
          <Slider value={returnRate} onChange={setReturnRate} min={1} max={15} step={0.5}/>
        </Field>
      </div>
      <div style={{ marginTop:16, height:8, background:T.border, borderRadius:4, overflow:'hidden' }}>
        <div style={{ height:'100%', width:`${pctToCoast}%`, background:`linear-gradient(90deg,${T.cyan},${coasted?T.green:T.blue})`, borderRadius:4, transition:'width .4s' }}/>
      </div>
      <div style={{ fontSize:11, color:T.muted, marginTop:4 }}>{pctToCoast.toFixed(0)}% to Coast FIRE</div>
      <ResultGrid>
        <ResultCard label="Coast FIRE Number" value={fmt(coastNumber,sym)} color={T.cyan} large/>
        <ResultCard label="Current vs Coast" value={coasted ? '✅ Coasted!' : fmt(gap,sym)+' gap'} color={coasted?T.green:T.red}/>
        <ResultCard label="Years to Grow" value={`${yearsToGrow} yrs`} color={T.blue}/>
        <ResultCard label="Progress" value={fmtPct(pctToCoast)} color={T.yellow}/>
        
      </ResultGrid>
      <div style={{ marginTop:24 }}>
  <div style={{
    fontSize:12, fontWeight:700, color:T.muted,
    textTransform:'uppercase', letterSpacing:'.08em', marginBottom:12,
  }}>
    Portfolio Growth Over Time
  </div>

  {yearsToGrow === null && (
    <div style={{
      padding:'10px 14px', borderRadius:8, marginBottom:12,
      background:`${T.red}12`, border:`1px solid ${T.red}30`,
      fontSize:12.5, color:T.red,
    }}>
      ⚠ At your current savings rate, your portfolio may not reach your FIRE number.
      Try increasing savings or return rate.
    </div>
  )}

  <ResponsiveContainer width="100%" height={260}>
    <LineChart data={chartData} margin={{ top:4, right:16, left:8, bottom:4 }}>
      <CartesianGrid
        strokeDasharray="3 3"
        stroke="rgba(255,255,255,.06)"
        vertical={false}
      />
      <XAxis
        dataKey="year"
        tick={{ fill:T.muted, fontSize:11 }}
        tickLine={false}
        axisLine={false}
        interval="preserveStartEnd"
      />
      <YAxis
        tick={{ fill:T.muted, fontSize:11 }}
        tickLine={false}
        axisLine={false}
        width={56}
        tickFormatter={n => shortNum(n, sym)}
      />
      <Tooltip
        contentStyle={{
          background:T.bg2, border:`1px solid ${T.border}`,
          borderRadius:8, fontSize:12,
        }}
        labelStyle={{ color:T.muted, marginBottom:4 }}
        itemStyle={{ color:T.white }}
      formatter={(value, name) => [
  shortNum(value, sym),
  name === 'portfolio'    ? 'Portfolio value' :
  name === 'fireTarget'   ? 'FIRE target'     :
  'Coast FIRE number',
]}
        labelFormatter={year => `Year ${year}`}
      />
      <Legend
        wrapperStyle={{ fontSize:12, color:T.muted, paddingTop:8 }}
formatter={name =>
  name === 'portfolio'    ? 'Portfolio value' :
  name === 'fireTarget'   ? 'FIRE target'     :
  'Coast FIRE number'
}      />

      {/* FIRE crossover line */}
      {fireYear && (
        <ReferenceLine
  y={coastNumber}
  stroke={T.cyan}
  strokeDasharray="4 3"
  strokeWidth={1}
  label={{ value:'Coast #', position:'right', fill:T.cyan, fontSize:10 }}
        />
      )}
<Line
  type="monotone"
  dataKey="portfolio"
  stroke={T.cyan}
  strokeWidth={2}
  dot={false}
  activeDot={{ r:4, fill:T.cyan }}
/>
      <Line
  type="monotone"
  dataKey="fireTarget"
  stroke={T.green}
  strokeWidth={1.5}
  strokeDasharray="6 4"
  dot={false}
  activeDot={false}
      />
      <Line
type="monotone"
  dataKey="coastTarget"
  stroke={T.yellow}
  strokeWidth={1}
  strokeDasharray="3 3"
  dot={false}
  activeDot={false}
      />
    </LineChart>
  </ResponsiveContainer>
</div>
    </div>
  );
}

/* 3. FAT FIRE CALCULATOR */
function FatFIRECalc({ sym }) {
  const [annualExp,    setAnnualExp]    = useState(100000);
  const [currentSave,  setCurrentSave]  = useState(200000);
  const [annualSave,   setAnnualSave]   = useState(60000);
  const [returnRate,   setReturnRate]   = useState(7);
  const [withdrawRate, setWithdrawRate] = useState(3.5);

  const fireNumber  = annualExp / (withdrawRate / 100);
  const realReturn  = returnRate / 100;

const yearsToFire = (() => {
  if (currentSave >= fireNumber) return 0;

  // Iterative: simulate year by year
  // More robust than the closed-form log formula when annualSave/r > currentSave
  if (realReturn !== 0) {
    // Try closed-form first — only valid when den > 0
    const terminalVal = annualSave / realReturn;
    const num = fireNumber  - terminalVal;
    const den = currentSave - terminalVal;

    if (den > 0 && num > 0 && num / den > 0) {
      return Math.log(num / den) / Math.log(1 + realReturn);
    }

    // Fallback: iterate year by year (handles den <= 0 cases)
    let balance = currentSave;
    for (let y = 1; y <= 200; y++) {
      balance = balance * (1 + realReturn) + annualSave;
      if (balance >= fireNumber) return y;
    }
    return null; // genuinely unreachable in 200 years
  }

  // Zero real return — simple linear
  return annualSave > 0 ? (fireNumber - currentSave) / annualSave : Infinity;
})();
  const fireYear = yearsToFire !== null ? new Date().getFullYear() + Math.ceil(yearsToFire) : null;

const chartData = useMemo(() => {
  const maxYears = yearsToFire !== null ? Math.ceil(yearsToFire) + 10 : 50;
  return Array.from({ length: maxYears + 1 }, (_, y) => {
    const portfolio = realReturn !== 0
      ? currentSave * Math.pow(1 + realReturn, y) + annualSave * (Math.pow(1 + realReturn, y) - 1) / realReturn
      : currentSave + annualSave * y;
    return {
      year:      new Date().getFullYear() + y,
      portfolio: Math.round(Math.max(0, portfolio)),
      fireTarget:Math.round(fireNumber),
    };
  });
}, [currentSave, annualSave, realReturn, fireNumber, yearsToFire]);


  return (
    <div>
      <div className="ft-grid-2" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <Field label="Annual Expenses (Fat lifestyle)">
          <Input value={annualExp} onChange={v=>setAnnualExp(+v)} prefix={sym}/>
        </Field>
        <Field label="Current Portfolio">
          <Input value={currentSave} onChange={v=>setCurrentSave(+v)} prefix={sym}/>
        </Field>
        <Field label="Annual Contributions">
          <Input value={annualSave} onChange={v=>setAnnualSave(+v)} prefix={sym}/>
        </Field>
        <Field label={`Withdrawal Rate: ${withdrawRate}%`} help="Fat FIRE often uses 3-3.5%">
          <Slider value={withdrawRate} onChange={setWithdrawRate} min={2} max={5} step={0.25}/>
        </Field>
        <Field label={`Expected Return: ${returnRate}%`}>
          <Slider value={returnRate} onChange={setReturnRate} min={1} max={15} step={0.5}/>
        </Field>
      </div>
      <ResultGrid>
        <ResultCard label="Fat FIRE Number" value={fmt(fireNumber,sym)} color={T.yellow} large/>
        <ResultCard label="Years to Fat FIRE" value={fmtYrs(yearsToFire)} color={T.green}/>
        <ResultCard label="Monthly Income" value={fmt(annualExp/12,sym)} color={T.blue}/>
        <ResultCard label="Withdrawal Rate" value={fmtPct(withdrawRate)} color={T.purple}/>
      </ResultGrid>

      <div style={{ marginTop:24 }}>
  <div style={{
    fontSize:12, fontWeight:700, color:T.muted,
    textTransform:'uppercase', letterSpacing:'.08em', marginBottom:12,
  }}>
    Portfolio Growth Over Time
  </div>

  {yearsToFire === null && (
    <div style={{
      padding:'10px 14px', borderRadius:8, marginBottom:12,
      background:`${T.red}12`, border:`1px solid ${T.red}30`,
      fontSize:12.5, color:T.red,
    }}>
      ⚠ Portfolio cannot reach Fat FIRE target at current contributions and return rate.
    </div>
  )}

  <ResponsiveContainer width="100%" height={260}>
    <LineChart data={chartData} margin={{ top:4, right:16, left:8, bottom:4 }}>
      <CartesianGrid
        strokeDasharray="3 3"
        stroke="rgba(255,255,255,.06)"
        vertical={false}
      />
      <XAxis
        dataKey="year"
        tick={{ fill:T.muted, fontSize:11 }}
        tickLine={false}
        axisLine={false}
        interval="preserveStartEnd"
      />
      <YAxis
        tick={{ fill:T.muted, fontSize:11 }}
        tickLine={false}
        axisLine={false}
        width={60}
        tickFormatter={n => shortNum(n, sym)}
      />
      <Tooltip
        contentStyle={{
          background:T.bg2, border:`1px solid ${T.border}`,
          borderRadius:8, fontSize:12,
        }}
        labelStyle={{ color:T.muted, marginBottom:4 }}
        itemStyle={{ color:T.white }}
        formatter={(value, name) => [
          shortNum(value, sym),
          name === 'portfolio' ? 'Portfolio value' : 'Fat FIRE target',
        ]}
        labelFormatter={year => `Year ${year}`}
      />
      <Legend
        wrapperStyle={{ fontSize:12, color:T.muted, paddingTop:8 }}
        formatter={name => name === 'portfolio' ? 'Portfolio value' : 'Fat FIRE target'}
      />

      {fireYear && (
        <ReferenceLine
          x={fireYear}
          stroke={T.yellow}
          strokeDasharray="4 3"
          strokeWidth={1.5}
          label={{
            value:'Fat FIRE',
            position:'top',
            fill:T.yellow,
            fontSize:11,
            fontWeight:700,
          }}
        />
      )}

      <Line
        type="monotone"
        dataKey="portfolio"
        stroke={T.cyan}
        strokeWidth={2}
        dot={false}
        activeDot={{ r:4, fill:T.cyan }}
      />
      <Line
        type="monotone"
        dataKey="fireTarget"
        stroke={T.yellow}
        strokeWidth={1.5}
        strokeDasharray="6 4"
        dot={false}
        activeDot={false}
      />
    </LineChart>
  </ResponsiveContainer>
</div>
    </div>
  );
}

/* 4. BARISTA FIRE CALCULATOR */
function BaristaFIRECalc({ sym }) {
  const [annualExp,    setAnnualExp]    = useState(60000);
  const [partTimeInc,  setPartTimeInc]  = useState(20000);
  const [currentSave,  setCurrentSave]  = useState(150000);
  const [annualSave,   setAnnualSave]   = useState(30000);
  const [returnRate,   setReturnRate]   = useState(7);
  const [withdrawRate, setWithdrawRate] = useState(4);

  // ── Calculations ────────────────────────────────────────────────
  const remainingNeeded = Math.max(0, annualExp - partTimeInc);
  const fireNumber      = remainingNeeded > 0 ? remainingNeeded / (withdrawRate / 100) : 0;
  const realReturn      = returnRate / 100;
  const partTimePct     = annualExp > 0 ? (partTimeInc / annualExp) * 100 : 0;
  const portfolioPct    = annualExp > 0 ? (remainingNeeded / annualExp) * 100 : 0;

  const yearsToFire = (() => {
    if (fireNumber === 0 || currentSave >= fireNumber) return 0;
    if (realReturn !== 0) {
      const terminalVal = annualSave / realReturn;
      const num = fireNumber  - terminalVal;
      const den = currentSave - terminalVal;
      if (den > 0 && num > 0 && num / den > 0) {
        return Math.log(num / den) / Math.log(1 + realReturn);
      }
      // Iterative fallback
      let balance = currentSave;
      for (let y = 1; y <= 200; y++) {
        balance = balance * (1 + realReturn) + annualSave;
        if (balance >= fireNumber) return y;
      }
      return null;
    }
    return annualSave > 0 ? (fireNumber - currentSave) / annualSave : Infinity;
  })();

  const fireYear = yearsToFire !== null
    ? new Date().getFullYear() + Math.ceil(yearsToFire)
    : null;

  const chartData = useMemo(() => {
    const maxYears = yearsToFire !== null ? Math.ceil(yearsToFire) + 10 : 50;
    return Array.from({ length: maxYears + 1 }, (_, y) => {
      const portfolio = realReturn !== 0
        ? currentSave * Math.pow(1 + realReturn, y) + annualSave * (Math.pow(1 + realReturn, y) - 1) / realReturn
        : currentSave + annualSave * y;
      return {
        year:      new Date().getFullYear() + y,
        portfolio: Math.round(Math.max(0, portfolio)),
        fireTarget:Math.round(fireNumber),
      };
    });
  }, [currentSave, annualSave, realReturn, fireNumber, yearsToFire]);

  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <Field label="Annual Expenses">
          <Input value={annualExp}   onChange={v=>setAnnualExp(+v)}   prefix={sym}/>
        </Field>
        <Field label="Part-Time Income" help="barista job, freelance, etc.">
          <Input value={partTimeInc} onChange={v=>setPartTimeInc(+v)} prefix={sym}/>
        </Field>
        <Field label="Current Portfolio">
          <Input value={currentSave} onChange={v=>setCurrentSave(+v)} prefix={sym}/>
        </Field>
        <Field label="Annual Contributions">
          <Input value={annualSave}  onChange={v=>setAnnualSave(+v)}  prefix={sym}/>
        </Field>
        <Field label={`Withdrawal Rate: ${withdrawRate}%`}>
          <Slider value={withdrawRate} onChange={setWithdrawRate} min={2} max={6} step={0.25}/>
        </Field>
        <Field label={`Expected Return: ${returnRate}%`}>
          <Slider value={returnRate}   onChange={setReturnRate}   min={1} max={15} step={0.5}/>
        </Field>
      </div>

      {/* Income split bar */}
      <div style={{ marginTop:16, marginBottom:4 }}>
        <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:T.muted, marginBottom:5 }}>
          <span>Income split</span>
          <span>
            <span style={{ color:T.teal }}>{partTimePct.toFixed(0)}% part-time</span>
            {' + '}
            <span style={{ color:T.cyan }}>{portfolioPct.toFixed(0)}% portfolio</span>
          </span>
        </div>
        <div style={{ height:7, background:T.border, borderRadius:4, overflow:'hidden', display:'flex' }}>
          <div style={{
            width:`${partTimePct}%`, height:'100%',
            background:T.teal, borderRadius:'4px 0 0 4px',
            transition:'width .4s',
          }}/>
          <div style={{
            width:`${portfolioPct}%`, height:'100%',
            background:T.cyan,
            transition:'width .4s',
          }}/>
        </div>
      </div>

      <ResultGrid>
        <ResultCard label="Barista FIRE Number" value={fmt(fireNumber, sym)}          color={T.teal}   large/>
        <ResultCard label="Years Away"           value={fmtYrs(yearsToFire)}           color={T.green}/>
        <ResultCard label="Portfolio Covers"     value={fmt(remainingNeeded, sym)+'/yr'}color={T.blue}/>
        <ResultCard label="Part-Time Covers"     value={fmt(partTimeInc, sym)+'/yr'}   color={T.yellow}/>
      </ResultGrid>

      {/* Chart */}
      <div style={{ marginTop:24 }}>
        <div style={{
          fontSize:12, fontWeight:700, color:T.muted,
          textTransform:'uppercase', letterSpacing:'.08em', marginBottom:12,
        }}>
          Portfolio Growth Over Time
        </div>

        {yearsToFire === null && (
          <div style={{
            padding:'10px 14px', borderRadius:8, marginBottom:12,
            background:`${T.red}12`, border:`1px solid ${T.red}30`,
            fontSize:12.5, color:T.red,
          }}>
            ⚠ Portfolio cannot reach Barista FIRE target at current contributions and return rate.
          </div>
        )}

        {partTimeInc >= annualExp && (
          <div style={{
            padding:'10px 14px', borderRadius:8, marginBottom:12,
            background:`${T.green}12`, border:`1px solid ${T.green}30`,
            fontSize:12.5, color:T.green,
          }}>
            ✅ Your part-time income already covers all expenses — no portfolio withdrawals needed!
          </div>
        )}

        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={chartData} margin={{ top:4, right:16, left:8, bottom:4 }}>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="rgba(255,255,255,.06)"
              vertical={false}
            />
            <XAxis
              dataKey="year"
              tick={{ fill:T.muted, fontSize:11 }}
              tickLine={false}
              axisLine={false}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fill:T.muted, fontSize:11 }}
              tickLine={false}
              axisLine={false}
              width={60}
              tickFormatter={n => shortNum(n, sym)}
            />
            <Tooltip
              contentStyle={{
                background:T.bg2, border:`1px solid ${T.border}`,
                borderRadius:8, fontSize:12,
              }}
              labelStyle={{ color:T.muted, marginBottom:4 }}
              itemStyle={{ color:T.white }}
              formatter={(value, name) => [
                shortNum(value, sym),
                name === 'portfolio' ? 'Portfolio value' : 'Barista FIRE target',
              ]}
              labelFormatter={year => `Year ${year}`}
            />
            <Legend
              wrapperStyle={{ fontSize:12, color:T.muted, paddingTop:8 }}
              formatter={name => name === 'portfolio' ? 'Portfolio value' : 'Barista FIRE target'}
            />

            {fireYear && (
              <ReferenceLine
                x={fireYear}
                stroke={T.teal}
                strokeDasharray="4 3"
                strokeWidth={1.5}
                label={{
                  value:'Barista FIRE',
                  position:'top',
                  fill:T.teal,
                  fontSize:11,
                  fontWeight:700,
                }}
              />
            )}

            <Line
              type="monotone"
              dataKey="portfolio"
              stroke={T.cyan}
              strokeWidth={2}
              dot={false}
              activeDot={{ r:4, fill:T.cyan }}
            />
            <Line
              type="monotone"
              dataKey="fireTarget"
              stroke={T.teal}
              strokeWidth={1.5}
              strokeDasharray="6 4"
              dot={false}
              activeDot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
/* 5. WINDFALL IMPACT ON FIRE */
function WindfallFIRECalc({ sym }) {
  const [annualExp,    setAnnualExp]    = useState(50000);
  const [currentSave,  setCurrentSave]  = useState(200000);
  const [annualSave,   setAnnualSave]   = useState(30000);
  const [windfall,     setWindfall]     = useState(50000);
  const [returnRate,   setReturnRate]   = useState(7);
  const [withdrawRate, setWithdrawRate] = useState(4);

  // ── Calculations ────────────────────────────────────────────────
  const fireNumber = annualExp / (withdrawRate / 100);
  const r          = returnRate / 100;

  const calcYears = (startBalance) => {
    if (startBalance >= fireNumber) return 0;
    if (r !== 0) {
      const terminalVal = annualSave / r;
      const num = fireNumber   - terminalVal;
      const den = startBalance - terminalVal;
      if (den > 0 && num > 0 && num / den > 0) {
        return Math.log(num / den) / Math.log(1 + r);
      }
      // Iterative fallback
      let balance = startBalance;
      for (let y = 1; y <= 200; y++) {
        balance = balance * (1 + r) + annualSave;
        if (balance >= fireNumber) return y;
      }
      return null;
    }
    return annualSave > 0 ? (fireNumber - startBalance) / annualSave : Infinity;
  };

  const yearsWithout = calcYears(currentSave);
  const yearsWith    = calcYears(currentSave + windfall);
  const saved        = (yearsWithout !== null && yearsWith !== null)
    ? yearsWithout - yearsWith
    : null;

  const fireYearWithout = yearsWithout !== null
    ? new Date().getFullYear() + Math.ceil(yearsWithout) : null;
  const fireYearWith    = yearsWith !== null
    ? new Date().getFullYear() + Math.ceil(yearsWith)    : null;

  // ── Chart data — both trajectories on same chart ────────────────
  const chartData = useMemo(() => {
    const maxYears = Math.max(
      yearsWithout !== null ? Math.ceil(yearsWithout) : 0,
      yearsWith    !== null ? Math.ceil(yearsWith)    : 0,
    ) + 10;

    return Array.from({ length: Math.min(maxYears, 60) + 1 }, (_, y) => {
      const withoutPortfolio = r !== 0
        ? currentSave * Math.pow(1 + r, y) + annualSave * (Math.pow(1 + r, y) - 1) / r
        : currentSave + annualSave * y;

      const withPortfolio = r !== 0
        ? (currentSave + windfall) * Math.pow(1 + r, y) + annualSave * (Math.pow(1 + r, y) - 1) / r
        : (currentSave + windfall) + annualSave * y;

      return {
        year:          new Date().getFullYear() + y,
        without:       Math.round(Math.max(0, withoutPortfolio)),
        with:          Math.round(Math.max(0, withPortfolio)),
        fireTarget:    Math.round(fireNumber),
      };
    });
  }, [currentSave, windfall, annualSave, r, fireNumber, yearsWithout, yearsWith]);

  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <Field label="Annual Expenses">
          <Input value={annualExp}   onChange={v=>setAnnualExp(+v)}   prefix={sym}/>
        </Field>
        <Field label="Current Portfolio">
          <Input value={currentSave} onChange={v=>setCurrentSave(+v)} prefix={sym}/>
        </Field>
        <Field label="Annual Contributions">
          <Input value={annualSave}  onChange={v=>setAnnualSave(+v)}  prefix={sym}/>
        </Field>
        <Field label="Windfall Amount">
          <Input value={windfall}    onChange={v=>setWindfall(+v)}    prefix={sym}/>
        </Field>
        <Field label={`Return Rate: ${returnRate}%`}>
          <Slider value={returnRate}   onChange={setReturnRate}   min={1} max={15} step={0.5}/>
        </Field>
        <Field label={`Withdrawal Rate: ${withdrawRate}%`}>
          <Slider value={withdrawRate} onChange={setWithdrawRate} min={2} max={6}  step={0.25}/>
        </Field>
      </div>

      <ResultGrid>
        <ResultCard label="Without Windfall" value={fmtYrs(yearsWithout)}                              color={T.red}/>
        <ResultCard label="With Windfall"    value={fmtYrs(yearsWith)}                                 color={T.green} large/>
        <ResultCard label="Years Saved"      value={saved !== null ? `${saved.toFixed(1)} yrs` : '—'} color={T.cyan}/>
        <ResultCard label="FIRE Number"      value={fmt(fireNumber, sym)}                              color={T.blue}/>
      </ResultGrid>

      {/* Chart */}
      <div style={{ marginTop:24 }}>
        <div style={{
          fontSize:12, fontWeight:700, color:T.muted,
          textTransform:'uppercase', letterSpacing:'.08em', marginBottom:12,
        }}>
          Portfolio Growth — With vs Without Windfall
        </div>

        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={chartData} margin={{ top:4, right:16, left:8, bottom:4 }}>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="rgba(255,255,255,.06)"
              vertical={false}
            />
            <XAxis
              dataKey="year"
              tick={{ fill:T.muted, fontSize:11 }}
              tickLine={false}
              axisLine={false}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fill:T.muted, fontSize:11 }}
              tickLine={false}
              axisLine={false}
              width={60}
              tickFormatter={n => shortNum(n, sym)}
            />
            <Tooltip
              contentStyle={{
                background:T.bg2, border:`1px solid ${T.border}`,
                borderRadius:8, fontSize:12,
              }}
              labelStyle={{ color:T.muted, marginBottom:4 }}
              itemStyle={{ color:T.white }}
              formatter={(value, name) => [
                shortNum(value, sym),
                name === 'with'       ? `With windfall (${sym}${windfall.toLocaleString()})` :
                name === 'without'    ? 'Without windfall' :
                'FIRE target',
              ]}
              labelFormatter={year => `Year ${year}`}
            />
            <Legend
              wrapperStyle={{ fontSize:12, color:T.muted, paddingTop:8 }}
              formatter={name =>
                name === 'with'    ? `With windfall (${sym}${windfall.toLocaleString()})` :
                name === 'without' ? 'Without windfall' :
                'FIRE target'
              }
            />

            {/* Without windfall FIRE date */}
            {fireYearWithout && (
              <ReferenceLine
                x={fireYearWithout}
                stroke={T.red}
                strokeDasharray="4 3"
                strokeWidth={1}
                label={{
                  value:'No windfall',
                  position:'top',
                  fill:T.red,
                  fontSize:10,
                  fontWeight:700,
                }}
              />
            )}

            {/* With windfall FIRE date */}
            {fireYearWith && (
              <ReferenceLine
                x={fireYearWith}
                stroke={T.green}
                strokeDasharray="4 3"
                strokeWidth={1}
                label={{
                  value:'Windfall',
                  position:'insideTopRight',
                  fill:T.green,
                  fontSize:10,
                  fontWeight:700,
                }}
              />
            )}

            {/* FIRE target line */}
            <Line
              type="monotone"
              dataKey="fireTarget"
              stroke={T.blue}
              strokeWidth={1.5}
              strokeDasharray="6 4"
              dot={false}
              activeDot={false}
            />

            {/* Without windfall trajectory */}
            <Line
              type="monotone"
              dataKey="without"
              stroke={T.red}
              strokeWidth={2}
              dot={false}
              activeDot={{ r:4, fill:T.red }}
            />

            {/* With windfall trajectory */}
            <Line
              type="monotone"
              dataKey="with"
              stroke={T.green}
              strokeWidth={2}
              dot={false}
              activeDot={{ r:4, fill:T.green }}
            />
          </LineChart>
        </ResponsiveContainer>

        {/* Savings summary callout */}
        {saved !== null && saved > 0 && (
          <div style={{
            marginTop:12, padding:'10px 14px', borderRadius:8,
            background:`${T.green}10`, border:`1px solid ${T.green}25`,
            fontSize:12.5, color:T.green, textAlign:'center',
          }}>
            The {sym}{windfall.toLocaleString()} windfall saves you{' '}
            <strong>{saved.toFixed(1)} years</strong> — retiring in {fireYearWith} instead of {fireYearWithout}.
          </div>
        )}
      </div>
    </div>
  );
}
/* 6. PURCHASE IMPACT ON FIRE */
function PurchaseFIRECalc({ sym }) {
  const [annualExp,    setAnnualExp]    = useState(50000);
  const [currentSave,  setCurrentSave]  = useState(200000);
  const [annualSave,   setAnnualSave]   = useState(30000);
  const [purchase,     setPurchase]     = useState(20000);
  const [returnRate,   setReturnRate]   = useState(7);
  const [withdrawRate, setWithdrawRate] = useState(4);

  // ── Calculations ────────────────────────────────────────────────
  const fireNumber    = annualExp / (withdrawRate / 100);
  const r             = returnRate / 100;
  const afterPurchase = Math.max(0, currentSave - purchase);

  const calcYears = (startBalance) => {
    if (startBalance >= fireNumber) return 0;
    if (r !== 0) {
      const terminalVal = annualSave / r;
      const num = fireNumber   - terminalVal;
      const den = startBalance - terminalVal;
      if (den > 0 && num > 0 && num / den > 0) {
        return Math.log(num / den) / Math.log(1 + r);
      }
      let balance = startBalance;
      for (let y = 1; y <= 200; y++) {
        balance = balance * (1 + r) + annualSave;
        if (balance >= fireNumber) return y;
      }
      return null;
    }
    return annualSave > 0 ? (fireNumber - startBalance) / annualSave : Infinity;
  };

  const yearsWithout = calcYears(currentSave);
  const yearsWith    = calcYears(afterPurchase);
  const delay        = (yearsWithout !== null && yearsWith !== null)
    ? yearsWith - yearsWithout
    : null;

  const fireYearWithout = yearsWithout !== null
    ? new Date().getFullYear() + Math.ceil(yearsWithout) : null;
  const fireYearWith    = yearsWith !== null
    ? new Date().getFullYear() + Math.ceil(yearsWith)    : null;

  // ── Chart data ──────────────────────────────────────────────────
  const chartData = useMemo(() => {
    const maxYears = Math.min(
      Math.max(
        yearsWith    !== null ? Math.ceil(yearsWith)    : 0,
        yearsWithout !== null ? Math.ceil(yearsWithout) : 0,
      ) + 10,
      60
    );
    return Array.from({ length: maxYears + 1 }, (_, y) => {
      const withoutPortfolio = r !== 0
        ? currentSave   * Math.pow(1 + r, y) + annualSave * (Math.pow(1 + r, y) - 1) / r
        : currentSave   + annualSave * y;
      const withPortfolio = r !== 0
        ? afterPurchase * Math.pow(1 + r, y) + annualSave * (Math.pow(1 + r, y) - 1) / r
        : afterPurchase + annualSave * y;
      return {
        year:      new Date().getFullYear() + y,
        without:   Math.round(Math.max(0, withoutPortfolio)),
        with:      Math.round(Math.max(0, withPortfolio)),
        fireTarget:Math.round(fireNumber),
      };
    });
  }, [currentSave, afterPurchase, annualSave, r, fireNumber, yearsWithout, yearsWith]);

  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <Field label="Annual Expenses">
          <Input value={annualExp}   onChange={v=>setAnnualExp(+v)}   prefix={sym}/>
        </Field>
        <Field label="Current Portfolio">
          <Input value={currentSave} onChange={v=>setCurrentSave(+v)} prefix={sym}/>
        </Field>
        <Field label="Annual Contributions">
          <Input value={annualSave}  onChange={v=>setAnnualSave(+v)}  prefix={sym}/>
        </Field>
        <Field label="Purchase Amount">
          <Input value={purchase}    onChange={v=>setPurchase(+v)}    prefix={sym}/>
        </Field>
        <Field label={`Return Rate: ${returnRate}%`}>
          <Slider value={returnRate}   onChange={setReturnRate}   min={1} max={15} step={0.5}/>
        </Field>
        <Field label={`Withdrawal Rate: ${withdrawRate}%`}>
          <Slider value={withdrawRate} onChange={setWithdrawRate} min={2} max={6}  step={0.25}/>
        </Field>
      </div>

      <ResultGrid>
        <ResultCard label="Without Purchase" value={fmtYrs(yearsWithout)}                              color={T.green}/>
        <ResultCard label="With Purchase"    value={fmtYrs(yearsWith)}                                 color={T.red}   large/>
        <ResultCard label="FIRE Delayed By"  value={delay !== null ? `${delay.toFixed(1)} yrs` : '—'} color={T.orange}/>
        <ResultCard label="FIRE Number"      value={fmt(fireNumber, sym)}                              color={T.blue}/>
      </ResultGrid>

      {/* Chart */}
      <div style={{ marginTop:24 }}>
        <div style={{
          fontSize:12, fontWeight:700, color:T.muted,
          textTransform:'uppercase', letterSpacing:'.08em', marginBottom:12,
        }}>
          Portfolio Growth — With vs Without Purchase
        </div>

        {purchase > currentSave && (
          <div style={{
            padding:'10px 14px', borderRadius:8, marginBottom:12,
            background:`${T.orange}12`, border:`1px solid ${T.orange}30`,
            fontSize:12.5, color:T.orange,
          }}>
            ⚠ Purchase amount exceeds current portfolio — balance would be zero after purchase.
          </div>
        )}

        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={chartData} margin={{ top:4, right:16, left:8, bottom:4 }}>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="rgba(255,255,255,.06)"
              vertical={false}
            />
            <XAxis
              dataKey="year"
              tick={{ fill:T.muted, fontSize:11 }}
              tickLine={false}
              axisLine={false}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fill:T.muted, fontSize:11 }}
              tickLine={false}
              axisLine={false}
              width={60}
              tickFormatter={n => shortNum(n, sym)}
            />
            <Tooltip
              contentStyle={{
                background:T.bg2, border:`1px solid ${T.border}`,
                borderRadius:8, fontSize:12,
              }}
              labelStyle={{ color:T.muted, marginBottom:4 }}
              itemStyle={{ color:T.white }}
              formatter={(value, name) => [
                shortNum(value, sym),
                name === 'without'   ? 'Without purchase' :
                name === 'with'      ? `With purchase (−${sym}${purchase.toLocaleString()})` :
                'FIRE target',
              ]}
              labelFormatter={year => `Year ${year}`}
            />
            <Legend
              wrapperStyle={{ fontSize:12, color:T.muted, paddingTop:8 }}
              formatter={name =>
                name === 'without'  ? 'Without purchase' :
                name === 'with'     ? `With purchase (−${sym}${purchase.toLocaleString()})` :
                'FIRE target'
              }
            />

            {/* Without purchase FIRE date */}
            {fireYearWithout && (
              <ReferenceLine
                x={fireYearWithout}
                stroke={T.green}
                strokeDasharray="4 3"
                strokeWidth={1}
                label={{
                  value:'No purchase',
                  position:'top',
                  fill:T.green,
                  fontSize:10,
                  fontWeight:700,
                }}
              />
            )}

            {/* With purchase FIRE date */}
            {fireYearWith && fireYearWith !== fireYearWithout && (
              <ReferenceLine
                x={fireYearWith}
                stroke={T.red}
                strokeDasharray="4 3"
                strokeWidth={1}
                label={{
                  value:'After purchase',
                  position:'insideTopRight',
                  fill:T.red,
                  fontSize:10,
                  fontWeight:700,
                }}
              />
            )}

            {/* FIRE target */}
            <Line
              type="monotone"
              dataKey="fireTarget"
              stroke={T.blue}
              strokeWidth={1.5}
              strokeDasharray="6 4"
              dot={false}
              activeDot={false}
            />
            {/* Without purchase */}
            <Line
              type="monotone"
              dataKey="without"
              stroke={T.green}
              strokeWidth={2}
              dot={false}
              activeDot={{ r:4, fill:T.green }}
            />
            {/* With purchase */}
            <Line
              type="monotone"
              dataKey="with"
              stroke={T.red}
              strokeWidth={2}
              dot={false}
              activeDot={{ r:4, fill:T.red }}
            />
          </LineChart>
        </ResponsiveContainer>

        {/* Delay callout */}
        {delay !== null && delay > 0 && (
          <div style={{
            marginTop:12, padding:'10px 14px', borderRadius:8,
            background:`${T.red}10`, border:`1px solid ${T.red}25`,
            fontSize:12.5, color:T.red, textAlign:'center',
          }}>
            This {sym}{purchase.toLocaleString()} purchase delays your FIRE date by{' '}
            <strong>{delay.toFixed(1)} years</strong> — retiring in {fireYearWith} instead of {fireYearWithout}.
          </div>
        )}

        {delay !== null && delay <= 0 && (
          <div style={{
            marginTop:12, padding:'10px 14px', borderRadius:8,
            background:`${T.green}10`, border:`1px solid ${T.green}25`,
            fontSize:12.5, color:T.green, textAlign:'center',
          }}>
            This purchase has no meaningful impact on your FIRE date.
          </div>
        )}
      </div>
    </div>
  );
}
/* 7. COMPOUND INTEREST */
function CompoundInterestCalc({ sym }) {
  const [principal,  setPrincipal]  = useState(10000);
  const [monthly,    setMonthly]    = useState(500);
  const [rate,       setRate]       = useState(8);
  const [years,      setYears]      = useState(30);
  const [compound,   setCompound]   = useState(12);

  const n = compound;
  const r = rate / 100;
  const t = years;
  const finalAmount = principal * Math.pow(1 + r/n, n*t)
    + monthly * (Math.pow(1 + r/n, n*t) - 1) / (r/n);
  const totalContrib = principal + monthly * 12 * t;
  const totalInterest = finalAmount - totalContrib;
const chartData = useMemo(() => {
  return Array.from({ length: years + 1 }, (_, y) => {
    const compounded = principal * Math.pow(1 + r/n, n*y)
      + monthly * (Math.pow(1 + r/n, n*y) - 1) / (r/n);
    const contributions = principal + monthly * 12 * y;
    return {
      year:          new Date().getFullYear() + y,
      total:         Math.round(Math.max(0, compounded)),
      contributions: Math.round(contributions),
      interest:      Math.round(Math.max(0, compounded - contributions)),
    };
  });
}, [principal, monthly, r, n, years]);
  return (
    <div>
      <div className="ft-grid-2" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <Field label="Initial Investment"><Input value={principal} onChange={v=>setPrincipal(+v)} prefix={sym}/></Field>
        <Field label="Monthly Contribution"><Input value={monthly} onChange={v=>setMonthly(+v)} prefix={sym}/></Field>
        <Field label={`Annual Return: ${rate}%`}><Slider value={rate} onChange={setRate} min={1} max={20} step={0.5}/></Field>
        <Field label={`Time Period: ${years} years`}><Slider value={years} onChange={setYears} min={1} max={50}/></Field>
        <Field label="Compound Frequency">
          <select value={compound} onChange={e=>setCompound(+e.target.value)} style={{ ...inputStyle }}>
            <option value={1}>Annually</option>
            <option value={4}>Quarterly</option>
            <option value={12}>Monthly</option>
            <option value={365}>Daily</option>
          </select>
        </Field>
      </div>
      <ResultGrid>
        <ResultCard label="Final Amount" value={fmt(finalAmount,sym)} color={T.green} large/>
        <ResultCard label="Total Contributions" value={fmt(totalContrib,sym)} color={T.blue}/>
        <ResultCard label="Total Interest" value={fmt(totalInterest,sym)} color={T.cyan}/>
        <ResultCard label="Growth" value={fmtPct((finalAmount/totalContrib-1)*100)} color={T.yellow}/>
      </ResultGrid>
      <div style={{ marginTop:24 }}>
  <div style={{
    fontSize:12, fontWeight:700, color:T.muted,
    textTransform:'uppercase', letterSpacing:'.08em', marginBottom:12,
  }}>
    Growth Over Time
  </div>

  <ResponsiveContainer width="100%" height={260}>
    <LineChart data={chartData} margin={{ top:4, right:16, left:8, bottom:4 }}>
      <CartesianGrid
        strokeDasharray="3 3"
        stroke="rgba(255,255,255,.06)"
        vertical={false}
      />
      <XAxis
        dataKey="year"
        tick={{ fill:T.muted, fontSize:11 }}
        tickLine={false}
        axisLine={false}
        interval="preserveStartEnd"
      />
      <YAxis
        tick={{ fill:T.muted, fontSize:11 }}
        tickLine={false}
        axisLine={false}
        width={60}
        tickFormatter={n => shortNum(n, sym)}
      />
      <Tooltip
        contentStyle={{
          background:T.bg2, border:`1px solid ${T.border}`,
          borderRadius:8, fontSize:12,
        }}
        labelStyle={{ color:T.muted, marginBottom:4 }}
        itemStyle={{ color:T.white }}
        formatter={(value, name) => [
          shortNum(value, sym),
          name === 'total'         ? 'Total value' :
          name === 'contributions' ? 'Contributions' :
          'Interest earned',
        ]}
        labelFormatter={year => `Year ${year}`}
      />
      <Legend
        wrapperStyle={{ fontSize:12, color:T.muted, paddingTop:8 }}
        formatter={name =>
          name === 'total'         ? 'Total value' :
          name === 'contributions' ? 'Contributions' :
          'Interest earned'
        }
      />

      {/* Contributions — what you put in */}
      <Line
        type="monotone"
        dataKey="contributions"
        stroke={T.blue}
        strokeWidth={1.5}
        strokeDasharray="6 4"
        dot={false}
        activeDot={false}
      />
      {/* Interest earned — the gap between contributions and total */}
      <Line
        type="monotone"
        dataKey="interest"
        stroke={T.cyan}
        strokeWidth={1.5}
        strokeDasharray="3 3"
        dot={false}
        activeDot={false}
      />
      {/* Total value — the main line */}
      <Line
        type="monotone"
        dataKey="total"
        stroke={T.green}
        strokeWidth={2}
        dot={false}
        activeDot={{ r:4, fill:T.green }}
      />
    </LineChart>
  </ResponsiveContainer>

  {/* Interest vs contributions breakdown */}
  <div style={{
    marginTop:12, display:'flex', gap:8,
  }}>
    <div style={{
      flex:1, padding:'10px 14px', borderRadius:8,
      background:`${T.blue}10`, border:`1px solid ${T.blue}25`,
      fontSize:12, textAlign:'center',
    }}>
      <div style={{ color:T.muted, marginBottom:3 }}>You contributed</div>
      <div style={{ color:T.blue, fontWeight:700, fontSize:14 }}>{fmt(totalContrib, sym)}</div>
    </div>
    <div style={{
      flex:1, padding:'10px 14px', borderRadius:8,
      background:`${T.cyan}10`, border:`1px solid ${T.cyan}25`,
      fontSize:12, textAlign:'center',
    }}>
      <div style={{ color:T.muted, marginBottom:3 }}>Market added</div>
      <div style={{ color:T.cyan, fontWeight:700, fontSize:14 }}>{fmt(totalInterest, sym)}</div>
    </div>
    <div style={{
      flex:1, padding:'10px 14px', borderRadius:8,
      background:`${T.green}10`, border:`1px solid ${T.green}25`,
      fontSize:12, textAlign:'center',
    }}>
      <div style={{ color:T.muted, marginBottom:3 }}>
        {totalInterest > totalContrib ? 'Market did ' : 'You did '}
        more work
      </div>
      <div style={{ color:T.green, fontWeight:700, fontSize:14 }}>
        {fmtPct((totalInterest / totalContrib) * 100)} return
      </div>
    </div>
  </div>
</div>
    </div>
  );
}

/* 8. LOAN PAYMENT CALCULATOR */
function LoanCalc({ sym }) {
  const [principal, setPrincipal] = useState(300000);
  const [rate,      setRate]      = useState(6.5);
  const [years,     setYears]     = useState(30);
  const [extra,     setExtra]     = useState(0);

  const monthlyRate    = rate / 100 / 12;
  const n              = years * 12;
  const payment        = monthlyRate > 0
    ? principal * (monthlyRate * Math.pow(1+monthlyRate,n)) / (Math.pow(1+monthlyRate,n)-1)
    : principal / n;
  const totalPaid      = payment * n;
  const totalInterest  = totalPaid - principal;

  // With extra payment
  let balExtra = principal, monthsExtra = 0;
  if (extra > 0) {
    let b = principal;
    for (let i=0;i<600;i++) {
      b = b * (1 + monthlyRate) - (payment + extra);
      monthsExtra++;
      if (b <= 0) break;
    }
  }
  const monthsSaved = extra > 0 ? n - monthsExtra : 0;
const chartData = useMemo(() => {
  const points = [];
  let balance    = principal;
  let cumInterest= 0;
  let balExtra   = principal;
  let cumIntExtra= 0;
  let extraDone  = false;

  for (let m = 0; m <= n; m++) {
    // Sample yearly (every 12 months) + month 0
    if (m % 12 === 0) {
      points.push({
        year:        new Date().getFullYear() + m / 12,
        balance:     Math.round(Math.max(0, balance)),
        cumInterest: Math.round(cumInterest),
        balExtra:    extra > 0 ? Math.round(Math.max(0, extraDone ? 0 : balExtra)) : undefined,
      });
    }
    // Advance standard loan
    if (balance > 0) {
      const intCharge = balance * monthlyRate;
      cumInterest    += intCharge;
      balance         = balance * (1 + monthlyRate) - payment;
      if (balance < 0) balance = 0;
    }
    // Advance extra payment loan
    if (extra > 0 && !extraDone) {
      const intChargeEx = balExtra * monthlyRate;
      cumIntExtra      += intChargeEx;
      balExtra          = balExtra * (1 + monthlyRate) - (payment + extra);
      if (balExtra <= 0) { balExtra = 0; extraDone = true; }
    }
  }
  return points;
}, [principal, payment, monthlyRate, n, extra]);
  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <Field label="Loan Amount"><Input value={principal} onChange={v=>setPrincipal(+v)} prefix={sym}/></Field>
        <Field label={`Interest Rate: ${rate}%`}><Slider value={rate} onChange={setRate} min={1} max={20} step={0.25}/></Field>
        <Field label={`Loan Term: ${years} years`}><Slider value={years} onChange={setYears} min={1} max={30}/></Field>
        <Field label="Extra Monthly Payment" help="optional"><Input value={extra} onChange={v=>setExtra(+v)} prefix={sym}/></Field>
      </div>
      <ResultGrid>
        <ResultCard label="Monthly Payment" value={fmt(payment,sym,2)} color={T.cyan} large/>
        <ResultCard label="Total Interest" value={fmt(totalInterest,sym)} color={T.red}/>
        <ResultCard label="Total Paid" value={fmt(totalPaid,sym)} color={T.blue}/>
        {extra > 0 && <ResultCard label="Months Saved" value={`${monthsSaved} mo`} color={T.green}/>}
      </ResultGrid>
      <div style={{ marginTop:24 }}>
  <div style={{
    fontSize:12, fontWeight:700, color:T.muted,
    textTransform:'uppercase', letterSpacing:'.08em', marginBottom:12,
  }}>
    Amortization Over Time
  </div>

  <ResponsiveContainer width="100%" height={260}>
    <LineChart data={chartData} margin={{ top:4, right:16, left:8, bottom:4 }}>
      <CartesianGrid
        strokeDasharray="3 3"
        stroke="rgba(255,255,255,.06)"
        vertical={false}
      />
      <XAxis
        dataKey="year"
        tick={{ fill:T.muted, fontSize:11 }}
        tickLine={false}
        axisLine={false}
        interval="preserveStartEnd"
      />
      <YAxis
        tick={{ fill:T.muted, fontSize:11 }}
        tickLine={false}
        axisLine={false}
        width={60}
        tickFormatter={n => shortNum(n, sym)}
      />
      <Tooltip
        contentStyle={{
          background:T.bg2, border:`1px solid ${T.border}`,
          borderRadius:8, fontSize:12,
        }}
        labelStyle={{ color:T.muted, marginBottom:4 }}
        itemStyle={{ color:T.white }}
        formatter={(value, name) => [
          shortNum(value, sym),
          name === 'balance'     ? 'Balance remaining' :
          name === 'cumInterest' ? 'Interest paid so far' :
          `Balance w/ +${sym}${extra}/mo`,
        ]}
        labelFormatter={year => `Year ${year}`}
      />
      <Legend
        wrapperStyle={{ fontSize:12, color:T.muted, paddingTop:8 }}
        formatter={name =>
          name === 'balance'     ? 'Balance remaining' :
          name === 'cumInterest' ? 'Interest paid so far' :
          `Balance w/ extra ${sym}${extra}/mo`
        }
      />

      {/* Payoff line for extra payments */}
      {extra > 0 && monthsSaved > 0 && (
        <ReferenceLine
          x={new Date().getFullYear() + Math.ceil(monthsExtra / 12)}
          stroke={T.green}
          strokeDasharray="4 3"
          strokeWidth={1.5}
          label={{
            value:`Paid off ${monthsSaved} mo early`,
            position:'top',
            fill:T.green,
            fontSize:10,
            fontWeight:700,
          }}
        />
      )}

      {/* Cumulative interest */}
      <Line
        type="monotone"
        dataKey="cumInterest"
        stroke={T.red}
        strokeWidth={1.5}
        strokeDasharray="4 3"
        dot={false}
        activeDot={false}
      />
      {/* Balance with extra payments */}
      {extra > 0 && (
        <Line
          type="monotone"
          dataKey="balExtra"
          stroke={T.green}
          strokeWidth={2}
          dot={false}
          activeDot={{ r:4, fill:T.green }}
          connectNulls={false}
        />
      )}
      {/* Standard balance */}
      <Line
        type="monotone"
        dataKey="balance"
        stroke={T.cyan}
        strokeWidth={2}
        dot={false}
        activeDot={{ r:4, fill:T.cyan }}
      />
    </LineChart>
  </ResponsiveContainer>

  {/* Interest breakdown tiles */}
  <div style={{ marginTop:12, display:'flex', gap:8 }}>
    <div style={{
      flex:1, padding:'10px 14px', borderRadius:8,
      background:`${T.cyan}10`, border:`1px solid ${T.cyan}25`,
      fontSize:12, textAlign:'center',
    }}>
      <div style={{ color:T.muted, marginBottom:3 }}>Principal</div>
      <div style={{ color:T.cyan, fontWeight:700, fontSize:14 }}>{fmt(principal, sym)}</div>
    </div>
    <div style={{
      flex:1, padding:'10px 14px', borderRadius:8,
      background:`${T.red}10`, border:`1px solid ${T.red}25`,
      fontSize:12, textAlign:'center',
    }}>
      <div style={{ color:T.muted, marginBottom:3 }}>Interest cost</div>
      <div style={{ color:T.red, fontWeight:700, fontSize:14 }}>{fmt(totalInterest, sym)}</div>
    </div>
    <div style={{
      flex:1, padding:'10px 14px', borderRadius:8,
      background:`${T.blue}10`, border:`1px solid ${T.blue}25`,
      fontSize:12, textAlign:'center',
    }}>
      <div style={{ color:T.muted, marginBottom:3 }}>Interest ratio</div>
      <div style={{ color:T.blue, fontWeight:700, fontSize:14 }}>
        {fmtPct((totalInterest / principal) * 100)}
      </div>
    </div>
    {extra > 0 && monthsSaved > 0 && (
      <div style={{
        flex:1, padding:'10px 14px', borderRadius:8,
        background:`${T.green}10`, border:`1px solid ${T.green}25`,
        fontSize:12, textAlign:'center',
      }}>
        <div style={{ color:T.muted, marginBottom:3 }}>Months saved</div>
        <div style={{ color:T.green, fontWeight:700, fontSize:14 }}>{monthsSaved} mo</div>
      </div>
    )}
  </div>
</div>
    </div>
  );
}

/* 9. MORTGAGE 15 vs 30 */
function MortgageCalc({ sym }) {
  const [loan,  setLoan]  = useState(400000);
  const [rate15,setRate15]= useState(5.5);
  const [rate30,setRate30]= useState(6.5);

  const calc = (principal, rate, years) => {
    const r = rate/100/12, n = years*12;
    const payment = r > 0 ? principal*(r*Math.pow(1+r,n))/(Math.pow(1+r,n)-1) : principal/n;
    return { payment, total: payment*n, interest: payment*n - principal };
  };
  const y15 = calc(loan, rate15, 15);
  const y30 = calc(loan, rate30, 30);
  const interestSaved = y30.interest - y15.interest;

const chartData = useMemo(() => {
  const r15 = rate15 / 100 / 12;
  const r30 = rate30 / 100 / 12;
  const points = [];

  for (let y = 0; y <= 30; y++) {
    const m15 = y * 12;
    const m30 = y * 12;

    // 15-year balance at year y
    const bal15 = y <= 15
      ? m15 === 0 ? loan
        : r15 > 0
          ? loan * Math.pow(1 + r15, m15) - y15.payment * (Math.pow(1 + r15, m15) - 1) / r15
          : Math.max(0, loan - y15.payment * m15)
      : 0;

    // 30-year balance at year y
    const bal30 = r30 > 0
      ? loan * Math.pow(1 + r30, m30) - y30.payment * (Math.pow(1 + r30, m30) - 1) / r30
      : Math.max(0, loan - y30.payment * m30);

    // Cumulative interest paid
    const cumInt15 = y <= 15 ? y15.payment * m15 - (loan - Math.max(0, bal15)) : y15.total - loan;
    const cumInt30 = y30.payment * m30 - (loan - Math.max(0, bal30));

    points.push({
      year:    new Date().getFullYear() + y,
      bal15:   Math.round(Math.max(0, bal15)),
      bal30:   Math.round(Math.max(0, bal30)),
      cumInt15:Math.round(Math.max(0, cumInt15)),
      cumInt30:Math.round(Math.max(0, cumInt30)),
    });
  }
  return points;
}, [loan, rate15, rate30, y15.payment, y30.payment, y15.total]);

  return (
    <div>
      <div className="ft-grid-2"style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <Field label="Loan Amount" style={{ gridColumn:'1/-1' }}><Input value={loan} onChange={v=>setLoan(+v)} prefix={sym}/></Field>
        <Field label={`15-Year Rate: ${rate15}%`}><Slider value={rate15} onChange={setRate15} min={1} max={15} step={0.125}/></Field>
        <Field label={`30-Year Rate: ${rate30}%`}><Slider value={rate30} onChange={setRate30} min={1} max={15} step={0.125}/></Field>
      </div>
      <div className="ft-grid-2" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginTop:16 }}>
        {[{label:'15-Year',data:y15,color:T.green},{label:'30-Year',data:y30,color:T.blue}].map(({label,data,color})=>(
          <div key={label} style={{ padding:'14px', background:`${color}0d`, border:`1px solid ${color}30`, borderRadius:10 }}>
            <div style={{ fontSize:13, fontWeight:700, color, marginBottom:10 }}>{label} Mortgage</div>
            {[['Monthly',fmt(data.payment,sym,2)],['Total Paid',fmt(data.total,sym)],['Total Interest',fmt(data.interest,sym)]].map(([l,v])=>(
              <div key={l} style={{ display:'flex', justifyContent:'space-between', padding:'4px 0', fontSize:12, borderBottom:`1px solid ${T.border}` }}>
                <span style={{ color:T.muted }}>{l}</span>
                <span style={{ color:T.white, fontFamily:T.mono }}>{v}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
      <ResultGrid>
        <ResultCard label="Interest Saved (15yr)" value={fmt(interestSaved,sym)} color={T.green} large/>
        <ResultCard label="Extra Monthly (15yr)" value={fmt(y15.payment-y30.payment,sym,2)} color={T.orange}/>
      </ResultGrid>
      <div style={{ marginTop:24 }}>
  <div style={{
    fontSize:12, fontWeight:700, color:T.muted,
    textTransform:'uppercase', letterSpacing:'.08em', marginBottom:12,
  }}>
    Balance Remaining Over Time
  </div>

  <ResponsiveContainer width="100%" height={260}>
    <LineChart data={chartData} margin={{ top:4, right:16, left:8, bottom:4 }}>
      <CartesianGrid
        strokeDasharray="3 3"
        stroke="rgba(255,255,255,.06)"
        vertical={false}
      />
      <XAxis
        dataKey="year"
        tick={{ fill:T.muted, fontSize:11 }}
        tickLine={false}
        axisLine={false}
        interval="preserveStartEnd"
      />
      <YAxis
        tick={{ fill:T.muted, fontSize:11 }}
        tickLine={false}
        axisLine={false}
        width={60}
        tickFormatter={n => shortNum(n, sym)}
      />
      <Tooltip
        contentStyle={{
          background:T.bg2, border:`1px solid ${T.border}`,
          borderRadius:8, fontSize:12,
        }}
        labelStyle={{ color:T.muted, marginBottom:4 }}
        itemStyle={{ color:T.white }}
        formatter={(value, name) => [
          shortNum(value, sym),
          name === 'bal15'    ? '15-yr balance' :
          name === 'bal30'    ? '30-yr balance' :
          name === 'cumInt15' ? '15-yr interest paid' :
          '30-yr interest paid',
        ]}
        labelFormatter={year => `Year ${year}`}
      />
      <Legend
        wrapperStyle={{ fontSize:12, color:T.muted, paddingTop:8 }}
        formatter={name =>
          name === 'bal15'    ? '15-yr balance' :
          name === 'bal30'    ? '30-yr balance' :
          name === 'cumInt15' ? '15-yr interest paid' :
          '30-yr interest paid'
        }
      />

      {/* 15-yr paid off marker */}
      <ReferenceLine
        x={new Date().getFullYear() + 15}
        stroke={T.green}
        strokeDasharray="4 3"
        strokeWidth={1}
        label={{ value:'15yr paid off', position:'top', fill:T.green, fontSize:10, fontWeight:700 }}
      />

      {/* Cumulative interest lines — dashed */}
      <Line type="monotone" dataKey="cumInt15" stroke={T.green}  strokeWidth={1.5} strokeDasharray="4 3" dot={false} activeDot={false}/>
      <Line type="monotone" dataKey="cumInt30" stroke={T.blue}   strokeWidth={1.5} strokeDasharray="4 3" dot={false} activeDot={false}/>

      {/* Balance lines — solid */}
      <Line type="monotone" dataKey="bal15"    stroke={T.green}  strokeWidth={2}   dot={false} activeDot={{ r:4, fill:T.green }}/>
      <Line type="monotone" dataKey="bal30"    stroke={T.blue}   strokeWidth={2}   dot={false} activeDot={{ r:4, fill:T.blue  }}/>
    </LineChart>
  </ResponsiveContainer>

  {/* Comparison summary tiles */}
  <div style={{ marginTop:12, display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
    {[
      { label:'15-yr total interest', value:fmt(y15.interest, sym), color:T.green },
      { label:'30-yr total interest', value:fmt(y30.interest, sym), color:T.blue  },
      { label:'Interest saved',       value:fmt(interestSaved, sym),color:T.cyan  },
      { label:'Extra monthly cost',   value:fmt(y15.payment - y30.payment, sym, 2), color:T.orange },
    ].map(({ label, value, color }) => (
      <div key={label} style={{
        padding:'10px 14px', borderRadius:8,
        background:`${color}10`, border:`1px solid ${color}25`,
        fontSize:12, display:'flex', justifyContent:'space-between', alignItems:'center',
      }}>
        <span style={{ color:T.muted }}>{label}</span>
        <span style={{ color, fontWeight:700, fontSize:13, fontFamily:T.mono }}>{value}</span>
      </div>
    ))}
  </div>
</div>
    </div>
  );
}

/* 10. PORTFOLIO REBALANCING */
function PortfolioRebalanceCalc({ sym }) {
  const [portfolio, setPortfolio] = useState([
    { name:'US Stocks',   current:45, target:60, value:45000 },
    { name:'Bonds',       current:30, target:20, value:30000 },
    { name:'Int\'l Stock',current:15, target:15, value:15000 },
    { name:'Real Estate', current:10, target:5,  value:10000 },
  ]);
  const total = portfolio.reduce((s,a)=>s+a.value,0);

  const rebalanced = portfolio.map(a => ({
    ...a,
    targetValue: (a.target/100) * total,
    action: (a.target/100)*total - a.value,
  }));

  return (
    <div>
      <div style={{ marginBottom:14, fontSize:12, color:T.muted }}>
        Edit target allocations to see rebalancing actions. Total portfolio: {fmt(total,sym)}
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
        {rebalanced.map((a,i) => (
          <div key={i} style={{ padding:'10px 12px', background:T.bg2, border:`1px solid ${T.border}`, borderRadius:8 }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 80px 80px 80px 100px', gap:8, alignItems:'center' }}>
              <span style={{ fontSize:12.5, fontWeight:600, color:T.white }}>{a.name}</span>
              <span style={{ fontSize:12, color:T.muted, textAlign:'center' }}>{fmt(a.value,sym)}</span>
              <div style={{ textAlign:'center' }}>
                <input type="number" value={a.target} min={0} max={100}
                  onChange={e=>{const p=[...portfolio]; p[i]={...p[i],target:+e.target.value}; setPortfolio(p);}}
                  style={{ ...inputStyle, padding:'4px 6px', width:'100%', textAlign:'center', fontSize:12 }}
                />
              </div>
              <span style={{ fontSize:12, color:T.muted, textAlign:'center' }}>{fmt(a.targetValue,sym)}</span>
              <span style={{
                fontSize:12, fontFamily:T.mono, fontWeight:700, textAlign:'right',
                color: a.action > 0 ? T.green : T.red,
              }}>
                {a.action > 0 ? '+' : ''}{fmt(a.action,sym,0)}
              </span>
            </div>
          </div>
        ))}
      </div>
      <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:T.dim, marginTop:6 }}>
        <span>Asset</span><span>Current</span><span>Target%</span><span>Target{sym}</span><span>Action</span>
      </div>
    </div>
  );
}

/* 11. SAVINGS RATE */
function SavingsRateCalc({ sym }) {
  const [grossIncome, setGrossIncome] = useState(80000);
  const [netIncome,   setNetIncome]   = useState(60000);
  const [expenses,    setExpenses]    = useState(40000);
  const [employer401, setEmployer401] = useState(3000);

  const totalSavings   = netIncome - expenses + employer401;
  const savingsRate    = netIncome > 0 ? (totalSavings / netIncome) * 100 : 0;
  const grossSavRate   = grossIncome > 0 ? (totalSavings / grossIncome) * 100 : 0;
  const monthlyExp     = expenses / 12;
  const monthlyIncome  = netIncome / 12;
// How savings rate affects years to FIRE (classic MMM calculation)
const yearsToFIREData = useMemo(() => {
  return Array.from({ length: 21 }, (_, i) => {
    const sr   = (i * 5) / 100;   // 0% to 100% in 5% steps
    const spend = 1 - sr;
    // Years to FIRE = log(1 / (1 - 25 * spend * r)) / log(1 + r)  where r = 5% real return
    const r    = 0.05;
    const fiN  = spend / 0.04;     // 25x spending
    const yrs  = sr > 0 && spend > 0
      ? Math.log((fiN - sr / r) / (1 - sr / r)) / Math.log(1 + r)
      : null;
    return {
      rate:  i * 5,
      years: yrs !== null && yrs > 0 && yrs < 100 ? Math.round(yrs * 10) / 10 : null,
    };
  }).filter(d => d.years !== null);
}, []);

// Income breakdown for bar chart
const breakdown = useMemo(() => [
  { name:'Taxes',    value: Math.max(0, grossIncome - netIncome), color:T.red    },
  { name:'Expenses', value: Math.max(0, expenses),                color:T.orange },
  { name:'Savings',  value: Math.max(0, totalSavings),            color:T.green  },
], [grossIncome, netIncome, expenses, totalSavings]);
  return (
    <div>
      <div className="ft-grid-2" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <Field label="Gross Annual Income"><Input value={grossIncome} onChange={v=>setGrossIncome(+v)} prefix={sym}/></Field>
        <Field label="Net Annual Income (take-home)"><Input value={netIncome} onChange={v=>setNetIncome(+v)} prefix={sym}/></Field>
        <Field label="Annual Expenses"><Input value={expenses} onChange={v=>setExpenses(+v)} prefix={sym}/></Field>
        <Field label="Employer 401k Match (annual)"><Input value={employer401} onChange={v=>setEmployer401(+v)} prefix={sym}/></Field>
      </div>
      <ResultGrid>
        <ResultCard label="Savings Rate (net)" value={fmtPct(savingsRate)} color={savingsRate>=20?T.green:T.orange} large/>
        <ResultCard label="Savings Rate (gross)" value={fmtPct(grossSavRate)} color={T.blue}/>
        <ResultCard label="Annual Savings" value={fmt(totalSavings,sym)} color={T.cyan}/>
        <ResultCard label="Monthly Surplus" value={fmt((totalSavings)/12,sym)} color={T.yellow}/>
      </ResultGrid>
      <div style={{ marginTop:24 }}>

  {/* ── Income breakdown bar ───────────────────────────────── */}
  <div style={{
    fontSize:12, fontWeight:700, color:T.muted,
    textTransform:'uppercase', letterSpacing:'.08em', marginBottom:10,
  }}>
    Where Your Income Goes
  </div>

  <div style={{ marginBottom:6 }}>
    <div style={{ display:'flex', height:28, borderRadius:6, overflow:'hidden', gap:2 }}>
      {breakdown.map(({ name, value, color }) => {
        const pct = grossIncome > 0 ? (value / grossIncome) * 100 : 0;
        if (pct <= 0) return null;
        return (
          <div
            key={name}
            style={{
              width:`${pct}%`, background:color,
              display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:11, fontWeight:700, color:'#07080c',
              transition:'width .4s', whiteSpace:'nowrap', overflow:'hidden',
              minWidth: pct > 5 ? 'auto' : 0,
            }}
          >
            {pct > 8 ? `${pct.toFixed(0)}%` : ''}
          </div>
        );
      })}
    </div>
    <div style={{ display:'flex', gap:16, marginTop:7, flexWrap:'wrap' }}>
      {breakdown.map(({ name, value, color }) => {
        const pct = grossIncome > 0 ? (value / grossIncome) * 100 : 0;
        return (
          <div key={name} style={{ display:'flex', alignItems:'center', gap:5, fontSize:11.5 }}>
            <div style={{ width:9, height:9, borderRadius:2, background:color, flexShrink:0 }}/>
            <span style={{ color:T.muted }}>{name}</span>
            <span style={{ color:T.white, fontFamily:T.mono }}>{fmt(value, sym)}</span>
            <span style={{ color:T.dim }}>({pct.toFixed(1)}%)</span>
          </div>
        );
      })}
    </div>
  </div>

  {/* Savings rate benchmarks */}
  <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:7, marginTop:14, marginBottom:24 }}>
    {[
      { label:'10% — Getting started', rate:10, color:T.red    },
      { label:'20% — Good baseline',   rate:20, color:T.orange },
      { label:'40% — FIRE-focused',    rate:40, color:T.cyan   },
      { label:'60%+ — Aggressive',     rate:60, color:T.green  },
    ].map(({ label, rate: benchmark, color }) => (
      <div key={label} style={{
        padding:'8px 10px', borderRadius:8,
        background: savingsRate >= benchmark ? `${color}18` : 'rgba(255,255,255,.03)',
        border:`1px solid ${savingsRate >= benchmark ? color+'40' : T.border}`,
        fontSize:10.5, color: savingsRate >= benchmark ? color : T.dim,
        textAlign:'center', lineHeight:1.5,
        transition:'all .3s',
      }}>
        {label}
        {savingsRate >= benchmark && <div style={{ fontSize:10, marginTop:3 }}>✓ Achieved</div>}
      </div>
    ))}
  </div>

  {/* ── Savings rate → years to FIRE curve ────────────────── */}
  <div style={{
    fontSize:12, fontWeight:700, color:T.muted,
    textTransform:'uppercase', letterSpacing:'.08em', marginBottom:8,
  }}>
    Savings Rate vs Years to FIRE
  </div>
  <div style={{ fontSize:12, color:T.muted, marginBottom:10 }}>
    Assumes 5% real return, 4% withdrawal rate, starting from zero.
    Your current savings rate is highlighted.
  </div>

  <ResponsiveContainer width="100%" height={240}>
    <LineChart data={yearsToFIREData} margin={{ top:4, right:16, left:8, bottom:4 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.06)" vertical={false}/>
      <XAxis
        dataKey="rate"
        tick={{ fill:T.muted, fontSize:11 }}
        tickLine={false} axisLine={false}
        tickFormatter={v => `${v}%`}
        interval="preserveStartEnd"
      />
      <YAxis
        tick={{ fill:T.muted, fontSize:11 }}
        tickLine={false} axisLine={false}
        width={40}
        tickFormatter={v => `${v}yr`}
        domain={[0, 'auto']}
      />
      <Tooltip
        contentStyle={{ background:T.bg2, border:`1px solid ${T.border}`, borderRadius:8, fontSize:12 }}
        labelStyle={{ color:T.muted, marginBottom:4 }}
        itemStyle={{ color:T.white }}
        formatter={(value) => [`${value} years`, 'Years to FIRE']}
        labelFormatter={v => `Savings rate: ${v}%`}
      />

      {/* Current savings rate marker */}
      <ReferenceLine
        x={Math.round(savingsRate / 5) * 5}
        stroke={T.cyan}
        strokeDasharray="4 3"
        strokeWidth={1.5}
        label={{
          value:`You (${savingsRate.toFixed(0)}%)`,
          position:'top',
          fill:T.cyan,
          fontSize:10,
          fontWeight:700,
        }}
      />

      <Line
        type="monotone"
        dataKey="years"
        stroke={T.green}
        strokeWidth={2.5}
        dot={false}
        activeDot={{ r:4, fill:T.green }}
        connectNulls={false}
      />
    </LineChart>
  </ResponsiveContainer>

  {/* Key insight callout */}
  {savingsRate > 0 && savingsRate < 100 && (() => {
    const spend = 1 - savingsRate / 100;
    const r     = 0.05;
    const fiN   = spend / 0.04;
    const num   = fiN - (savingsRate / 100) / r;
    const den   = 1   - (savingsRate / 100) / r;
    const yrs   = den > 0 && num > 0 ? Math.log(num / den) / Math.log(1 + r) : null;
    if (!yrs || yrs < 0) return null;
    const increaseBy5 = (() => {
      const sr2   = Math.min((savingsRate + 5) / 100, 0.99);
      const sp2   = 1 - sr2;
      const fin2  = sp2 / 0.04;
      const n2    = fin2 - sr2 / r;
      const d2    = 1   - sr2 / r;
      const y2    = d2 > 0 && n2 > 0 ? Math.log(n2 / d2) / Math.log(1 + r) : null;
      return y2 ? Math.round(yrs - y2) : null;
    })();

    return (
      <div style={{
        marginTop:12, padding:'10px 14px', borderRadius:8,
        background:`${T.cyan}10`, border:`1px solid ${T.cyan}25`,
        fontSize:12.5, color:T.muted, lineHeight:1.7,
      }}>
        At your current{' '}
        <span style={{ color:T.cyan, fontWeight:700 }}>{savingsRate.toFixed(1)}% savings rate</span>
        , you'd reach FIRE in approximately{' '}
        <span style={{ color:T.green, fontWeight:700 }}>{Math.ceil(yrs)} years</span>
        {increaseBy5 && increaseBy5 > 0 && (
          <> — increasing by just 5% saves another{' '}
          <span style={{ color:T.yellow, fontWeight:700 }}>{increaseBy5} years</span></>
        )}.
      </div>
    );
  })()}
</div>
    </div>
  );
}

/* 12. EMERGENCY FUND */
function EmergencyFundCalc({ sym }) {
  const [monthlyExp,  setMonthlyExp]  = useState(4000);
  const [jobRisk,     setJobRisk]     = useState('medium');
  const [dependents,  setDependents]  = useState(0);
  const [currentFund, setCurrentFund] = useState(10000);

  const riskMonths = { low:3, medium:6, high:9 }[jobRisk] + Math.min(dependents, 3);
  const target     = monthlyExp * riskMonths;
  const gap        = Math.max(0, target - currentFund);
  const pct        = Math.min(100, (currentFund/target)*100);
const monthlyContribOptions = [500, 1000, 2000, monthlyExp * 0.1].map(Math.round);

const timelineData = useMemo(() => {
  if (gap <= 0) return [];
  const maxMonths = 60;
  return Array.from({ length: maxMonths + 1 }, (_, m) => {
    const entry = { month: m };
    monthlyContribOptions.forEach(contrib => {
      entry[`c${contrib}`] = Math.min(target, currentFund + contrib * m);
    });
    entry.target = target;
    return entry;
  });
}, [gap, target, currentFund, monthlyContribOptions]);

// Months to fill gap at each contribution level
const monthsToFill = monthlyContribOptions.map(c =>
  gap > 0 && c > 0 ? Math.ceil(gap / c) : 0
);
  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <Field label="Monthly Expenses"><Input value={monthlyExp} onChange={v=>setMonthlyExp(+v)} prefix={sym}/></Field>
        <Field label="Current Emergency Fund"><Input value={currentFund} onChange={v=>setCurrentFund(+v)} prefix={sym}/></Field>
        <Field label="Job Stability">
          <select value={jobRisk} onChange={e=>setJobRisk(e.target.value)} style={inputStyle}>
            <option value="low">Stable / Government (3 months)</option>
            <option value="medium">Average / Corporate (6 months)</option>
            <option value="high">Freelance / Variable (9 months)</option>
          </select>
        </Field>
        <Field label={`Dependents: ${dependents}`}><Slider value={dependents} onChange={setDependents} min={0} max={5}/></Field>
      </div>
      <div style={{ marginTop:16, height:8, background:T.border, borderRadius:4, overflow:'hidden' }}>
        <div style={{ height:'100%', width:`${pct}%`, background:`linear-gradient(90deg,${pct>=100?T.green:T.cyan},${pct>=100?T.teal:T.blue})`, borderRadius:4, transition:'width .4s' }}/>
      </div>
      <div style={{ fontSize:11, color:T.muted, marginTop:4 }}>{pct.toFixed(0)}% funded</div>
      <ResultGrid>
        <ResultCard label="Target Fund" value={fmt(target,sym)} color={T.cyan} large/>
        <ResultCard label="Months Coverage" value={`${riskMonths} months`} color={T.blue}/>
        <ResultCard label="Gap to Fill" value={gap > 0 ? fmt(gap,sym) : '✅ Fully Funded'} color={gap>0?T.red:T.green}/>
        <ResultCard label="Current" value={fmt(currentFund,sym)} color={T.yellow}/>
      </ResultGrid>
      <div style={{ marginTop:24 }}>

  {/* ── Coverage visualization ─────────────────────────────── */}
  <div style={{
    fontSize:12, fontWeight:700, color:T.muted,
    textTransform:'uppercase', letterSpacing:'.08em', marginBottom:10,
  }}>
    Emergency Coverage Breakdown
  </div>

  <div style={{
    display:'grid', gridTemplateColumns:`repeat(${riskMonths},1fr)`,
    gap:4, marginBottom:16,
  }}>
    {Array.from({ length: riskMonths }, (_, i) => {
      const monthCovered = currentFund >= (i + 1) * monthlyExp;
      return (
        <div key={i} style={{
          height:36, borderRadius:5,
          background: monthCovered
            ? `linear-gradient(135deg,${T.cyan},${T.blue})`
            : 'rgba(255,255,255,.06)',
          border:`1px solid ${monthCovered ? T.cyan+'40' : T.border}`,
          display:'flex', alignItems:'center', justifyContent:'center',
          fontSize:10, fontWeight:700,
          color: monthCovered ? '#07080c' : T.dim,
          transition:'all .3s',
        }}>
          {i + 1}mo
        </div>
      );
    })}
  </div>

  <div style={{ fontSize:12, color:T.muted, marginBottom: gap <= 0 ? 0 : 20, lineHeight:1.6 }}>
    {gap <= 0
      ? `✅ You have ${riskMonths} months of expenses covered. You're fully funded for your risk level.`
      : `You have ${Math.floor(currentFund / monthlyExp)} of ${riskMonths} months covered. Need ${fmt(gap, sym)} more.`
    }
  </div>

  {/* ── Timeline to fill gap ───────────────────────────────── */}
  {gap > 0 && (
    <>
      <div style={{
        fontSize:12, fontWeight:700, color:T.muted,
        textTransform:'uppercase', letterSpacing:'.08em', marginBottom:8,
      }}>
        Time to Fill Gap — By Monthly Contribution
      </div>

      <ResponsiveContainer width="100%" height={240}>
        <LineChart data={timelineData} margin={{ top:4, right:16, left:8, bottom:4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.06)" vertical={false}/>
          <XAxis
            dataKey="month"
            tick={{ fill:T.muted, fontSize:11 }}
            tickLine={false} axisLine={false}
            interval="preserveStartEnd"
            tickFormatter={v => `Mo ${v}`}
          />
          <YAxis
            tick={{ fill:T.muted, fontSize:11 }}
            tickLine={false} axisLine={false}
            width={60}
            tickFormatter={n => shortNum(n, sym)}
          />
          <Tooltip
            contentStyle={{ background:T.bg2, border:`1px solid ${T.border}`, borderRadius:8, fontSize:12 }}
            labelStyle={{ color:T.muted, marginBottom:4 }}
            itemStyle={{ color:T.white }}
            formatter={(value, name) => [
              shortNum(value, sym),
              name === 'target'
                ? 'Target fund'
                : `Saving ${sym}${name.replace('c','')}/mo`,
            ]}
            labelFormatter={m => `Month ${m}`}
          />
          <Legend
            wrapperStyle={{ fontSize:11, color:T.muted, paddingTop:8 }}
            formatter={name =>
              name === 'target'
                ? 'Target fund'
                : `${sym}${name.replace('c','')}/mo`
            }
          />

          {/* Target line */}
          <Line
            type="monotone" dataKey="target"
            stroke={T.green} strokeWidth={1.5}
            strokeDasharray="6 4" dot={false} activeDot={false}
          />

          {/* One line per contribution level */}
          {[
            { key:`c${monthlyContribOptions[0]}`, color:T.red    },
            { key:`c${monthlyContribOptions[1]}`, color:T.orange },
            { key:`c${monthlyContribOptions[2]}`, color:T.cyan   },
            { key:`c${monthlyContribOptions[3]}`, color:T.purple },
          ].map(({ key, color }) => (
            <Line
              key={key}
              type="monotone" dataKey={key}
              stroke={color} strokeWidth={2}
              dot={false} activeDot={{ r:4, fill:color }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>

      {/* Months to fill summary */}
      <div style={{ marginTop:12, display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:7 }}>
        {monthlyContribOptions.map((contrib, i) => {
          const months = monthsToFill[i];
          const colors = [T.red, T.orange, T.cyan, T.purple];
          const color  = colors[i];
          return (
            <div key={contrib} style={{
              padding:'9px 10px', borderRadius:8,
              background:`${color}10`, border:`1px solid ${color}25`,
              fontSize:11.5, textAlign:'center',
            }}>
              <div style={{ color:T.muted, marginBottom:3 }}>
                {sym}{contrib.toLocaleString()}/mo
              </div>
              <div style={{ color, fontWeight:700, fontSize:13 }}>
                {months} months
              </div>
              <div style={{ color:T.dim, fontSize:10, marginTop:2 }}>
                {(months / 12).toFixed(1)} years
              </div>
            </div>
          );
        })}
      </div>

      {/* Recommended action */}
      <div style={{
        marginTop:12, padding:'10px 14px', borderRadius:8,
        background:`${T.cyan}10`, border:`1px solid ${T.cyan}25`,
        fontSize:12.5, color:T.muted, lineHeight:1.7,
      }}>
        Saving{' '}
        <span style={{ color:T.cyan, fontWeight:700 }}>
          {sym}{monthlyContribOptions[1].toLocaleString()}/mo
        </span>
        {' '}fills your emergency fund in{' '}
        <span style={{ color:T.green, fontWeight:700 }}>
          {monthsToFill[1]} months
        </span>
        . Consider automating this into a HYSA separate from your chequing account.
      </div>
    </>
  )}
</div>
    </div>
  );
}

/* 13. COMPOUND INTEREST / HSA GROWTH */
function HSACalc({ sym }) {
  const [annual,    setAnnual]    = useState(3850);
  const [employer,  setEmployer]  = useState(500);
  const [current,   setCurrent]   = useState(5000);
  const [returnRate,setReturnRate]= useState(7);
  const [years,     setYears]     = useState(25);

  const r       = returnRate / 100;
  const contrib  = annual + employer;
  const final    = current * Math.pow(1+r, years) + contrib * (Math.pow(1+r,years)-1)/r;
  const invested = current + contrib * years;
  const growth   = final - invested;
const chartData = useMemo(() => {
  return Array.from({ length: years + 1 }, (_, y) => {
    const balance      = current * Math.pow(1 + r, y) + contrib * (Math.pow(1 + r, y) - 1) / r;
    const contributions = current + contrib * y;
    return {
      year:         new Date().getFullYear() + y,
      balance:      Math.round(Math.max(0, balance)),
      contributions:Math.round(contributions),
      growth:       Math.round(Math.max(0, balance - contributions)),
    };
  });
}, [current, contrib, r, years]);
  return (
    <div>
      <div className="ft-grid-2"style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <Field label="Annual Contribution"><Input value={annual} onChange={v=>setAnnual(+v)} prefix={sym}/></Field>
        <Field label="Employer Contribution"><Input value={employer} onChange={v=>setEmployer(+v)} prefix={sym}/></Field>
        <Field label="Current Balance"><Input value={current} onChange={v=>setCurrent(+v)} prefix={sym}/></Field>
        <Field label={`Investment Return: ${returnRate}%`}><Slider value={returnRate} onChange={setReturnRate} min={1} max={15} step={0.5}/></Field>
        <Field label={`Years: ${years}`}><Slider value={years} onChange={setYears} min={1} max={40}/></Field>
      </div>
      <ResultGrid>
        <ResultCard label="Future HSA Balance" value={fmt(final,sym)} color={T.teal} large/>
        <ResultCard label="Total Contributed" value={fmt(invested,sym)} color={T.blue}/>
        <ResultCard label="Tax-Free Growth" value={fmt(growth,sym)} color={T.green}/>
        <ResultCard label="Annual Contrib" value={fmt(contrib,sym)} color={T.yellow}/>
      </ResultGrid>
      <div style={{ marginTop:24 }}>
  <div style={{
    fontSize:12, fontWeight:700, color:T.muted,
    textTransform:'uppercase', letterSpacing:'.08em', marginBottom:12,
  }}>
    HSA Growth Over Time
  </div>

  <ResponsiveContainer width="100%" height={260}>
    <LineChart data={chartData} margin={{ top:4, right:16, left:8, bottom:4 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.06)" vertical={false}/>
      <XAxis
        dataKey="year"
        tick={{ fill:T.muted, fontSize:11 }}
        tickLine={false} axisLine={false}
        interval="preserveStartEnd"
      />
      <YAxis
        tick={{ fill:T.muted, fontSize:11 }}
        tickLine={false} axisLine={false}
        width={60}
        tickFormatter={n => shortNum(n, sym)}
      />
      <Tooltip
        contentStyle={{ background:T.bg2, border:`1px solid ${T.border}`, borderRadius:8, fontSize:12 }}
        labelStyle={{ color:T.muted, marginBottom:4 }}
        itemStyle={{ color:T.white }}
        formatter={(value, name) => [
          shortNum(value, sym),
          name === 'balance'       ? 'HSA balance'        :
          name === 'contributions' ? 'Total contributions' :
          'Tax-free growth',
        ]}
        labelFormatter={year => `Year ${year}`}
      />
      <Legend
        wrapperStyle={{ fontSize:12, color:T.muted, paddingTop:8 }}
        formatter={name =>
          name === 'balance'       ? 'HSA balance'        :
          name === 'contributions' ? 'Total contributions' :
          'Tax-free growth'
        }
      />

      {/* Contributions — what you put in */}
      <Line
        type="monotone" dataKey="contributions"
        stroke={T.blue} strokeWidth={1.5}
        strokeDasharray="6 4" dot={false} activeDot={false}
      />
      {/* Tax-free growth — the compounding gains */}
      <Line
        type="monotone" dataKey="growth"
        stroke={T.green} strokeWidth={1.5}
        strokeDasharray="3 3" dot={false} activeDot={false}
      />
      {/* Total HSA balance */}
      <Line
        type="monotone" dataKey="balance"
        stroke={T.teal} strokeWidth={2.5}
        dot={false} activeDot={{ r:4, fill:T.teal }}
      />
    </LineChart>
  </ResponsiveContainer>

  {/* Triple power of HSA callout */}
  <div style={{ marginTop:12, display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8 }}>
    {[
      {
        icon:'1️⃣',
        label:'Tax deduction',
        desc:`${sym}${contrib.toLocaleString()} contributed pre-tax each year`,
        color:T.teal,
      },
      {
        icon:'2️⃣',
        label:'Tax-free growth',
        desc:`${sym}${Math.round(growth).toLocaleString()} earned with no capital gains tax`,
        color:T.green,
      },
      {
        icon:'3️⃣',
        label:'Tax-free withdrawal',
        desc:'Withdraw tax-free for any medical expense',
        color:T.blue,
      },
    ].map(({ icon, label, desc, color }) => (
      <div key={label} style={{
        padding:'11px 12px', borderRadius:8,
        background:`${color}10`, border:`1px solid ${color}25`,
        fontSize:11.5,
      }}>
        <div style={{ fontSize:16, marginBottom:5 }}>{icon}</div>
        <div style={{ color, fontWeight:700, marginBottom:3 }}>{label}</div>
        <div style={{ color:T.muted, lineHeight:1.5 }}>{desc}</div>
      </div>
    ))}
  </div>

  {/* Growth vs contributions crossover insight */}
  {(() => {
    const crossover = chartData.find(d => d.growth >= d.contributions);
    return crossover ? (
      <div style={{
        marginTop:10, padding:'10px 14px', borderRadius:8,
        background:`${T.teal}10`, border:`1px solid ${T.teal}25`,
        fontSize:12.5, color:T.muted, lineHeight:1.7,
      }}>
        📈 Tax-free growth overtakes your contributions in{' '}
        <span style={{ color:T.teal, fontWeight:700 }}>
          {crossover.year - new Date().getFullYear()} years
        </span>{' '}
        ({crossover.year}) — after that the market does more work than you do.
      </div>
    ) : null;
  })()}
</div>
    </div>
  );
}

/* 14. INFLATION CALCULATOR */
function InflationCalc({ sym }) {
  const [currentSalary,setCurrentSalary]= useState(75000);
  const [inflRate,     setInflRate]     = useState(3.5);
  const [years,        setYears]        = useState(3);

  const required    = currentSalary * Math.pow(1 + inflRate/100, years);
  const increase    = required - currentSalary;
  const pctIncrease = (increase / currentSalary) * 100;
const chartData = useMemo(() => {
  const maxYears = Math.max(years, 10);
  return Array.from({ length: maxYears + 1 }, (_, y) => {
    const required    = Math.round(currentSalary * Math.pow(1 + inflRate / 100, y));
    const realValue   = Math.round(currentSalary / Math.pow(1 + inflRate / 100, y));
    return {
      year:        new Date().getFullYear() + y,
      required,
      realValue,
      currentFlat: currentSalary,
    };
  });
}, [currentSalary, inflRate, years]);
  return (
    <div>
      <div className="ft-grid-2"style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <Field label="Current Salary"><Input value={currentSalary} onChange={v=>setCurrentSalary(+v)} prefix={sym}/></Field>
        <Field label={`Inflation Rate: ${inflRate}%`}><Slider value={inflRate} onChange={setInflRate} min={0.5} max={15} step={0.5}/></Field>
        <Field label={`Years: ${years}`}><Slider value={years} onChange={setYears} min={1} max={20}/></Field>
      </div>
      <ResultGrid>
        <ResultCard label={`Salary Needed in ${years} yrs`} value={fmt(required,sym)} color={T.orange} large/>
        <ResultCard label="Required Raise" value={fmt(increase,sym)} color={T.red}/>
        <ResultCard label="% Increase Needed" value={fmtPct(pctIncrease)} color={T.yellow}/>
        <ResultCard label="Monthly Difference" value={fmt(increase/12,sym)} color={T.blue}/>
      </ResultGrid>
      <div style={{ marginTop:24 }}>
  <div style={{
    fontSize:12, fontWeight:700, color:T.muted,
    textTransform:'uppercase', letterSpacing:'.08em', marginBottom:12,
  }}>
    Purchasing Power Over Time
  </div>

  <ResponsiveContainer width="100%" height={240}>
    <LineChart data={chartData} margin={{ top:4, right:16, left:8, bottom:4 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.06)" vertical={false}/>
      <XAxis
        dataKey="year"
        tick={{ fill:T.muted, fontSize:11 }}
        tickLine={false} axisLine={false}
        interval="preserveStartEnd"
      />
      <YAxis
        tick={{ fill:T.muted, fontSize:11 }}
        tickLine={false} axisLine={false}
        width={64}
        tickFormatter={n => shortNum(n, sym)}
      />
      <Tooltip
        contentStyle={{ background:T.bg2, border:`1px solid ${T.border}`, borderRadius:8, fontSize:12 }}
        labelStyle={{ color:T.muted, marginBottom:4 }}
        itemStyle={{ color:T.white }}
        formatter={(value, name) => [
          shortNum(value, sym),
          name === 'required'    ? 'Salary needed to keep up'    :
          name === 'realValue'   ? 'Real value of current salary' :
          'Current salary (flat)',
        ]}
        labelFormatter={year => `Year ${year}`}
      />
      <Legend
        wrapperStyle={{ fontSize:12, color:T.muted, paddingTop:8 }}
        formatter={name =>
          name === 'required'    ? 'Salary needed to keep up'    :
          name === 'realValue'   ? 'Real value of current salary' :
          'Current salary (flat)'
        }
      />

      {/* Target year marker */}
      <ReferenceLine
        x={new Date().getFullYear() + years}
        stroke={T.orange}
        strokeDasharray="4 3"
        strokeWidth={1.5}
        label={{
          value:`Year ${years}`,
          position:'top',
          fill:T.orange,
          fontSize:10,
          fontWeight:700,
        }}
      />

      {/* Current salary flat — what they earn now if no raises */}
      <Line
        type="monotone" dataKey="currentFlat"
        stroke={T.muted} strokeWidth={1.5}
        strokeDasharray="4 3" dot={false} activeDot={false}
      />
      {/* Real value of current salary — purchasing power erosion */}
      <Line
        type="monotone" dataKey="realValue"
        stroke={T.red} strokeWidth={2}
        dot={false} activeDot={{ r:4, fill:T.red }}
      />
      {/* Salary needed to maintain lifestyle */}
      <Line
        type="monotone" dataKey="required"
        stroke={T.orange} strokeWidth={2}
        dot={false} activeDot={{ r:4, fill:T.orange }}
      />
    </LineChart>
  </ResponsiveContainer>

  {/* Summary tiles */}
  <div style={{ marginTop:12, display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8 }}>
    {[
      {
        label:`Real value in ${years} yrs`,
        value:fmt(chartData[Math.min(years, chartData.length-1)]?.realValue ?? 0, sym),
        color:T.red,
        sub:'if salary stays flat',
      },
      {
        label:`Salary needed in ${years} yrs`,
        value:fmt(required, sym),
        color:T.orange,
        sub:'to maintain lifestyle',
      },
      {
        label:'Purchasing power lost',
        value:fmtPct(((currentSalary - (chartData[Math.min(years, chartData.length-1)]?.realValue ?? 0)) / currentSalary) * 100),
        color:T.yellow,
        sub:`over ${years} years`,
      },
    ].map(({ label, value, color, sub }) => (
      <div key={label} style={{
        padding:'10px 12px', borderRadius:8,
        background:`${color}10`, border:`1px solid ${color}25`,
        fontSize:11.5, textAlign:'center',
      }}>
        <div style={{ color:T.muted, marginBottom:3 }}>{label}</div>
        <div style={{ color, fontWeight:700, fontSize:13, fontFamily:T.mono }}>{value}</div>
        <div style={{ color:T.dim, fontSize:10.5, marginTop:2 }}>{sub}</div>
      </div>
    ))}
  </div>

  {/* Insight callout */}
  <div style={{
    marginTop:10, padding:'10px 14px', borderRadius:8,
    background:`${T.orange}10`, border:`1px solid ${T.orange}25`,
    fontSize:12.5, color:T.muted, lineHeight:1.7,
  }}>
    At {inflRate}% inflation, your{' '}
    <span style={{ color:T.white, fontWeight:700 }}>{sym}{currentSalary.toLocaleString()}</span>
    {' '}salary has the purchasing power of{' '}
    <span style={{ color:T.red, fontWeight:700 }}>
      {sym}{(chartData[Math.min(years, chartData.length-1)]?.realValue ?? 0).toLocaleString()}
    </span>
    {' '}in {years} years — you need a{' '}
    <span style={{ color:T.orange, fontWeight:700 }}>{inflRate}% annual raise</span>
    {' '}just to stay even.
  </div>
</div>
    </div>
  );
}

/* 15. UPWORK FEE CALCULATOR */
function UpworkCalc({ sym }) {
  const [rate,     setRate]     = useState(50);
  const [hours,    setHours]    = useState(40);
  const [lifetime, setLifetime] = useState(500);

const feeRate = lifetime < 500 ? 0.20 : 0.10;  const grossMonth = rate * hours;
  const fee        = grossMonth * feeRate;
  const net        = grossMonth - fee;
  const annualNet  = net * 12;
  const effectiveRate = net / hours;
// Sensitivity: net earnings at different hourly rates
const rateData = useMemo(() => {
  return Array.from({ length: 19 }, (_, i) => {
    const r       = 20 + i * 10;   // $20 to $200/hr
    const gross   = r * hours;
    const fee_    = gross * feeRate;
    const net_    = gross - fee_;
    return {
      rate:    r,
      gross:   Math.round(gross),
      net:     Math.round(net_),
      fee:     Math.round(fee_),
    };
  });
}, [hours, feeRate]);

// Lifetime billings → fee tier progression
const lifetimeData = useMemo(() => {
  return [0, 100, 250, 499, 500, 1000, 5000, 10000, 15000, 20000].map(lb => ({
    lifetime: lb,
    feeRate:  lb < 500 ? 20 : 10,
    netHourly:Math.round(rate * (1 - (lb < 500 ? 0.20 : 0.10)) * 100) / 100,
  }));
}, [rate]);
  return (
    <div>
      <div className="ft-grid-2"style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <Field label="Hourly Rate"><Input value={rate} onChange={v=>setRate(+v)} prefix={sym}/></Field>
        <Field label="Hours per Month"><Input value={hours} onChange={v=>setHours(+v)} suffix="hrs"/></Field>
        <Field label="Lifetime Billings with Client" help="affects fee tier">
          <Input value={lifetime} onChange={v=>setLifetime(+v)} prefix={sym}/>
        </Field>
      </div>
      <div style={{ padding:'10px 12px', background:T.surf, border:`1px solid ${T.border}`, borderRadius:8, fontSize:12, color:T.muted, marginBottom:4 }}>
        Upwork fee tier: <strong style={{ color:T.white }}>{(feeRate*100).toFixed(0)}%</strong>
        {lifetime < 500 && ' (less than $500 billed — 20%)'}
        {lifetime >= 500 && lifetime < 10000 && ' ($500–$10,000 billed — 10%)'}
        {lifetime >= 10000 && ' (over $10,000 billed — 10%)'}
      </div>
      <ResultGrid>
        <ResultCard label="Monthly Take-Home" value={fmt(net,sym)} color={T.green} large/>
        <ResultCard label="Upwork Fee" value={fmt(fee,sym)} color={T.red}/>
        <ResultCard label="Effective Hourly" value={fmt(effectiveRate,sym,2)} color={T.cyan}/>
        <ResultCard label="Annual Net" value={fmt(annualNet,sym)} color={T.yellow}/>
      </ResultGrid>
      <div style={{ marginTop:24 }}>

  {/* ── Net vs Gross at different rates ───────────────────── */}
  <div style={{
    fontSize:12, fontWeight:700, color:T.muted,
    textTransform:'uppercase', letterSpacing:'.08em', marginBottom:8,
  }}>
    Monthly Earnings by Hourly Rate ({hours} hrs/mo)
  </div>

  <ResponsiveContainer width="100%" height={220}>
    <LineChart data={rateData} margin={{ top:4, right:16, left:8, bottom:4 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.06)" vertical={false}/>
      <XAxis
        dataKey="rate"
        tick={{ fill:T.muted, fontSize:11 }}
        tickLine={false} axisLine={false}
        tickFormatter={v => `${sym}${v}`}
        interval="preserveStartEnd"
      />
      <YAxis
        tick={{ fill:T.muted, fontSize:11 }}
        tickLine={false} axisLine={false}
        width={60}
        tickFormatter={n => shortNum(n, sym)}
      />
      <Tooltip
        contentStyle={{ background:T.bg2, border:`1px solid ${T.border}`, borderRadius:8, fontSize:12 }}
        labelStyle={{ color:T.muted, marginBottom:4 }}
        itemStyle={{ color:T.white }}
        formatter={(value, name) => [
          shortNum(value, sym),
          name === 'gross' ? 'Gross monthly' :
          name === 'net'   ? 'Net take-home'  :
          'Upwork fee',
        ]}
        labelFormatter={v => `Rate: ${sym}${v}/hr`}
      />
      <Legend
        wrapperStyle={{ fontSize:12, color:T.muted, paddingTop:8 }}
        formatter={name =>
          name === 'gross' ? 'Gross monthly' :
          name === 'net'   ? 'Net take-home'  :
          'Upwork fee'
        }
      />
      <ReferenceLine
        x={rate}
        stroke={T.cyan}
        strokeDasharray="4 3"
        strokeWidth={1.5}
        label={{ value:'Your rate', position:'top', fill:T.cyan, fontSize:10, fontWeight:700 }}
      />
      <Line type="monotone" dataKey="gross" stroke={T.blue}  strokeWidth={1.5} strokeDasharray="6 4" dot={false} activeDot={false}/>
      <Line type="monotone" dataKey="fee"   stroke={T.red}   strokeWidth={1.5} strokeDasharray="3 3" dot={false} activeDot={false}/>
      <Line type="monotone" dataKey="net"   stroke={T.green} strokeWidth={2.5} dot={false} activeDot={{ r:4, fill:T.green }}/>
    </LineChart>
  </ResponsiveContainer>

  {/* ── Fee tier progression ───────────────────────────────── */}
  <div style={{
    fontSize:12, fontWeight:700, color:T.muted,
    textTransform:'uppercase', letterSpacing:'.08em',
    marginBottom:8, marginTop:24,
  }}>
    Fee Tier vs Lifetime Billings
  </div>
  <div style={{ fontSize:12, color:T.muted, marginBottom:10 }}>
    How your effective hourly rate improves as you bill more with a client.
  </div>

  <ResponsiveContainer width="100%" height={180}>
    <LineChart data={lifetimeData} margin={{ top:4, right:16, left:8, bottom:4 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.06)" vertical={false}/>
      <XAxis
        dataKey="lifetime"
        tick={{ fill:T.muted, fontSize:11 }}
        tickLine={false} axisLine={false}
        tickFormatter={v => v >= 1000 ? `${sym}${v/1000}K` : `${sym}${v}`}
        interval="preserveStartEnd"
      />
      <YAxis
        yAxisId="fee"
        tick={{ fill:T.muted, fontSize:11 }}
        tickLine={false} axisLine={false}
        width={36}
        tickFormatter={v => `${v}%`}
        domain={[0, 25]}
      />
      <YAxis
        yAxisId="hourly"
        orientation="right"
        tick={{ fill:T.muted, fontSize:11 }}
        tickLine={false} axisLine={false}
        width={48}
        tickFormatter={v => `${sym}${v}`}
      />
      <Tooltip
        contentStyle={{ background:T.bg2, border:`1px solid ${T.border}`, borderRadius:8, fontSize:12 }}
        labelStyle={{ color:T.muted, marginBottom:4 }}
        itemStyle={{ color:T.white }}
        formatter={(value, name) => [
          name === 'feeRate'    ? `${value}%`         :
          name === 'netHourly'  ? `${sym}${value}/hr`  : value,
          name === 'feeRate'    ? 'Upwork fee rate'    :
          'Your net hourly',
        ]}
        labelFormatter={v => `Lifetime billings: ${sym}${v.toLocaleString()}`}
      />
      <Legend
        wrapperStyle={{ fontSize:12, color:T.muted, paddingTop:8 }}
        formatter={name =>
          name === 'feeRate'   ? 'Fee rate (%)' :
          'Net hourly rate'
        }
      />
      <ReferenceLine
        yAxisId="fee"
        x={lifetime}
        stroke={T.cyan}
        strokeDasharray="4 3"
        strokeWidth={1.5}
        label={{ value:'You', position:'top', fill:T.cyan, fontSize:10, fontWeight:700 }}
      />
      <ReferenceLine
        yAxisId="fee"
        x={500}
        stroke={T.yellow}
        strokeDasharray="3 3"
        strokeWidth={1}
        label={{ value:'$500 tier', position:'insideTopRight', fill:T.yellow, fontSize:9 }}
      />
      <Line yAxisId="fee"    type="stepAfter" dataKey="feeRate"   stroke={T.red}   strokeWidth={2}   dot={false} activeDot={{ r:4, fill:T.red }}/>
      <Line yAxisId="hourly" type="stepAfter" dataKey="netHourly" stroke={T.green} strokeWidth={2}   dot={false} activeDot={{ r:4, fill:T.green }}/>
    </LineChart>
  </ResponsiveContainer>

  {/* Summary tiles */}
  <div style={{ marginTop:12, display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8 }}>
    {[
      { label:'Current fee tier', value:`${(feeRate*100).toFixed(0)}%`,  color:feeRate===0.10?T.green:T.red },
      { label:'Monthly fee paid', value:fmt(fee, sym),                    color:T.red   },
      { label:'Annual fee paid',  value:fmt(fee*12, sym),                 color:T.orange},
      { label:'To next tier',     value:lifetime < 500
          ? `${sym}${(500-lifetime).toLocaleString()} more`
          : '✅ Best rate',                                                color:lifetime<500?T.yellow:T.green },
    ].map(({ label, value, color }) => (
      <div key={label} style={{
        padding:'9px 10px', borderRadius:8,
        background:`${color}10`, border:`1px solid ${color}25`,
        fontSize:11.5, textAlign:'center',
      }}>
        <div style={{ color:T.muted, marginBottom:3 }}>{label}</div>
        <div style={{ color, fontWeight:700, fontSize:12.5, fontFamily:T.mono }}>{value}</div>
      </div>
    ))}
  </div>

  {/* Insight callout */}
  {lifetime < 500 && (
    <div style={{
      marginTop:10, padding:'10px 14px', borderRadius:8,
      background:`${T.yellow}10`, border:`1px solid ${T.yellow}25`,
      fontSize:12.5, color:T.muted, lineHeight:1.7,
    }}>
      💡 Bill just{' '}
      <span style={{ color:T.yellow, fontWeight:700 }}>
        {sym}{(500 - lifetime).toLocaleString()} more
      </span>
      {' '}with this client to drop from 20% to 10% — saving{' '}
      <span style={{ color:T.green, fontWeight:700 }}>
        {sym}{Math.round(grossMonth * 0.10)}/mo
      </span>
      {' '}going forward.
    </div>
  )}
</div>
    </div>
  );
}

/* 16. RENTAL PROPERTY */
function RentalCalc({ sym }) {
  const [price,        setPrice]        = useState(300000);
  const [downPct,      setDownPct]      = useState(20);
  const [rate,         setRate]         = useState(6.5);
  const [rent,         setRent]         = useState(2000);
  const [vacancy,      setVacancy]      = useState(5);
  const [expenses,     setExpenses]     = useState(800);
  const [appreciation, setAppreciation] = useState(3);

  const down          = price * downPct / 100;
  const loan          = price - down;
  const r             = rate/100/12, n = 30*12;
  const mortgage      = r>0 ? loan*(r*Math.pow(1+r,n))/(Math.pow(1+r,n)-1) : loan/n;
  const effRent       = rent * (1 - vacancy/100);
  const noi           = (effRent - expenses) * 12;
  const cashFlow      = effRent - expenses - mortgage;
  const cashOnCash    = down > 0 ? (cashFlow * 12 / down) * 100 : 0;
  const capRate       = price > 0 ? (noi / price) * 100 : 0;
const chartData = useMemo(() => {
  return Array.from({ length: 31 }, (_, y) => {
    const propertyValue   = Math.round(price * Math.pow(1 + appreciation / 100, y));
    const cumulativeCF    = Math.round(cashFlow * 12 * y);
    const r_              = rate / 100 / 12;
    const n_              = 30 * 12;
    const m               = y * 12;
    const loanBalance     = r_ > 0
      ? loan * Math.pow(1 + r_, m) - mortgage * (Math.pow(1 + r_, m) - 1) / r_
      : Math.max(0, loan - mortgage * m);
    const equity          = Math.round(propertyValue - Math.max(0, loanBalance));
    const totalReturn     = Math.round(cumulativeCF + equity - down);

    return {
      year:         new Date().getFullYear() + y,
      propertyValue,
      equity,
      cumulativeCF,
      totalReturn,
    };
  });
}, [price, appreciation, cashFlow, rate, loan, mortgage, down]);
  return (
    <div>
      <div className="ft-grid-2"style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <Field label="Purchase Price"><Input value={price} onChange={v=>setPrice(+v)} prefix={sym}/></Field>
        <Field label={`Down Payment: ${downPct}%`}><Slider value={downPct} onChange={setDownPct} min={5} max={100}/></Field>
        <Field label={`Mortgage Rate: ${rate}%`}><Slider value={rate} onChange={setRate} min={1} max={15} step={0.25}/></Field>
        <Field label="Monthly Rent"><Input value={rent} onChange={v=>setRent(+v)} prefix={sym}/></Field>
        <Field label="Monthly Expenses (taxes, insurance, maintenance)"><Input value={expenses} onChange={v=>setExpenses(+v)} prefix={sym}/></Field>
        <Field label={`Vacancy Rate: ${vacancy}%`}><Slider value={vacancy} onChange={setVacancy} min={0} max={20}/></Field>
      </div>
      <ResultGrid>
        <ResultCard label="Monthly Cash Flow" value={fmt(cashFlow,sym,2)} color={cashFlow>=0?T.green:T.red} large/>
        <ResultCard label="Cash-on-Cash Return" value={fmtPct(cashOnCash)} color={T.cyan}/>
        <ResultCard label="Cap Rate" value={fmtPct(capRate)} color={T.blue}/>
        <ResultCard label="Down Payment" value={fmt(down,sym)} color={T.yellow}/>
      </ResultGrid>
      <div style={{ marginTop:24 }}>
  <div style={{
    fontSize:12, fontWeight:700, color:T.muted,
    textTransform:'uppercase', letterSpacing:'.08em', marginBottom:12,
  }}>
    30-Year Rental Investment Projection
  </div>

  {cashFlow < 0 && (
    <div style={{
      padding:'10px 14px', borderRadius:8, marginBottom:12,
      background:`${T.red}12`, border:`1px solid ${T.red}30`,
      fontSize:12.5, color:T.red,
    }}>
      ⚠ Negative cash flow — you'll need to top up {fmt(Math.abs(cashFlow), sym, 2)}/mo
      out of pocket. Property may still appreciate.
    </div>
  )}

  <ResponsiveContainer width="100%" height={280}>
    <LineChart data={chartData} margin={{ top:4, right:16, left:8, bottom:4 }}>
      <CartesianGrid
        strokeDasharray="3 3"
        stroke="rgba(255,255,255,.06)"
        vertical={false}
      />
      <XAxis
        dataKey="year"
        tick={{ fill:T.muted, fontSize:11 }}
        tickLine={false}
        axisLine={false}
        interval="preserveStartEnd"
      />
      <YAxis
        tick={{ fill:T.muted, fontSize:11 }}
        tickLine={false}
        axisLine={false}
        width={64}
        tickFormatter={n => shortNum(n, sym)}
      />
      <Tooltip
        contentStyle={{
          background:T.bg2, border:`1px solid ${T.border}`,
          borderRadius:8, fontSize:12,
        }}
        labelStyle={{ color:T.muted, marginBottom:4 }}
        itemStyle={{ color:T.white }}
        formatter={(value, name) => [
          shortNum(value, sym),
          name === 'propertyValue' ? 'Property value'      :
          name === 'equity'        ? 'Your equity'          :
          name === 'cumulativeCF'  ? 'Cumulative cash flow' :
          'Total return (equity + CF − down)',
        ]}
        labelFormatter={year => `Year ${year}`}
      />
      <Legend
        wrapperStyle={{ fontSize:12, color:T.muted, paddingTop:8 }}
        formatter={name =>
          name === 'propertyValue' ? 'Property value'      :
          name === 'equity'        ? 'Your equity'          :
          name === 'cumulativeCF'  ? 'Cumulative cash flow' :
          'Total return'
        }
      />

      {/* Break-even on cash flow */}
      <ReferenceLine
        y={0}
        stroke="rgba(255,255,255,.15)"
        strokeWidth={1}
      />

      {/* Property value */}
      <Line
        type="monotone"
        dataKey="propertyValue"
        stroke={T.blue}
        strokeWidth={1.5}
        strokeDasharray="6 4"
        dot={false}
        activeDot={false}
      />
      {/* Equity */}
      <Line
        type="monotone"
        dataKey="equity"
        stroke={T.cyan}
        strokeWidth={2}
        dot={false}
        activeDot={{ r:4, fill:T.cyan }}
      />
      {/* Cumulative cash flow */}
      <Line
        type="monotone"
        dataKey="cumulativeCF"
        stroke={cashFlow >= 0 ? T.green : T.red}
        strokeWidth={2}
        dot={false}
        activeDot={{ r:4, fill:cashFlow >= 0 ? T.green : T.red }}
      />
      {/* Total return */}
      <Line
        type="monotone"
        dataKey="totalReturn"
        stroke={T.yellow}
        strokeWidth={2}
        dot={false}
        activeDot={{ r:4, fill:T.yellow }}
      />
    </LineChart>
  </ResponsiveContainer>

  {/* 30-year summary tiles */}
  <div style={{ marginTop:12, display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8 }}>
    {(() => {
      const last = chartData[chartData.length - 1];
      return [
        { label:'Property value',  value:fmt(last.propertyValue, sym), color:T.blue  },
        { label:'Your equity',     value:fmt(last.equity, sym),        color:T.cyan  },
        { label:'Total cash flow', value:fmt(last.cumulativeCF, sym),  color:cashFlow >= 0 ? T.green : T.red },
        { label:'Total return',    value:fmt(last.totalReturn, sym),   color:T.yellow},
      ];
    })().map(({ label, value, color }) => (
      <div key={label} style={{
        padding:'10px 12px', borderRadius:8,
        background:`${color}10`, border:`1px solid ${color}25`,
        fontSize:11.5, textAlign:'center',
      }}>
        <div style={{ color:T.muted, marginBottom:3 }}>{label}</div>
        <div style={{ color, fontWeight:700, fontSize:13, fontFamily:T.mono }}>{value}</div>
      </div>
    ))}
  </div>
</div>
    </div>
  );
}

/* 17. HOUSE HACKING */
function HouseHackCalc({ sym }) {
  const [price,    setPrice]    = useState(400000);
  const [downPct,  setDownPct]  = useState(5);
  const [rate,     setRate]     = useState(6.5);
  const [rent,     setRent]     = useState(1500);
  const [expenses, setExpenses] = useState(500);

  const down     = price * downPct / 100;
  const loan     = price - down;
  const r        = rate/100/12, n = 30*12;
  const mortgage = r>0 ? loan*(r*Math.pow(1+r,n))/(Math.pow(1+r,n)-1) : loan/n;
  const netCost  = mortgage + expenses - rent;
  const saving   = Math.max(0, (price*0.01) - netCost);


  const marketRent     = price * 0.01;  // 1% rule estimate for comparable rent
const annualAppreciation = 3;         // standard 3% assumption

const chartData = useMemo(() => {
  return Array.from({ length: 31 }, (_, y) => {
    const r_          = rate / 100 / 12;
    const n_          = 30 * 12;
    const m           = y * 12;
    const loanBalance = r_ > 0
      ? loan * Math.pow(1 + r_, m) - mortgage * (Math.pow(1 + r_, m) - 1) / r_
      : Math.max(0, loan - mortgage * m);
    const propValue   = Math.round(price * Math.pow(1 + annualAppreciation / 100, y));
    const equity      = Math.round(propValue - Math.max(0, loanBalance));
    const cumHackCost = Math.round(Math.max(0, netCost) * 12 * y);
    const cumRentCost = Math.round(marketRent * 12 * y * Math.pow(1.03, y)); // rent inflates 3%/yr

    return {
      year: new Date().getFullYear() + y,
      equity,
      propValue,
      cumHackCost,
      cumRentCost,
    };
  });
}, [price, rate, loan, mortgage, netCost, rent]);

  return (
    <div>
      <div className="ft-grid-2"style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <Field label="Purchase Price"><Input value={price} onChange={v=>setPrice(+v)} prefix={sym}/></Field>
        <Field label={`Down Payment: ${downPct}%`}><Slider value={downPct} onChange={setDownPct} min={3} max={25}/></Field>
        <Field label={`Mortgage Rate: ${rate}%`}><Slider value={rate} onChange={setRate} min={1} max={15} step={0.25}/></Field>
        <Field label="Monthly Rental Income (from tenants)"><Input value={rent} onChange={v=>setRent(+v)} prefix={sym}/></Field>
        <Field label="Monthly Expenses (tax, insurance, etc.)"><Input value={expenses} onChange={v=>setExpenses(+v)} prefix={sym}/></Field>
      </div>
      <ResultGrid>
        <ResultCard label="Your Monthly Housing Cost" value={fmt(netCost,sym,2)} color={netCost<=0?T.green:T.orange} large/>
        <ResultCard label="Mortgage Payment" value={fmt(mortgage,sym,2)} color={T.blue}/>
        <ResultCard label="Tenant Covers" value={fmt(rent,sym)} color={T.green}/>
        <ResultCard label="Down Payment" value={fmt(down,sym)} color={T.yellow}/>
      </ResultGrid>
      <div style={{ marginTop:24 }}>
  <div style={{
    fontSize:12, fontWeight:700, color:T.muted,
    textTransform:'uppercase', letterSpacing:'.08em', marginBottom:12,
  }}>
    House Hack vs Renting — 30-Year Comparison
  </div>

  {netCost <= 0 && (
    <div style={{
      padding:'10px 14px', borderRadius:8, marginBottom:12,
      background:`${T.green}12`, border:`1px solid ${T.green}30`,
      fontSize:12.5, color:T.green,
    }}>
      ✅ Tenants cover your full mortgage + expenses — you live for free and build equity!
    </div>
  )}

  {netCost > mortgage && (
    <div style={{
      padding:'10px 14px', borderRadius:8, marginBottom:12,
      background:`${T.orange}12`, border:`1px solid ${T.orange}30`,
      fontSize:12.5, color:T.orange,
    }}>
      ⚠ Rental income doesn't offset expenses much — consider higher rent or lower expenses.
    </div>
  )}

  <ResponsiveContainer width="100%" height={280}>
    <LineChart data={chartData} margin={{ top:4, right:16, left:8, bottom:4 }}>
      <CartesianGrid
        strokeDasharray="3 3"
        stroke="rgba(255,255,255,.06)"
        vertical={false}
      />
      <XAxis
        dataKey="year"
        tick={{ fill:T.muted, fontSize:11 }}
        tickLine={false}
        axisLine={false}
        interval="preserveStartEnd"
      />
      <YAxis
        tick={{ fill:T.muted, fontSize:11 }}
        tickLine={false}
        axisLine={false}
        width={64}
        tickFormatter={n => shortNum(n, sym)}
      />
      <Tooltip
        contentStyle={{
          background:T.bg2, border:`1px solid ${T.border}`,
          borderRadius:8, fontSize:12,
        }}
        labelStyle={{ color:T.muted, marginBottom:4 }}
        itemStyle={{ color:T.white }}
        formatter={(value, name) => [
          shortNum(value, sym),
          name === 'equity'      ? 'Your equity'            :
          name === 'propValue'   ? 'Property value'          :
          name === 'cumHackCost' ? 'Total housing cost (hack)':
          'Total rent paid (if renting)',
        ]}
        labelFormatter={year => `Year ${year}`}
      />
      <Legend
        wrapperStyle={{ fontSize:12, color:T.muted, paddingTop:8 }}
        formatter={name =>
          name === 'equity'      ? 'Your equity'             :
          name === 'propValue'   ? 'Property value'           :
          name === 'cumHackCost' ? 'Total housing cost (hack)' :
          'Total rent paid (if renting)'
        }
      />

      {/* Property value */}
      <Line
        type="monotone"
        dataKey="propValue"
        stroke={T.blue}
        strokeWidth={1.5}
        strokeDasharray="6 4"
        dot={false}
        activeDot={false}
      />
      {/* Equity built */}
      <Line
        type="monotone"
        dataKey="equity"
        stroke={T.cyan}
        strokeWidth={2}
        dot={false}
        activeDot={{ r:4, fill:T.cyan }}
      />
      {/* Cumulative house hack housing cost */}
      <Line
        type="monotone"
        dataKey="cumHackCost"
        stroke={T.green}
        strokeWidth={2}
        dot={false}
        activeDot={{ r:4, fill:T.green }}
      />
      {/* Cumulative rent if they had rented instead */}
      <Line
        type="monotone"
        dataKey="cumRentCost"
        stroke={T.red}
        strokeWidth={2}
        strokeDasharray="4 3"
        dot={false}
        activeDot={false}
      />
    </LineChart>
  </ResponsiveContainer>

  {/* 30-year summary */}
  <div style={{ marginTop:12, display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8 }}>
    {(() => {
      const last    = chartData[chartData.length - 1];
      const rentGap = last.cumRentCost - last.cumHackCost;
      return [
        { label:'Property value',   value:fmt(last.propValue, sym),   color:T.blue  },
        { label:'Equity built',     value:fmt(last.equity, sym),      color:T.cyan  },
        { label:'Your housing cost',value:fmt(last.cumHackCost, sym), color:T.green },
        { label:'Saved vs renting', value:fmt(Math.max(0, rentGap + last.equity - down), sym), color:T.yellow },
      ];
    })().map(({ label, value, color }) => (
      <div key={label} style={{
        padding:'10px 12px', borderRadius:8,
        background:`${color}10`, border:`1px solid ${color}25`,
        fontSize:11.5, textAlign:'center',
      }}>
        <div style={{ color:T.muted, marginBottom:3 }}>{label}</div>
        <div style={{ color, fontWeight:700, fontSize:13, fontFamily:T.mono }}>{value}</div>
      </div>
    ))}
  </div>
</div>
    </div>
  );
}

/* 18. CAP RATE */
function CapRateCalc({ sym }) {
  const [price,   setPrice]   = useState(500000);
  const [rent,    setRent]    = useState(3000);
  const [vacancy, setVacancy] = useState(5);
  const [expenses,setExpenses]= useState(800);

  const grossIncome = rent * 12 * (1 - vacancy/100);
  const noi         = grossIncome - expenses * 12;
  const capRate     = price > 0 ? (noi/price)*100 : 0;
  const grm         = rent > 0 ? price / (rent*12) : 0;
const chartData = useMemo(() => {
  const minPrice = Math.round(price * 0.5);
  const maxPrice = Math.round(price * 1.5);
  const step     = Math.round((maxPrice - minPrice) / 20);
  return Array.from({ length: 21 }, (_, i) => {
    const p         = minPrice + i * step;
    const gi        = rent * 12 * (1 - vacancy / 100);
    const n         = gi - expenses * 12;
    const cr        = p > 0 ? (n / p) * 100 : 0;
    return {
      price: Math.round(p / 1000),   // show as $K for cleaner axis
      capRate: Math.round(cr * 100) / 100,
      target6: 6,   // good cap rate benchmark
      target4: 4,   // minimum acceptable benchmark
    };
  });
}, [price, rent, vacancy, expenses]);
  return (
    <div>
      <div className="ft-grid-2"style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <Field label="Property Value"><Input value={price} onChange={v=>setPrice(+v)} prefix={sym}/></Field>
        <Field label="Monthly Rent"><Input value={rent} onChange={v=>setRent(+v)} prefix={sym}/></Field>
        <Field label={`Vacancy Rate: ${vacancy}%`}><Slider value={vacancy} onChange={setVacancy} min={0} max={20}/></Field>
        <Field label="Monthly Operating Expenses"><Input value={expenses} onChange={v=>setExpenses(+v)} prefix={sym}/></Field>
      </div>
      <ResultGrid>
        <ResultCard label="Cap Rate" value={fmtPct(capRate,2)} color={capRate>=6?T.green:capRate>=4?T.yellow:T.red} large/>
        <ResultCard label="NOI (annual)" value={fmt(noi,sym)} color={T.cyan}/>
        <ResultCard label="Gross Rent Multiplier" value={grm.toFixed(1)+'x'} color={T.blue}/>
        <ResultCard label="Gross Income" value={fmt(grossIncome,sym)} color={T.yellow}/>
      </ResultGrid>

      <div style={{ marginTop:24 }}>
  <div style={{
    fontSize:12, fontWeight:700, color:T.muted,
    textTransform:'uppercase', letterSpacing:'.08em', marginBottom:12,
  }}>
    Cap Rate Sensitivity — Price vs Cap Rate
  </div>

  <div style={{ fontSize:12, color:T.muted, marginBottom:10 }}>
    How cap rate changes as property price varies ±50% from your current value.
    Your current price ({sym}{(price/1000).toFixed(0)}K) is highlighted.
  </div>

  {capRate < 4 && (
    <div style={{
      padding:'10px 14px', borderRadius:8, marginBottom:12,
      background:`${T.red}12`, border:`1px solid ${T.red}30`,
      fontSize:12.5, color:T.red,
    }}>
      ⚠ Cap rate below 4% — generally considered a weak investment return.
      Price is too high relative to NOI.
    </div>
  )}
  {capRate >= 4 && capRate < 6 && (
    <div style={{
      padding:'10px 14px', borderRadius:8, marginBottom:12,
      background:`${T.yellow}10`, border:`1px solid ${T.yellow}30`,
      fontSize:12.5, color:T.yellow,
    }}>
      ⚡ Cap rate between 4–6% — acceptable in high-demand markets,
      but limited upside. Target 6%+ for stronger returns.
    </div>
  )}
  {capRate >= 6 && (
    <div style={{
      padding:'10px 14px', borderRadius:8, marginBottom:12,
      background:`${T.green}10`, border:`1px solid ${T.green}30`,
      fontSize:12.5, color:T.green,
    }}>
      ✅ Cap rate above 6% — strong return. This property generates
      good income relative to its price.
    </div>
  )}

  <ResponsiveContainer width="100%" height={260}>
    <LineChart data={chartData} margin={{ top:4, right:16, left:8, bottom:4 }}>
      <CartesianGrid
        strokeDasharray="3 3"
        stroke="rgba(255,255,255,.06)"
        vertical={false}
      />
      <XAxis
        dataKey="price"
        tick={{ fill:T.muted, fontSize:11 }}
        tickLine={false}
        axisLine={false}
        interval="preserveStartEnd"
        tickFormatter={v => `${sym}${v}K`}
      />
      <YAxis
        tick={{ fill:T.muted, fontSize:11 }}
        tickLine={false}
        axisLine={false}
        width={40}
        tickFormatter={v => `${v}%`}
        domain={[0, 'auto']}
      />
      <Tooltip
        contentStyle={{
          background:T.bg2, border:`1px solid ${T.border}`,
          borderRadius:8, fontSize:12,
        }}
        labelStyle={{ color:T.muted, marginBottom:4 }}
        itemStyle={{ color:T.white }}
        formatter={(value, name) => [
          `${value}%`,
          name === 'capRate' ? 'Cap rate'           :
          name === 'target6' ? 'Good (6% target)'   :
          'Minimum (4% target)',
        ]}
        labelFormatter={v => `Price: ${sym}${v}K`}
      />
      <Legend
        wrapperStyle={{ fontSize:12, color:T.muted, paddingTop:8 }}
        formatter={name =>
          name === 'capRate' ? 'Cap rate'           :
          name === 'target6' ? 'Good (6% target)'   :
          'Minimum (4% target)'
        }
      />

      {/* Current price marker */}
      <ReferenceLine
        x={Math.round(price / 1000)}
        stroke={T.cyan}
        strokeDasharray="4 3"
        strokeWidth={1.5}
        label={{
          value: 'Current',
          position: 'top',
          fill: T.cyan,
          fontSize: 10,
          fontWeight: 700,
        }}
      />

      {/* 6% benchmark */}
      <Line
        type="monotone"
        dataKey="target6"
        stroke={T.green}
        strokeWidth={1}
        strokeDasharray="6 4"
        dot={false}
        activeDot={false}
      />
      {/* 4% benchmark */}
      <Line
        type="monotone"
        dataKey="target4"
        stroke={T.yellow}
        strokeWidth={1}
        strokeDasharray="6 4"
        dot={false}
        activeDot={false}
      />
      {/* Cap rate curve */}
      <Line
        type="monotone"
        dataKey="capRate"
        stroke={capRate >= 6 ? T.green : capRate >= 4 ? T.yellow : T.red}
        strokeWidth={2.5}
        dot={false}
        activeDot={{ r:4, fill: capRate >= 6 ? T.green : capRate >= 4 ? T.yellow : T.red }}
      />
    </LineChart>
  </ResponsiveContainer>

  {/* Breakeven summary */}
  <div style={{ marginTop:12, display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8 }}>
    {(() => {
      // Find price where cap rate = 6% and = 4%
      const breakeven6 = noi > 0 ? Math.round(noi / 0.06) : null;
      const breakeven4 = noi > 0 ? Math.round(noi / 0.04) : null;
      return [
        { label:'Max price for 6% cap', value: breakeven6 ? fmt(breakeven6, sym) : '—', color:T.green  },
        { label:'Max price for 4% cap', value: breakeven4 ? fmt(breakeven4, sym) : '—', color:T.yellow },
        { label:'Annual NOI',           value: fmt(noi, sym),                            color:T.cyan   },
      ];
    })().map(({ label, value, color }) => (
      <div key={label} style={{
        padding:'10px 12px', borderRadius:8,
        background:`${color}10`, border:`1px solid ${color}25`,
        fontSize:11.5, textAlign:'center',
      }}>
        <div style={{ color:T.muted, marginBottom:3 }}>{label}</div>
        <div style={{ color, fontWeight:700, fontSize:13, fontFamily:T.mono }}>{value}</div>
      </div>
    ))}
  </div>
</div>
    </div>
  );
}

/* 19. CASH-ON-CASH RETURN */
function CashOnCashCalc({ sym }) {
  const [cashInvested,setCashInvested]= useState(80000);
  const [annualCashFlow,setAnnualCashFlow]= useState(6000);
  const [mortgage,setMortgage]= useState(1800);
  const [rent,setRent]= useState(2500);
  const [expenses,setExpenses]= useState(500);

  const netCashFlow = (rent - expenses - mortgage) * 12;
  const cocReturn   = cashInvested > 0 ? (netCashFlow / cashInvested) * 100 : 0;
// Sensitivity: cap rate across a range of property prices
  // Sensitivity: CoC return as cash invested varies ±50%
const sensitivityData = useMemo(() => {
  const minCash = Math.round(cashInvested * 0.5);
  const maxCash = Math.round(cashInvested * 1.5);
  const step    = Math.round((maxCash - minCash) / 20);
  return Array.from({ length: 21 }, (_, i) => {
    const cash = minCash + i * step;
    const coc  = cash > 0 ? (netCashFlow / cash) * 100 : 0;
    return {
      cash:    Math.round(cash / 1000),
      cocReturn: Math.round(coc * 100) / 100,
      target8: 8,
      target4: 4,
    };
  });
}, [cashInvested, netCashFlow]);

// Projection: cumulative cash flow over 10 years
const projectionData = useMemo(() => {
  return Array.from({ length: 11 }, (_, y) => ({
    year:       y,
    cumFlow:    Math.round(netCashFlow * y),
    breakeven:  cashInvested,
  }));
}, [netCashFlow, cashInvested]);

const breakevenYears = netCashFlow > 0 ? cashInvested / netCashFlow : null;
  return (
    <div>
      <div className="ft-grid-2"style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <Field label="Total Cash Invested (down + closing)"><Input value={cashInvested} onChange={v=>setCashInvested(+v)} prefix={sym}/></Field>
        <Field label="Monthly Rent"><Input value={rent} onChange={v=>setRent(+v)} prefix={sym}/></Field>
        <Field label="Monthly Mortgage"><Input value={mortgage} onChange={v=>setMortgage(+v)} prefix={sym}/></Field>
        <Field label="Monthly Expenses"><Input value={expenses} onChange={v=>setExpenses(+v)} prefix={sym}/></Field>
      </div>
      <ResultGrid>
        <ResultCard label="Cash-on-Cash Return" value={fmtPct(cocReturn,2)} color={cocReturn>=8?T.green:cocReturn>=4?T.yellow:T.red} large/>
        <ResultCard label="Annual Net Cash Flow" value={fmt(netCashFlow,sym)} color={T.cyan}/>
        <ResultCard label="Monthly Net" value={fmt(netCashFlow/12,sym,2)} color={T.blue}/>
        <ResultCard label="Cash Invested" value={fmt(cashInvested,sym)} color={T.yellow}/>
      </ResultGrid>
      <div style={{ marginTop:24 }}>

  {/* ── Sensitivity chart ─────────────────────────────────── */}
  <div style={{
    fontSize:12, fontWeight:700, color:T.muted,
    textTransform:'uppercase', letterSpacing:'.08em', marginBottom:8,
  }}>
    CoC Return vs Cash Invested (Sensitivity)
  </div>
  <div style={{ fontSize:12, color:T.muted, marginBottom:10 }}>
    How your return changes as total cash invested varies ±50%.
  </div>

  {cocReturn < 4 && (
    <div style={{
      padding:'10px 14px', borderRadius:8, marginBottom:12,
      background:`${T.red}12`, border:`1px solid ${T.red}30`,
      fontSize:12.5, color:T.red,
    }}>
      ⚠ CoC return below 4% — weak return on invested cash.
      Consider reducing down payment or finding a higher-rent property.
    </div>
  )}
  {cocReturn >= 4 && cocReturn < 8 && (
    <div style={{
      padding:'10px 14px', borderRadius:8, marginBottom:12,
      background:`${T.yellow}10`, border:`1px solid ${T.yellow}30`,
      fontSize:12.5, color:T.yellow,
    }}>
      ⚡ CoC return between 4–8% — acceptable but not exceptional.
      Target 8%+ for strong cash-on-cash performance.
    </div>
  )}
  {cocReturn >= 8 && (
    <div style={{
      padding:'10px 14px', borderRadius:8, marginBottom:12,
      background:`${T.green}10`, border:`1px solid ${T.green}30`,
      fontSize:12.5, color:T.green,
    }}>
      ✅ CoC return above 8% — strong cash-on-cash performance.
    </div>
  )}

  <ResponsiveContainer width="100%" height={220}>
    <LineChart data={sensitivityData} margin={{ top:4, right:16, left:8, bottom:4 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.06)" vertical={false}/>
      <XAxis
        dataKey="cash"
        tick={{ fill:T.muted, fontSize:11 }}
        tickLine={false} axisLine={false}
        interval="preserveStartEnd"
        tickFormatter={v => `${sym}${v}K`}
      />
      <YAxis
        tick={{ fill:T.muted, fontSize:11 }}
        tickLine={false} axisLine={false}
        width={40}
        tickFormatter={v => `${v}%`}
        domain={[0, 'auto']}
      />
      <Tooltip
        contentStyle={{ background:T.bg2, border:`1px solid ${T.border}`, borderRadius:8, fontSize:12 }}
        labelStyle={{ color:T.muted, marginBottom:4 }}
        itemStyle={{ color:T.white }}
        formatter={(value, name) => [
          `${value}%`,
          name === 'cocReturn' ? 'CoC return'          :
          name === 'target8'   ? 'Strong (8% target)'  :
          'Minimum (4% target)',
        ]}
        labelFormatter={v => `Cash invested: ${sym}${v}K`}
      />
      <Legend
        wrapperStyle={{ fontSize:12, color:T.muted, paddingTop:8 }}
        formatter={name =>
          name === 'cocReturn' ? 'CoC return'          :
          name === 'target8'   ? 'Strong (8% target)'  :
          'Minimum (4% target)'
        }
      />
      <ReferenceLine
        x={Math.round(cashInvested / 1000)}
        stroke={T.cyan}
        strokeDasharray="4 3"
        strokeWidth={1.5}
        label={{ value:'Current', position:'top', fill:T.cyan, fontSize:10, fontWeight:700 }}
      />
      <Line type="monotone" dataKey="target8"   stroke={T.green}  strokeWidth={1} strokeDasharray="6 4" dot={false} activeDot={false}/>
      <Line type="monotone" dataKey="target4"   stroke={T.yellow} strokeWidth={1} strokeDasharray="6 4" dot={false} activeDot={false}/>
      <Line
        type="monotone" dataKey="cocReturn"
        stroke={cocReturn >= 8 ? T.green : cocReturn >= 4 ? T.yellow : T.red}
        strokeWidth={2.5} dot={false}
        activeDot={{ r:4, fill: cocReturn >= 8 ? T.green : cocReturn >= 4 ? T.yellow : T.red }}
      />
    </LineChart>
  </ResponsiveContainer>

  {/* ── Cumulative cash flow projection ───────────────────── */}
  <div style={{
    fontSize:12, fontWeight:700, color:T.muted,
    textTransform:'uppercase', letterSpacing:'.08em',
    marginBottom:8, marginTop:24,
  }}>
    Cumulative Cash Flow — Breakeven Projection
  </div>

  <ResponsiveContainer width="100%" height={200}>
    <LineChart data={projectionData} margin={{ top:4, right:16, left:8, bottom:4 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.06)" vertical={false}/>
      <XAxis
        dataKey="year"
        tick={{ fill:T.muted, fontSize:11 }}
        tickLine={false} axisLine={false}
        tickFormatter={v => `Yr ${v}`}
      />
      <YAxis
        tick={{ fill:T.muted, fontSize:11 }}
        tickLine={false} axisLine={false}
        width={60}
        tickFormatter={n => shortNum(n, sym)}
      />
      <Tooltip
        contentStyle={{ background:T.bg2, border:`1px solid ${T.border}`, borderRadius:8, fontSize:12 }}
        labelStyle={{ color:T.muted, marginBottom:4 }}
        itemStyle={{ color:T.white }}
        formatter={(value, name) => [
          shortNum(value, sym),
          name === 'cumFlow'  ? 'Cumulative cash flow' : 'Cash invested',
        ]}
        labelFormatter={y => `Year ${y}`}
      />
      <Legend
        wrapperStyle={{ fontSize:12, color:T.muted, paddingTop:8 }}
        formatter={name => name === 'cumFlow' ? 'Cumulative cash flow' : 'Cash invested (breakeven line)'}
      />

      {/* Breakeven marker */}
      {breakevenYears !== null && breakevenYears <= 10 && (
        <ReferenceLine
          x={Math.ceil(breakevenYears)}
          stroke={T.green}
          strokeDasharray="4 3"
          strokeWidth={1.5}
          label={{
            value:`Breakeven yr ${Math.ceil(breakevenYears)}`,
            position:'top',
            fill:T.green,
            fontSize:10,
            fontWeight:700,
          }}
        />
      )}

      {/* Cash invested horizontal — breakeven line */}
      <Line
        type="monotone" dataKey="breakeven"
        stroke={T.yellow} strokeWidth={1.5}
        strokeDasharray="6 4" dot={false} activeDot={false}
      />
      {/* Cumulative cash flow */}
      <Line
        type="monotone" dataKey="cumFlow"
        stroke={netCashFlow >= 0 ? T.cyan : T.red}
        strokeWidth={2} dot={false}
        activeDot={{ r:4, fill: netCashFlow >= 0 ? T.cyan : T.red }}
      />
    </LineChart>
  </ResponsiveContainer>

  {/* Summary tiles */}
  <div style={{ marginTop:12, display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8 }}>
    {(() => {
      const maxCash6 = netCashFlow > 0 ? Math.round(netCashFlow / 0.08) : null;
      const maxCash4 = netCashFlow > 0 ? Math.round(netCashFlow / 0.04) : null;
      return [
        { label:'Max invest for 8% CoC', value: maxCash6 ? fmt(maxCash6, sym) : '—',                          color:T.green  },
        { label:'Max invest for 4% CoC', value: maxCash4 ? fmt(maxCash4, sym) : '—',                          color:T.yellow },
        { label:'Breakeven',             value: breakevenYears ? `${breakevenYears.toFixed(1)} yrs` : '—',    color:T.cyan   },
      ];
    })().map(({ label, value, color }) => (
      <div key={label} style={{
        padding:'10px 12px', borderRadius:8,
        background:`${color}10`, border:`1px solid ${color}25`,
        fontSize:11.5, textAlign:'center',
      }}>
        <div style={{ color:T.muted, marginBottom:3 }}>{label}</div>
        <div style={{ color, fontWeight:700, fontSize:13, fontFamily:T.mono }}>{value}</div>
      </div>
    ))}
  </div>
</div>
    </div>
  );
}

/* 20. EV SAVINGS */
function EVSavingsCalc({ sym }) {
  const [gasMPG,    setGasMPG]    = useState(28);
  const [milesYear, setMilesYear] = useState(12000);
  const [gasPrice,  setGasPrice]  = useState(3.50);
  const [elecCost,  setElecCost]  = useState(0.13);
  const [evMiPerKWh,setEvMiPerKWh]= useState(3.5);
  const [priceDiff, setPriceDiff] = useState(5000);

  const annualGasCost = (milesYear / gasMPG) * gasPrice;
  const annualElecCost= (milesYear / evMiPerKWh) * elecCost;
  const annualSaving  = annualGasCost - annualElecCost;
  const breakEvenYrs  = annualSaving > 0 ? priceDiff / annualSaving : Infinity;
const chartData = useMemo(() => {
  const maxYears = Math.min(Math.ceil(isFinite(breakEvenYrs) ? breakEvenYrs + 5 : 15), 20);
  return Array.from({ length: maxYears + 1 }, (_, y) => ({
    year:        y,
    gasCumCost:  Math.round(annualGasCost   * y),
    evCumCost:   Math.round(priceDiff + annualElecCost * y),
    gasSavings:  Math.round(annualSaving    * y),
    netEvSaving: Math.round(annualSaving    * y - priceDiff),
  }));
}, [annualGasCost, annualElecCost, annualSaving, priceDiff, breakEvenYrs]);
  return (
    <div>
      <div className="ft-grid-2" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <Field label="Gas Car MPG"><Input value={gasMPG} onChange={v=>setGasMPG(+v)} suffix="mpg"/></Field>
        <Field label="Miles per Year"><Input value={milesYear} onChange={v=>setMilesYear(+v)} suffix="mi"/></Field>
        <Field label="Gas Price per Gallon"><Input value={gasPrice} onChange={v=>setGasPrice(+v)} prefix={sym}/></Field>
        <Field label="Electricity Cost per kWh"><Input value={elecCost} onChange={v=>setElecCost(+v)} prefix={sym}/></Field>
        <Field label="EV Miles per kWh"><Input value={evMiPerKWh} onChange={v=>setEvMiPerKWh(+v)} suffix="mi/kWh"/></Field>
        <Field label="EV Premium (extra cost vs gas)"><Input value={priceDiff} onChange={v=>setPriceDiff(+v)} prefix={sym}/></Field>
      </div>
      <ResultGrid>
        <ResultCard label="Annual Fuel Savings" value={fmt(annualSaving,sym)} color={T.green} large/>
        <ResultCard label="Break-Even" value={isFinite(breakEvenYrs)?fmtYrs(breakEvenYrs):'Never'} color={T.cyan}/>
        <ResultCard label="Gas Annual Cost" value={fmt(annualGasCost,sym)} color={T.red}/>
        <ResultCard label="EV Annual Cost" value={fmt(annualElecCost,sym)} color={T.blue}/>
      </ResultGrid>
      <div style={{ marginTop:24 }}>
  <div style={{
    fontSize:12, fontWeight:700, color:T.muted,
    textTransform:'uppercase', letterSpacing:'.08em', marginBottom:12,
  }}>
    Total Cost of Ownership — Gas vs EV
  </div>

  {annualSaving <= 0 && (
    <div style={{
      padding:'10px 14px', borderRadius:8, marginBottom:12,
      background:`${T.red}12`, border:`1px solid ${T.red}30`,
      fontSize:12.5, color:T.red,
    }}>
      ⚠ EV electricity costs more than gas at these rates — no breakeven point.
      Check your electricity rate or EV efficiency.
    </div>
  )}

  {isFinite(breakEvenYrs) && (
    <div style={{
      padding:'10px 14px', borderRadius:8, marginBottom:12,
      background:`${T.green}10`, border:`1px solid ${T.green}30`,
      fontSize:12.5, color:T.green,
    }}>
      ✅ EV breaks even in{' '}
      <strong>{breakEvenYrs.toFixed(1)} years</strong>
      {' '}— after that you save{' '}
      <strong>{sym}{Math.round(annualSaving).toLocaleString()}/yr</strong> pure profit.
    </div>
  )}

  <ResponsiveContainer width="100%" height={260}>
    <LineChart data={chartData} margin={{ top:4, right:16, left:8, bottom:4 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.06)" vertical={false}/>
      <XAxis
        dataKey="year"
        tick={{ fill:T.muted, fontSize:11 }}
        tickLine={false} axisLine={false}
        tickFormatter={v => `Yr ${v}`}
      />
      <YAxis
        tick={{ fill:T.muted, fontSize:11 }}
        tickLine={false} axisLine={false}
        width={64}
        tickFormatter={n => shortNum(n, sym)}
      />
      <Tooltip
        contentStyle={{ background:T.bg2, border:`1px solid ${T.border}`, borderRadius:8, fontSize:12 }}
        labelStyle={{ color:T.muted, marginBottom:4 }}
        itemStyle={{ color:T.white }}
        formatter={(value, name) => [
          shortNum(value, sym),
          name === 'gasCumCost'  ? 'Gas car total fuel cost'  :
          name === 'evCumCost'   ? 'EV total cost (premium + electricity)' :
          name === 'netEvSaving' ? 'Net EV savings after premium' :
          'Cumulative fuel savings',
        ]}
        labelFormatter={y => `Year ${y}`}
      />
      <Legend
        wrapperStyle={{ fontSize:12, color:T.muted, paddingTop:8 }}
        formatter={name =>
          name === 'gasCumCost'  ? 'Gas fuel cost (cumulative)'     :
          name === 'evCumCost'   ? 'EV total cost (premium + elec)' :
          name === 'netEvSaving' ? 'Net EV savings'                  :
          'Fuel savings'
        }
      />

      {/* Zero line for net savings reference */}
      <ReferenceLine
        y={0}
        stroke="rgba(255,255,255,.1)"
        strokeWidth={1}
      />

      {/* Breakeven marker */}
      {isFinite(breakEvenYrs) && (
        <ReferenceLine
          x={Math.ceil(breakEvenYrs)}
          stroke={T.green}
          strokeDasharray="4 3"
          strokeWidth={1.5}
          label={{
            value:`Break-even yr ${Math.ceil(breakEvenYrs)}`,
            position:'top',
            fill:T.green,
            fontSize:10,
            fontWeight:700,
          }}
        />
      )}

      {/* Gas cumulative fuel cost */}
      <Line
        type="monotone" dataKey="gasCumCost"
        stroke={T.red} strokeWidth={2}
        dot={false} activeDot={{ r:4, fill:T.red }}
      />
      {/* EV total cost (premium + electricity) */}
      <Line
        type="monotone" dataKey="evCumCost"
        stroke={T.blue} strokeWidth={2}
        dot={false} activeDot={{ r:4, fill:T.blue }}
      />
      {/* Net EV savings after recouping premium */}
      <Line
        type="monotone" dataKey="netEvSaving"
        stroke={T.green} strokeWidth={2}
        strokeDasharray={annualSaving > 0 ? '0' : '4 3'}
        dot={false} activeDot={{ r:4, fill:T.green }}
      />
    </LineChart>
  </ResponsiveContainer>

  {/* Summary tiles */}
  <div style={{ marginTop:12, display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8 }}>
    {[
      { label:'Gas cost/yr',    value:fmt(annualGasCost, sym),   color:T.red   },
      { label:'EV elec cost/yr',value:fmt(annualElecCost, sym),  color:T.blue  },
      { label:'Annual saving',  value:fmt(annualSaving, sym),    color:T.green },
      { label:'Break-even',     value:isFinite(breakEvenYrs) ? `${breakEvenYrs.toFixed(1)} yrs` : 'Never', color:isFinite(breakEvenYrs)?T.cyan:T.red },
    ].map(({ label, value, color }) => (
      <div key={label} style={{
        padding:'9px 10px', borderRadius:8,
        background:`${color}10`, border:`1px solid ${color}25`,
        fontSize:11.5, textAlign:'center',
      }}>
        <div style={{ color:T.muted, marginBottom:3 }}>{label}</div>
        <div style={{ color, fontWeight:700, fontSize:12.5, fontFamily:T.mono }}>{value}</div>
      </div>
    ))}
  </div>
</div>
    </div>
  );
}

/* ─── TOOL REGISTRY ────────────────────────────────────────────────── */
const CATEGORIES = [
  {
    id:    'fire',
    label: 'FIRE',
    color: T.orange,
    icon:  '🔥',
    desc:  'Financial Independence / Retire Early',
    tools: [
      { id:'fire',         label:'FIRE Calculator',               icon:'🔥', component: FIRECalc },
      { id:'coast',        label:'Coast FIRE Calculator',         icon:'🌊', component: CoastFIRECalc },
      { id:'fatfire',      label:'Fat FIRE Calculator',           icon:'💰', component: FatFIRECalc },
      { id:'barista',      label:'Barista FIRE Calculator',       icon:'☕', component: BaristaFIRECalc },
      { id:'windfall',     label:'Windfall Impact on FIRE',       icon:'🎯', component: WindfallFIRECalc },
      { id:'purchase',     label:'Purchase Impact on FIRE',       icon:'🛒', component: PurchaseFIRECalc },
    ],
  },
  {
    id:    'investing',
    label: 'Investing & Loans',
    color: T.blue,
    icon:  '📈',
    desc:  'Loans, mortgages, real estate, portfolios',
    tools: [
      { id:'compound',     label:'Compound Interest',             icon:'📈', component: CompoundInterestCalc },
      { id:'loan',         label:'Loan Payment Calculator',       icon:'📅', component: LoanCalc },
      { id:'mortgage',     label:'15 vs 30 Year Mortgage',        icon:'🏠', component: MortgageCalc },
      { id:'portfolio',    label:'Portfolio Rebalancing',         icon:'🥧', component: PortfolioRebalanceCalc },
      { id:'rental',       label:'Rental Property Calculator',    icon:'🏘️', component: RentalCalc },
      { id:'househack',    label:'House Hacking Calculator',      icon:'🔑', component: HouseHackCalc },
      { id:'caprate',      label:'Cap Rate Calculator',           icon:'📊', component: CapRateCalc },
      { id:'cashoncash',   label:'Cash-on-Cash Return',           icon:'💵', component: CashOnCashCalc },
    ],
  },
  {
    id:    'saving',
    label: 'Saving & Income',
    color: T.green,
    icon:  '💵',
    desc:  'Savings, income, lifestyle costs',
    tools: [
      { id:'savings',      label:'Savings Rate Calculator',       icon:'📊', component: SavingsRateCalc },
      { id:'emergency',    label:'Emergency Fund Calculator',     icon:'🚨', component: EmergencyFundCalc },
      { id:'hsa',          label:'HSA Growth Calculator',         icon:'❤️', component: HSACalc },
      { id:'inflation',    label:'Keeping Up With Inflation',     icon:'🏃', component: InflationCalc },
      { id:'upwork',       label:'Upwork Fee Calculator',         icon:'🎧', component: UpworkCalc },
      { id:'ev',           label:'EV Savings Calculator',         icon:'⚡', component: EVSavingsCalc },
    ],
  },
  {
  id:    'canada',
  label: 'Canada & More',
  color: '#e74c3c',
  icon:  '🍁',
  desc:  'RRSP, TFSA, SIP, retirement, dividends, currency converter',
  tools: [
    { id:'sip',        label:'SIP Calculator',        icon:'📅', component: SIPCalc        },
    { id:'retirement', label:'Retirement Calculator',  icon:'🏖️', component: RetirementCalc  },
    { id:'rrsp',       label:'RRSP Contribution',      icon:'🍁', component: RRSPCalc        },
    { id:'tfsa',       label:'TFSA Growth',            icon:'💎', component: TFSACalc        },
    { id:'dividend',   label:'Dividend Yield',         icon:'💰', component: DividendCalc    },
    { id:'currency',   label:'Currency Converter',     icon:'🌍', component: CurrencyConverter },
  ],
},
];
const financeToolsSchema = {
  "@context":    "https://schema.org",
  "@type":       "WebApplication",
  "name":        "Free Financial Calculators — Tylented",
  "url":         "https://arhat.info/tools/finance",
  "description": "Free financial calculators for everyday use: FIRE number, Coast FIRE, Fat FIRE, Barista FIRE, compound interest, mortgage comparison, loan payment, portfolio rebalancing, rental property, savings rate, emergency fund, RRSP, TFSA, SIP, retirement, dividend yield, currency converter.",
  "applicationCategory": "FinanceApplication",
  "operatingSystem": "Any",
  "isAccessibleForFree": true,
  "offers": { "@type":"Offer", "price":"0", "priceCurrency":"USD" },
  "featureList": [
    "FIRE Number Calculator",
    "Coast FIRE Calculator",
    "Fat FIRE Calculator",
    "Barista FIRE Calculator",
    "Windfall Impact on FIRE",
    "Purchase Impact on FIRE",
    "Compound Interest Calculator",
    "Loan Payment Calculator",
    "15 vs 30 Year Mortgage Comparison",
    "Portfolio Rebalancing Calculator",
    "Rental Property ROI Calculator",
    "House Hacking Calculator",
    "Cap Rate Calculator",
    "Cash-on-Cash Return Calculator",
    "Savings Rate Calculator",
    "Emergency Fund Calculator",
    "HSA Growth Calculator",
    "Inflation / Salary Calculator",
    "Upwork Fee Calculator",
    "EV Savings Calculator",
    "SIP Calculator",
    "Retirement Calculator",
    "RRSP Contribution Calculator (Canada)",
    "TFSA Growth Calculator (Canada)",
    "Dividend Yield Calculator",
    "Currency Converter — USD, CAD, GBP, EUR, AUD, INR, JPY, SGD, CHF, NZD"
  ],
  "creator": { "@type":"Organization","name":"Tylented","url":"https://arhat.info" }
};

const financeFAQSchema = {
  "@context": "https://schema.org",
  "@type":    "FAQPage",
  "mainEntity": [
    {
      "@type":          "Question",
      "name":           "What is a FIRE number and how do I calculate it?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text":  "Your FIRE number is the portfolio size needed to retire early and live off investment returns. It equals your annual expenses divided by your safe withdrawal rate (typically 4%). For $50,000/year spending, your FIRE number is $1,250,000. Use the free FIRE Calculator at arhat.info/tools/finance — adjust withdrawal rate, expected returns, and current savings to see your exact timeline."
      }
    },
    {
      "@type":          "Question",
      "name":           "What is Coast FIRE and how is it different from regular FIRE?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text":  "Coast FIRE means you have saved enough that your investments will grow to your FIRE number by retirement age without any additional contributions — you only need to cover current expenses. Unlike traditional FIRE where you stop working entirely, Coast FIRE lets you work part-time or at a lower-stress job. Calculate your Coast FIRE number free at arhat.info/tools/finance."
      }
    },
    {
      "@type":          "Question",
      "name":           "How much can I contribute to my RRSP in 2024?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text":  "The 2024 RRSP contribution limit is $31,560 or 18% of prior year earned income, whichever is lower, plus unused room from prior years. The RRSP Calculator at arhat.info/tools/finance calculates your exact limit, estimated tax refund based on your marginal rate, and shows how your contributions grow tax-deferred over time."
      }
    },
    {
      "@type":          "Question",
      "name":           "Should I choose a 15-year or 30-year mortgage?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text":  "A 15-year mortgage builds equity faster and saves tens of thousands in interest but requires higher monthly payments. A 30-year mortgage offers lower payments and more cash flow flexibility but costs significantly more over the loan life. Use the free 15 vs 30 Year Mortgage calculator at arhat.info/tools/finance to compare side by side with your actual numbers."
      }
    }
  ]
};
 
const Mapping = {
  'fire': 'fire',
  'coast': 'fire',
  'fatfire': 'fire',
  'barista': 'fire',
  'windfall': 'fire',
  'purchase': 'fire',

  'compound':     'investing',
  'loan':         'investing',
  'mortgage':     'investing',
  'portfolio':    'investing',
  'rental':       'investing',
  'househack':    'investing',
  'caprate':      'investing',
  'cashoncash':   'investing',

  'savings':      'saving',
  'emergency':    'saving',
  'hsa':          'saving',
  'inflation':    'saving',
  'upwork':       'saving',
  'ev':           'saving',

  'sip':          'canada',
  'retirement':   'canada',
  'rrsp':         'canada',
  'tfsa':         'canada',
  'dividend':     'canada',
  'currency':     'canada',
};



const FINANCE_FAQS = {
  fire: [
    {
      q: 'What is FIRE?',
      a: 'FIRE stands for Financial Independence, Retire Early. It\'s a movement focused on saving and investing aggressively — typically 50–70% of income — to accumulate enough wealth to live off investment returns without needing to work. The goal is financial freedom, not necessarily stopping work entirely.',
    },
    {
      q: 'What is the FIRE number?',
      a: 'Your FIRE number is the total investment portfolio value you need to retire. It\'s calculated as your annual expenses divided by your withdrawal rate. At a 4% withdrawal rate, the FIRE number is 25× your annual expenses. Spend $50,000/year → FIRE number is $1,250,000.',
    },
    {
      q: 'What is the 4% rule?',
      a: 'The 4% rule comes from the Trinity Study (1998), which found that a portfolio of 50–75% stocks and 25–50% bonds could sustain a 4% annual withdrawal rate for at least 30 years with a 95%+ success rate. It\'s a starting point, not a guarantee — especially for retirements longer than 30 years.',
    },
    {
      q: 'What is real return vs nominal return?',
      a: 'Nominal return is the raw investment return (e.g. 7%). Real return adjusts for inflation: real return ≈ (1 + nominal) ÷ (1 + inflation) − 1. At 7% nominal and 3% inflation, real return is about 3.88%. FIRE calculators use real return so all figures are in today\'s purchasing power.',
    },
    {
      q: 'How does savings rate affect years to FIRE?',
      a: 'Savings rate has a dramatic non-linear effect. Saving 10% takes roughly 43 years. Saving 25% takes about 32 years. Saving 50% takes about 17 years. Saving 75% takes about 7 years. The relationship is exponential — higher savings rates both reduce spending (lowering the FIRE number) and increase contributions simultaneously.',
    },
    {
      q: 'Is FIRE achievable on an average income?',
      a: 'Yes, though it requires intentional spending choices. Many people who achieve FIRE do so on moderate incomes by keeping housing and transport costs low (the two biggest expenses), avoiding lifestyle inflation, and investing consistently over 15–25 years. Income helps, but savings rate is the primary lever.',
    },
  ],

  coast: [
    {
      q: 'What is Coast FIRE?',
      a: 'Coast FIRE is when you have enough invested that — without adding another dollar — your portfolio will grow to your full FIRE number by your target retirement age. Once you hit your Coast FIRE number, you only need to earn enough to cover current expenses (you can "coast" to retirement).',
    },
    {
      q: 'How is the Coast FIRE number calculated?',
      a: 'Coast FIRE number = FIRE target ÷ (1 + return rate)^years until retirement. Example: FIRE target $1,250,000, 25 years away, 7% return: Coast number = $1,250,000 ÷ (1.07)^25 = $232,000. If you have $232,000 invested today, it will grow to $1.25M in 25 years with no additional contributions.',
    },
    {
      q: 'What do I do after reaching Coast FIRE?',
      a: 'You have more flexibility — you can switch to a lower-stress job, work part-time, take a career break, or work on passion projects. You still need to earn enough to cover living expenses, but the pressure of aggressive saving is gone since your investments are doing the heavy lifting.',
    },
    {
      q: 'What is the difference between Coast FIRE and Barista FIRE?',
      a: 'Coast FIRE means your investments alone will grow to full retirement — you just need to cover current expenses from income. Barista FIRE means you\'ve partially retired: your portfolio covers some expenses and part-time work covers the rest. Barista FIRE is a semi-retirement state; Coast FIRE is a milestone on the way to full retirement.',
    },
  ],

  fat: [
    {
      q: 'What is Fat FIRE?',
      a: 'Fat FIRE is a variant targeting a higher retirement income — typically $100,000+ per year in retirement expenses. While lean FIRE aims for frugal early retirement and regular FIRE targets average spending, Fat FIRE targets luxury or comfortable upper-middle-class spending without financial constraints.',
    },
    {
      q: 'Why does Fat FIRE use a lower withdrawal rate?',
      a: 'Fat FIRE practitioners often use 3–3.5% withdrawal rates instead of 4% for extra safety. With a larger portfolio and higher spending, sequence of returns risk is more impactful. A lower withdrawal rate significantly extends portfolio longevity and provides more margin for market downturns.',
    },
    {
      q: 'How much do I need for Fat FIRE?',
      a: 'At $100,000/year expenses and 3.5% withdrawal rate, Fat FIRE requires ~$2.86M. At $150,000/year it\'s ~$4.3M. At $200,000/year it\'s ~$5.7M. The exact number depends on your target spending and chosen withdrawal rate.',
    },
  ],

  barista: [
    {
      q: 'What is Barista FIRE?',
      a: 'Barista FIRE is a semi-retirement strategy where you work part-time to cover some living expenses while your portfolio covers the rest. The name comes from the idea of working a low-stress job (like a barista) that covers health insurance and spending money, while your investments grow toward full FIRE.',
    },
    {
      q: 'How is the Barista FIRE number calculated?',
      a: 'Calculate how much income your part-time job covers, subtract that from total annual expenses, and divide the remainder by your withdrawal rate. Example: $60,000 expenses, $20,000 part-time income, $40,000 needs portfolio coverage. At 4% withdrawal: $40,000 ÷ 0.04 = $1,000,000 Barista FIRE number.',
    },
    {
      q: 'What are the benefits of Barista FIRE over full FIRE?',
      a: 'You can semi-retire years earlier since you need a smaller portfolio. Part-time work provides social connection, structure, and purpose. If markets perform poorly, you can increase work hours for extra income. Health benefits from an employer (especially in the US) reduce costs significantly.',
    },
  ],

  windfall: [
    {
      q: 'What counts as a windfall for FIRE planning?',
      a: 'A windfall is any unexpected or one-time large sum: inheritance, insurance payout, legal settlement, property sale proceeds, stock option vesting, bonus, or a gift. For FIRE planning purposes, it\'s any lump sum you can invest that accelerates your timeline.',
    },
    {
      q: 'Should I invest a windfall all at once or gradually?',
      a: 'Historically, lump-sum investing outperforms dollar-cost averaging about 2/3 of the time because markets tend to rise over time. However, DCA reduces the risk of investing at a peak. For FIRE planning, a common approach is to invest 50% immediately and DCA the rest over 6–12 months.',
    },
    {
      q: 'How does a windfall affect my FIRE date?',
      a: 'A windfall accelerates FIRE in two ways: it increases your current portfolio (reducing time to reach FIRE number) and because it compounds, its impact grows over time. This calculator shows exactly how many years earlier you\'ll reach FIRE with the windfall invested.',
    },
  ],

  purchase: [
    {
      q: 'How does a major purchase delay FIRE?',
      a: 'A purchase delays FIRE by reducing your invested portfolio. The real cost isn\'t just the purchase price — it\'s the purchase price plus all the compounded growth that money would have generated. A $20,000 purchase at 7% real return delays FIRE by more than the simple $20,000 would suggest.',
    },
    {
      q: 'Should I include a car purchase in FIRE planning?',
      a: 'Yes — cars are one of the largest FIRE-delaying purchases after housing. A $30,000 car financed at 6% has both the direct cost and the lost compound growth. FIRE-focused individuals often buy used cars for cash, drive them for 10+ years, and avoid car payments entirely.',
    },
    {
      q: 'What is the opportunity cost of a purchase?',
      a: 'Opportunity cost is the return you give up by spending money instead of investing it. If you spend $10,000 that could have earned 7% real return for 20 years, the opportunity cost is $10,000 × (1.07)^20 − $10,000 = ~$28,700. This is why early FIRE savers are mindful of large discretionary purchases.',
    },
  ],

  compound: [
    {
      q: 'What is compound interest?',
      a: 'Compound interest is interest calculated on both the initial principal and the accumulated interest from previous periods. Unlike simple interest (calculated only on principal), compound interest grows exponentially — Albert Einstein allegedly called it the "eighth wonder of the world".',
    },
    {
      q: 'How does compound frequency affect returns?',
      a: 'More frequent compounding produces slightly higher returns. $10,000 at 10% for 1 year: annually = $11,000. Monthly = $11,047. Daily = $11,052. The difference is small for moderate rates and periods but becomes meaningful at high rates over long periods. Most investments compound daily or monthly.',
    },
    {
      q: 'What is the Rule of 72?',
      a: 'Divide 72 by your annual return rate to estimate how many years it takes to double your money. At 6%: 72÷6 = 12 years. At 8%: 72÷8 = 9 years. At 12%: 72÷12 = 6 years. It\'s a quick mental math shortcut that\'s accurate within a year or two for rates between 6–20%.',
    },
    {
      q: 'What annual return should I assume for stock market investments?',
      a: 'The S&P 500 has returned approximately 10% nominally and 7% in real (inflation-adjusted) terms annually over long periods. Canadian stocks (TSX) have returned slightly less. A diversified global portfolio might return 6–8% real. For conservative planning, 5–6% real is a reasonable assumption.',
    },
  ],

  loan: [
    {
      q: 'How is a loan payment calculated?',
      a: 'Monthly payment = Principal × (r × (1+r)^n) ÷ ((1+r)^n − 1), where r is the monthly interest rate (annual rate ÷ 12) and n is the number of months. This formula calculates the fixed payment that pays off exactly the principal plus interest over the loan term.',
    },
    {
      q: 'How much do extra payments save on a loan?',
      a: 'Extra payments are highly effective because they reduce the principal directly, which reduces all future interest charges. On a $300,000 mortgage at 6.5% over 30 years, an extra $200/month saves over $80,000 in interest and pays off the loan 5+ years early.',
    },
    {
      q: 'What is an amortization schedule?',
      a: 'An amortization schedule shows each payment broken into its principal and interest components. Early payments are mostly interest; later payments are mostly principal. This is because interest is calculated on the remaining balance — which is highest at the start.',
    },
    {
      q: 'Should I pay off debt or invest?',
      a: 'The math: if your loan interest rate is higher than your expected investment return, pay off debt. If lower, invest. In practice: always pay off high-interest debt (credit cards at 20%+) first. For low-rate debt (mortgage at 5–6%), the decision is closer — many people do both.',
    },
  ],

  mortgage: [
    {
      q: 'Should I choose a 15-year or 30-year mortgage?',
      a: 'A 15-year mortgage has a higher monthly payment but significantly less total interest — often saving $100,000–$200,000 on a typical mortgage. A 30-year has lower payments and more cash flow flexibility. The right choice depends on your income stability, other financial goals, and risk tolerance.',
    },
    {
      q: 'How much does a lower interest rate save over the life of a mortgage?',
      a: 'On a $400,000 mortgage over 30 years: at 6.5% you pay ~$510,000 in interest. At 5.5% you pay ~$419,000 in interest — a $91,000 difference. Even a 0.5% rate difference on a large mortgage is worth tens of thousands of dollars over 30 years.',
    },
    {
      q: 'What is mortgage amortization?',
      a: 'Amortization is the process of paying off a loan through regular payments. Each payment covers interest on the remaining balance plus some principal. In the early years most of your payment is interest; by the end most is principal. Canadian mortgages typically amortize over 25 years.',
    },
    {
      q: 'What is the stress test for Canadian mortgages?',
      a: 'Canadian mortgage applicants must qualify at the higher of: their contract rate + 2%, or 5.25%. This "stress test" ensures borrowers can handle rate increases. If you\'re offered 5.5%, you must qualify at 7.5%. This limits how much you can borrow.',
    },
  ],

  rental: [
    {
      q: 'What is a good cap rate for a rental property?',
      a: 'Cap rates vary by market. In expensive cities like Toronto or Vancouver, 3–4% cap rates are common due to high property values and appreciation expectations. In smaller markets, 6–8% or higher is achievable. Below 4% is generally considered weak for cash flow; above 6% is solid. Always compare to local market rates.',
    },
    {
      q: 'What is cash-on-cash return?',
      a: 'Cash-on-cash return measures annual pre-tax cash flow as a percentage of total cash invested (down payment + closing costs + repairs). It\'s the most practical return metric for leveraged real estate. A 6–8% cash-on-cash return is generally considered good for residential rental properties.',
    },
    {
      q: 'What expenses should I include in rental property analysis?',
      a: 'Include: mortgage payment, property tax, insurance, property management (8–12% of rent if managed), maintenance and repairs (budget 1% of property value annually), vacancy (5–10% of annual rent), utilities if included in rent, and capital expenditure reserves for roof, HVAC, appliances.',
    },
    {
      q: 'What is the 1% rule for rental properties?',
      a: 'The 1% rule says monthly rent should be at least 1% of the purchase price. A $200,000 property should rent for $2,000+/month. It\'s a quick screening tool to find cash-flowing properties — not a guarantee. In expensive markets like Toronto or Vancouver, properties almost never meet the 1% rule, requiring investors to rely more on appreciation.',
    },
  ],

  savings: [
    {
      q: 'What savings rate should I aim for?',
      a: 'The standard financial advice is 15–20% of gross income. For early retirement: 40–50%+ is the FIRE baseline. The higher your savings rate, the faster you reach financial independence. Even increasing from 10% to 20% meaningfully accelerates retirement.',
    },
    {
      q: 'Should I calculate savings rate on gross or net income?',
      a: 'Both are useful for different purposes. Gross savings rate (savings ÷ gross income) is used by the FIRE community for comparing across tax situations. Net savings rate (savings ÷ take-home pay) is more practical for budgeting. This calculator shows both.',
    },
    {
      q: 'Should employer 401k/RRSP matching count toward my savings rate?',
      a: 'Yes — employer matching is part of your total compensation and should count toward your savings rate. If your employer matches 3% of salary and you contribute 10%, your effective savings rate is 13% (or more if you include the pre-tax benefit).',
    },
  ],

  emergency: [
    {
      q: 'How much should I keep in an emergency fund?',
      a: '3–6 months of essential expenses is the standard recommendation. 3 months for stable dual-income households. 6 months for single income, variable income, or anyone with dependents. 9–12 months for self-employed, freelancers, or those in unstable industries.',
    },
    {
      q: 'Where should I keep my emergency fund?',
      a: 'A High Interest Savings Account (HISA) or money market account — somewhere accessible within 1–3 business days, earns some interest, and is separate from your chequing account. In Canada, EQ Bank, Oaken Financial, and Wealthsimple Cash offer competitive HISA rates. Do not invest it in stocks.',
    },
    {
      q: 'Should I build an emergency fund before investing?',
      a: 'Generally yes — start with a $1,000–$2,000 mini emergency fund immediately, then aggressively pay off high-interest debt, then build the full 3–6 month fund, then invest. Without an emergency fund, unexpected expenses force you to sell investments or take on debt at the worst times.',
    },
  ],

  hsa: [
    {
      q: 'What is an HSA and who is eligible?',
      a: 'A Health Savings Account (HSA) is a US tax-advantaged account for medical expenses, available to people enrolled in a High Deductible Health Plan (HDHP). Contributions are tax-deductible, growth is tax-free, and withdrawals for qualified medical expenses are tax-free — the only triple-tax-advantaged account in the US.',
    },
    {
      q: 'What are the HSA contribution limits for 2026?',
      a: 'For 2026: $4,300 for individual coverage, $8,550 for family coverage. People 55+ can contribute an additional $1,000 catch-up contribution. Contributions can be made by you, your employer, or anyone else on your behalf up to the annual limit.',
    },
    {
      q: 'Can I invest my HSA funds?',
      a: 'Yes — most HSA providers allow you to invest funds above a minimum balance (often $1,000–$2,000) in mutual funds, ETFs, or stocks. The investment strategy of "pay medical expenses out of pocket, let HSA grow invested" maximises the triple tax advantage and turns it into a supplemental retirement account.',
    },
    {
      q: 'What happens to HSA funds if I don\'t use them for medical expenses?',
      a: 'Unlike FSAs, HSA funds never expire — they roll over indefinitely. After age 65, you can withdraw for any reason (not just medical) and pay ordinary income tax, making it function like a traditional IRA. Before 65, non-medical withdrawals are taxed plus a 20% penalty.',
    },
  ],

  inflation: [
    {
      q: 'How does inflation affect salary and purchasing power?',
      a: 'Inflation erodes purchasing power — $75,000 today buys less than $75,000 five years ago. At 3.5% inflation, purchasing power falls by about 16% over 5 years. To maintain the same lifestyle, your salary must increase by at least the inflation rate each year.',
    },
    {
      q: 'What is a real raise vs nominal raise?',
      a: 'A nominal raise is the dollar increase in your salary. A real raise accounts for inflation. If you get a 3% raise but inflation is 4%, your real raise is −1% — you\'ve actually taken a pay cut in purchasing power terms. This is why annual raises should be benchmarked against CPI.',
    },
    {
      q: 'What is the current inflation rate in Canada?',
      a: 'Canada\'s inflation rate fluctuates. The Bank of Canada targets 2% inflation over the medium term. After the COVID-era peak above 8% in 2022, inflation returned toward the 2–3% range by 2024–2025. Check the Bank of Canada or Statistics Canada for current figures.',
    },
  ],

  rrsp: [
    {
      q: 'What is an RRSP?',
      a: 'A Registered Retirement Savings Plan (RRSP) is a Canadian tax-advantaged retirement account. Contributions are tax-deductible (reducing your taxable income today), investments grow tax-sheltered, and withdrawals are taxed as income. The strategy is to contribute when your tax rate is high and withdraw in retirement when it\'s lower.',
    },
    {
      q: 'What is the RRSP contribution limit for 2026?',
      a: 'The RRSP annual contribution limit is 18% of your prior year earned income up to a maximum. For 2025, the dollar limit is $32,490. For 2026 it is expected to be approximately $33,810 (indexed to the average wage). Unused contribution room carries forward indefinitely.',
    },
    {
      q: 'When should I contribute to RRSP vs TFSA?',
      a: 'Contribute to RRSP when your current marginal tax rate is higher than your expected retirement rate — typically on income above $55,000 in most provinces. Below that, the TFSA is often better since RRSP withdrawals will be taxed at a similar or higher rate. Many Canadians contribute to both.',
    },
    {
      q: 'What is the RRSP Home Buyers\' Plan?',
      a: 'The Home Buyers\' Plan (HBP) lets first-time buyers withdraw up to $60,000 from their RRSP tax-free to buy or build a qualifying home. The withdrawal must be repaid to the RRSP over 15 years (1/15 per year). If not repaid, the annual amount is added to taxable income.',
    },
    {
      q: 'What happens to my RRSP at age 71?',
      a: 'You must convert your RRSP to a RRIF (Registered Retirement Income Fund) by December 31 of the year you turn 71. The RRIF requires minimum annual withdrawals based on your age — the percentage increases each year. These withdrawals are fully taxable as income.',
    },
  ],

  tfsa: [
    {
      q: 'What is a TFSA?',
      a: 'A Tax-Free Savings Account (TFSA) is a Canadian registered account where investment growth and withdrawals are completely tax-free. Unlike an RRSP, contributions are not tax-deductible, but all growth — interest, dividends, capital gains — is never taxed. Withdrawals don\'t affect income-tested benefits.',
    },
    {
      q: 'What is the TFSA contribution limit for 2026?',
      a: 'The annual TFSA limit for 2026 is $7,000 (same as 2024–2025). The cumulative lifetime room for someone who has been eligible since the TFSA\'s introduction in 2009 is approximately $102,000 as of 2026. Unused room accumulates each year and withdrawals restore room the following January 1.',
    },
    {
      q: 'What can I invest in a TFSA?',
      a: 'TFSAs can hold: stocks (Canadian and US), ETFs, mutual funds, bonds, GICs, cash, and REITs. The same investments available in an RRSP are generally available in a TFSA. Foreign dividends (like US stocks) are subject to withholding tax in a TFSA — this is why US dividend stocks are better held in an RRSP.',
    },
    {
      q: 'What happens if I over-contribute to a TFSA?',
      a: 'Over-contributions are penalised at 1% per month on the excess amount until it is withdrawn. Always track your TFSA contribution room carefully, especially if you withdraw and re-contribute in the same calendar year — room is only restored January 1 of the following year.',
    },
    {
      q: 'Should I use TFSA or RRSP for a down payment?',
      a: 'The new First Home Savings Account (FHSA) is specifically designed for first-time buyers and combines RRSP and TFSA benefits — contributions are deductible AND withdrawals are tax-free for a qualifying home purchase. After FHSA, consider TFSA for flexibility, then RRSP via the Home Buyers\' Plan.',
    },
  ],

  sip: [
    {
      q: 'What is a SIP (Systematic Investment Plan)?',
      a: 'A Systematic Investment Plan (SIP) is a method of investing a fixed amount regularly — typically monthly — into mutual funds or ETFs. SIPs automate investing and apply dollar-cost averaging, reducing the impact of market timing. They are popular in India through mutual fund platforms and conceptually equivalent to automatic investing plans worldwide.',
    },
    {
      q: 'What is the difference between SIP and lump sum investing?',
      a: 'SIP invests fixed amounts periodically (monthly), buying more units when prices are low and fewer when high — this is dollar-cost averaging. Lump sum invests the entire amount at once. Historically, lump sum outperforms about 2/3 of the time since markets tend to rise, but SIP reduces timing risk and is more practical for most investors who invest from income.',
    },
    {
      q: 'What return rate should I assume for SIP calculations?',
      a: 'For equity mutual funds (large-cap index funds), 10–12% nominal or 7–9% real (inflation-adjusted) is a reasonable long-term assumption based on historical returns. For debt funds, 6–7%. For a balanced fund, 8–10%. Always model conservatively and remember past returns don\'t guarantee future results.',
    },
  ],

  retirement: [
    {
      q: 'How much do I need to retire comfortably?',
      a: 'The standard rule is 25× your annual retirement expenses (at a 4% withdrawal rate). If you expect to spend $60,000/year in retirement, you need $1,500,000. Add CPP/OAS (Canada) or Social Security (US) income to reduce the portfolio required. The right number depends on retirement age, life expectancy, and desired lifestyle.',
    },
    {
      q: 'What is CPP and how does it affect retirement planning?',
      a: 'The Canada Pension Plan (CPP) pays a monthly retirement benefit based on your contributions during your working years. The maximum CPP at age 65 in 2026 is approximately $1,365/month (~$16,375/year). Taking CPP early (at 60) reduces it by 7.2%/year; delaying to 70 increases it by 8.4%/year.',
    },
    {
      q: 'What is OAS and when do I receive it?',
      a: 'Old Age Security (OAS) is a monthly benefit available to most Canadians aged 65+, regardless of work history. The maximum OAS in 2026 is approximately $714/month ($8,568/year). You can defer OAS up to age 70 for a 7.2% increase per year of deferral. High-income earners (above ~$90,000) face OAS clawback.',
    },
    {
      q: 'What is sequence of returns risk?',
      a: 'Sequence of returns risk is the danger of experiencing poor investment returns early in retirement when your portfolio is largest. Even if average returns are fine, a crash in year 1–5 of retirement can permanently impair your portfolio\'s ability to sustain withdrawals. This is why maintaining a cash buffer, flexible spending, and possibly a lower withdrawal rate in early retirement is important.',
    },
  ],

  dividend: [
    {
      q: 'What is dividend yield?',
      a: 'Dividend yield is the annual dividend per share divided by the current share price, expressed as a percentage. A stock paying $2.40/year that trades at $50 has a 4.8% yield. It represents the cash return on investment from dividends alone, separate from capital appreciation.',
    },
    {
      q: 'What is a good dividend yield?',
      a: 'There\'s no universal answer. High yield (5%+) can signal financial stress or slow growth. Moderate yield (2–4%) with consistent growth is generally preferable. Canadian dividend aristocrats (banks, utilities, telecoms) typically yield 3–6%. Always assess payout ratio and earnings growth alongside yield.',
    },
    {
      q: 'What is dividend growth investing?',
      a: 'Dividend growth investing focuses on companies that consistently increase dividends over time. A stock yielding 3% today growing dividends at 7%/year will yield 5.9% on your original cost in 10 years. Over time your "yield on cost" compounds significantly, and dividend growth typically correlates with strong underlying business performance.',
    },
    {
      q: 'Are Canadian dividends taxed differently than interest income?',
      a: 'Yes — eligible Canadian dividends receive the dividend tax credit, making them more tax-efficient than interest income in non-registered accounts. A Canadian in a 43% marginal bracket might pay only 25–30% effective tax on eligible dividends. US dividends don\'t receive this credit and are taxed as ordinary income in Canada.',
    },
  ],
};

const GENERIC_FINANCE_FAQS = [
  {
    q: 'Are these financial calculators free?',
    a: 'Yes — all 26 finance calculators are completely free with no account or sign-up required.',
  },
  {
    q: 'Do these calculators account for Canadian taxes?',
    a: 'The RRSP, TFSA, payroll, and GST/HST calculators are built specifically for the Canadian market. FIRE and investment calculators use pre-tax real returns that you can adjust. For tax-specific advice, consult a Canadian CPA or tax professional.',
  },
  {
    q: 'How accurate are these calculators?',
    a: 'These calculators use standard financial formulas and are accurate for planning purposes. They use simplified assumptions (constant returns, no sequence risk, constant inflation) that real markets don\'t follow. Use them for directional planning, not precise predictions. Always consult a financial advisor for major decisions.',
  },
];

function FinanceFAQSection({ active }) {
  const [open, setOpen] = useState(null);
  const faqs = FINANCE_FAQS[active] || GENERIC_FINANCE_FAQS;
  const categ = CATEGORIES.find(c => c.tools?.some(t => t.id === active));
  const accentColor = categ?.color || T.cyan;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
      {faqs.map((faq, i) => {
        const isOpen = open === i;
        return (
          <div key={i} style={{
            border:     `1px solid ${isOpen ? accentColor+'55' : T.border}`,
            borderRadius:10,
            background:  isOpen ? accentColor+'08' : T.bg2,
            overflow:   'hidden',
            transition: 'all .2s',
          }}>
            <button
              onClick={() => setOpen(isOpen ? null : i)}
              style={{
                width:'100%', display:'flex', justifyContent:'space-between',
                alignItems:'center', padding:'13px 16px',
                background:'transparent', border:'none',
                cursor:'pointer', textAlign:'left', gap:12,
              }}
            >
              <span style={{
                fontSize:13.5, fontWeight:600,
                color: isOpen ? accentColor : T.white,
                lineHeight:1.4, transition:'color .15s',
              }}>
                {faq.q}
              </span>
              <span style={{
                flexShrink:0, width:20, height:20, borderRadius:'50%',
                border:`1px solid ${isOpen ? accentColor : T.border}`,
                display:'flex', alignItems:'center', justifyContent:'center',
                fontSize:13, color: isOpen ? accentColor : T.muted,
                transition:'all .2s',
                transform: isOpen ? 'rotate(45deg)' : 'none',
              }}>+</span>
            </button>

            {isOpen && (
              <div style={{
                padding:'12px 16px 14px',
                fontSize:13, color:'rgba(220,230,250,.65)',
                lineHeight:1.75,
                borderTop:`1px solid ${accentColor}20`,
              }}>
                {faq.a}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ─── MAIN PAGE ────────────────────────────────────────────────────── */
export default function FinanceTools() {
    const navigate = useNavigate();

  const [currency,  setCurrency]  = useState('USD');

      
        const hostname = window.location.hostname;
      
      // app.dev.example.com => ["app", "dev", "example", "com"]
      const parts = hostname.split(".");
      
      const firstSubdomain =
        parts.length > 2 ? parts[0] : null;
      
      console.log(firstSubdomain);
      
      const path = window.location.pathname;
      
      // "/tools/useragent/"
      const match = path.match(/^\/tools\/finance\/([^/]+)/);
      
      const toolName = match ? match[1] : null;          
          
        const [activeTab, setActiveTab] = useState(toolName?toolName:firstSubdomain && firstSubdomain in Mapping ? Mapping[firstSubdomain] : 'fire');
            
          const firstTool = CATEGORIES.find(c=>c.id===activeTab)?.tools[0];

      const [activeTool,setActiveTool]= useState(toolName?toolName:firstSubdomain && firstSubdomain in Mapping ? Mapping[firstSubdomain] : firstTool);

  const curr   = CURRENCIES.find(c=>c.code===currency) || CURRENCIES[0];
  const sym    = curr.symbol;
  const categ  = CATEGORIES.find(c=>c.id===activeTab);
  const tool   = categ?.tools.find(t=>t.id===activeTool) || categ?.tools[0];
  const ToolComp = tool?.component;
useEffect(() => {
  // Pass the two schemas for this page
  const schemas = [financeToolsSchema, financeFAQSchema];
  const ids     = [];
  schemas.forEach((schema, i) => {
    const id  = `ld-json-tool-${i}`;
    document.getElementById(id)?.remove();
    const s   = document.createElement('script');
    s.id      = id;
    s.type    = 'application/ld+json';
    s.text    = JSON.stringify(schema);
    document.head.appendChild(s);
    ids.push(id);
  });
  return () => ids.forEach(id => document.getElementById(id)?.remove());
}, []);
  // Switch to first tool of new tab
  const handleTabChange = (tabId) => {
      navigate(`/tools/finance/${tabId}/`)

   

    setActiveTab(tabId);
    const firstTool = CATEGORIES.find(c=>c.id===tabId)?.tools[0];
    if (firstTool) setActiveTool(firstTool.id);
  };

  return (
    <>
      <style>{`
        * { box-sizing:border-box; margin:0; padding:0; }
        select option { background:#0c0f18; }
        input[type=range] { height:4px; }
        input:focus { border-color:rgba(0,212,255,.45) !important; }
        ::-webkit-scrollbar { width:5px; }
        ::-webkit-scrollbar-thumb { background:rgba(255,255,255,.1); border-radius:3px; }
        @media (max-width: 640px) {
  /* Sidebar layout becomes single column */
  .ft-layout {
    grid-template-columns: 1fr;
  }
      .ft-layout2 {
    grid-template-columns: 220px 1fr !important;
  }
  /* Hide sidebar, show mobile select instead */
  .ft-sidebar {
    display: none !important;
    border-right: none !important;
  }
  .ft-mobile-sel {
    display: block !important;
    margin-bottom: 12px;
  }
  /* Two-column tool grids → single column */
  .ft-grid-2 {
    grid-template-columns: 1fr !important;
  }
  /* Category tabs scroll */
  .ft-tabs {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  /* Result grid stays auto */
  .ft-results {
    grid-template-columns: repeat(2, 1fr) !important;
  }
}
@media (min-width: 641px) {
  .ft-mobile-sel { display: none !important; }

}
  @media (max-width: 640px) {
  .ft-layout2 {
    grid-template-columns: 1fr !important; 
  }
}
    @media (min-width: 640px) {
  .ft-layout2 {
  display:grid;
    grid-template-columns: 200px 1fr !important; 
  }
}


      `}</style>
{/* <div className="ft-mobile-sel" style={{ display:'none', marginBottom:12 }}>
  <select
    value={activeTool}
    onChange={e => setActiveTool(e.target.value)}
    style={{
      width:'100%', padding:'10px 12px', borderRadius:8,
      background:T.surf, border:`1px solid ${T.borderH}`,
      color:T.white, fontSize:13, fontFamily:T.sans,
    }}
  >
    {categ?.tools.map(t => (
      <option key={t.id} value={t.id}>{t.icon} {t.label}</option>
    ))}
  </select>
</div> */}
 <div className="page">
    <section className="section" style={{ paddingTop: 0 }}>
      <SEO
  title="FIRE Calculator, Mortgage, Compound Interest & Finance Tools — Free | Tylented"
  description="Free financial calculators: FIRE number, Coast FIRE, Fat FIRE, compound interest, mortgage (15 vs 30 year), loan payment, RRSP contribution (Canada), TFSA growth, SIP calculator, dividend yield, currency converter, rental property ROI. Supports USD, CAD, GBP, EUR and more."
  keywords="FIRE calculator, coast FIRE calculator, fat FIRE calculator, financial independence calculator, compound interest calculator free, mortgage calculator, 15 vs 30 year mortgage, RRSP calculator Canada 2024, TFSA calculator Canada, SIP calculator, dividend yield calculator, currency converter, retirement calculator, loan payment calculator, rental property calculator, savings rate calculator, emergency fund calculator"
  canonical="https://arhat.info/tools/finance"
  og={{
    title:       "Free FIRE Calculator, Mortgage & Finance Tools — Tylented",
    description: "26 free finance tools: FIRE, Coast FIRE, compound interest, mortgage, RRSP, TFSA, SIP, dividend yield, currency converter. Supports 10 currencies.",
    url:         "https://arhat.info/tools/finance",
    image:       "https://arhat.info/og-tools-finance.png",
  }}
/>
      <div className='ft-layout' style={{ minHeight:'100vh', background:T.bg, fontFamily:T.sans, color:T.white }}>

        {/* ── HEADER ─────────────────────────────────────────── */}
        <div style={{ padding:'28px 0 0', marginBottom:0 }}>
          <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:16, marginBottom:20 }}>
            <div>
              <h1 style={{ fontSize:'clamp(1.5rem,3vw,2rem)', fontWeight:900, letterSpacing:'-.02em', marginBottom:5 }}>
                💰 Financial Tools
              </h1>
              <p style={{ fontSize:13, color:T.muted }}>
                FIRE calculators, investing, loans, savings — all free, all in your browser
              </p>
            </div>
            <CurrencyPicker value={currency} onChange={setCurrency}/>
          </div>

          {/* Category tabs */}
          <div className="ft-tabs" style={{ display:'flex', borderBottom:`1px solid ${T.border}` }}>
            {CATEGORIES.map(c=>(
              <button key={c.id} onClick={()=>handleTabChange(c.id)} style={{
                padding:'11px 22px',
                border:'none',
                borderBottom:`2px solid ${activeTab===c.id ? c.color : 'transparent'}`,
                background:'transparent',
                color: activeTab===c.id ? T.white : T.muted,
                fontSize:13, fontWeight: activeTab===c.id ? 700 : 400,
                fontFamily:T.sans, cursor:'pointer',
                display:'flex', alignItems:'center', gap:7,
                marginBottom:-1, transition:'color .15s',
              }}>
                <span style={{ filter: activeTab===c.id ? 'none':'grayscale(1) opacity(.5)' }}>{c.icon}</span>
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── BODY ───────────────────────────────────────────── */}
        <select
  className="ft-mobile-sel"
  value={activeTool}
  onChange={(e) => 
  {
                     navigate(`/tools/finance/${e.target.value}/`)

    setActiveTool(e.target.value)
  
  }
}
  style={{
    display:'none',   /* hidden by default, CSS overrides on mobile */
    width:'100%', padding:'10px 12px', borderRadius:8,
    background:T.surf, border:`1px solid ${T.borderH}`,
    color:T.white, fontSize:13, fontFamily:T.sans,
    marginBottom:12,
  }}
>
  {categ?.tools.map(t => (
    <option key={t.id} value={t.id}>{t.icon} {t.label}</option>
  ))}
</select>
        <div className="ft-layout2" >

          {/* Sidebar */}
          <div className='ft-sidebar' style={{
            borderRight:`1px solid ${T.border}`,
            padding:'14px 0',
            background:T.bg2,
          }}>
            {categ?.tools.map(t=>(
              <button key={t.id} onClick={()=>setActiveTool(t.id)} style={{
                width:'100%', padding:'9px 16px',
                border:'none', borderLeft:`2px solid ${activeTool===t.id ? categ.color : 'transparent'}`,
                background: activeTool===t.id ? `${categ.color}10` : 'transparent',
                color: activeTool===t.id ? T.white : T.muted,
                fontSize:12.5, fontWeight: activeTool===t.id ? 600 : 400,
                fontFamily:T.sans, cursor:'pointer', textAlign:'left',
                display:'flex', alignItems:'center', gap:8,
                transition:'all .13s',
              }}>
                <span style={{ fontSize:14 }}>{t.icon}</span>
                {t.label}
              </button>
            ))}
          </div>

          {/* Tool panel */}
          <div  style={{ padding:'22px 24px', background:T.bg3 }}>
            {tool && (
              <>
                <div style={{ marginBottom:18 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4 }}>
                    <span style={{ fontSize:20 }}>{tool.icon}</span>
                    <h2 style={{ fontSize:17, fontWeight:800, letterSpacing:'-.01em' }}>{tool.label}</h2>
                  </div>
                  <div style={{ fontSize:11.5, color:T.muted }}>
                    Currency: <strong style={{ color:curr.color || T.cyan }}>{curr.code} ({curr.symbol})</strong>
                    {' · '}All calculations run locally in your browser
                  </div>
                </div>
                <div id="print-root">

                {ToolComp && <ToolComp sym={sym}/>}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      </section>
       <section className="section" style={{ paddingTop:0 }}>
        <div style={{ padding:'0 0 48px' }}>
          <h2 style={{
            fontSize:18, fontWeight:800, color:T.white,
            letterSpacing:'-.02em', marginBottom:6,
          }}>
            Frequently Asked Questions
          </h2>
          <p style={{ fontSize:13, color:T.muted, marginBottom:24 }}>
            Common questions about FIRE planning, investing, and financial calculators.
          </p>
          <FinanceFAQSection active={activeTool}/>
        </div>
      </section>
      </div>
    </>
  );
}